import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Folder, File, FileText, FileImage, 
  Search, Plus, HardDrive, Trash2, 
  Settings, User, ChevronRight, MoreVertical, UploadCloud, Lock, Unlock, KeyRound, AlertTriangle, ShieldAlert, ShieldCheck,
  Menu, X, LogOut, Sun, Moon, Monitor, ChevronDown, ChevronLeft, HelpCircle, ChevronUp, ArrowUpDown, GripVertical, Timer, PanelTop, RotateCcw, Zap, FolderUp,
  Wrench, PanelLeftClose, PanelLeftOpen, Download, Pencil, FolderInput, FolderOpen,
} from 'lucide-react';
import { db, normalizeFolderId, getFileSizeBytes, MAX_VAULT_FOLDERS, MAX_FOLDER_NAME_LENGTH } from './db';

// Tresor vorerst nicht buchbar (Kevin 24.10.26): keine Tarife, kein Buchen, kein Erweitern.
// Der Server sperrt dieselben Wege zusätzlich (TRESOR_EINZELVERKAUF im Haupt-Backend).
const TRESOR_BUCHBAR = false;
const TRESOR_FILE_DRAG_TYPE = 'application/x-tresor-file-id';
const TRESOR_FOLDER_DRAG_TYPE = 'application/x-tresor-folder-id';
const MAX_FILE_NAME_LENGTH = 200;

function validateFileName(name) {
  const trimmed = String(name || '').trim();
  if (!trimmed) return { ok: false, error: 'Bitte einen Dateinamen eingeben.' };
  if (trimmed.length > MAX_FILE_NAME_LENGTH) return { ok: false, error: `Der Dateiname darf maximal ${MAX_FILE_NAME_LENGTH} Zeichen haben.` };
  if (/[\\/:*?"<>|]/.test(trimmed)) return { ok: false, error: 'Der Dateiname enthält ungültige Zeichen (\\/:*?"<>|).' };
  if (trimmed === '.' || trimmed === '..') return { ok: false, error: 'Bitte einen anderen Dateinamen wählen.' };
  return { ok: true, name: trimmed };
}

// Touch oder Maus? Auf Touchgeraeten gibt es kein verlaessliches Ziehen, dort laeuft alles ueber das Menue.
const isCoarsePointer = () => typeof window !== 'undefined' && window.matchMedia?.('(hover: none) and (pointer: coarse)').matches;

function readStoredFileSort() {
  try {
    const raw = localStorage.getItem('tresor_file_sort');
    if (!raw) return { key: 'createdAt', dir: 'desc' };
    const parsed = JSON.parse(raw);
    if (['name', 'createdAt', 'size'].includes(parsed?.key) && ['asc', 'desc'].includes(parsed?.dir)) {
      return parsed;
    }
  } catch (_) { /* ignore */ }
  return { key: 'createdAt', dir: 'desc' };
}
import { runUploadJob, loadIncompletePendingUploads, deleteCloudUpload, abortPendingUploadsForFolders, abortPendingUpload, dropLegacyPlaintextResumeCopies } from './uploads';
import {
  createClientId,
  META_LAST_EVENT,
  syncJournalFromServer,
  bootstrapLocalJournal,
  appendFolderCreate,
  appendFolderRename,
  appendFolderDelete,
  appendFolderTrash,
  appendFolderRestore,
  appendFileMove,
  appendFileRename,
  appendFolderMove,
  appendFileDelete,
  appendFileTrash,
  appendFileRestore,
  appendFileAdd
} from './journal';
import { fetchAndDecryptFile, streamDecryptToWritable, decryptedChunksInOrder } from './cloudDownload';
import { streamDownloadSupported, prepareStreamDownload, streamToDownload } from './streamDownload';
import { createDeleteProof, ensureDeleteProofKey } from './deleteProof';
import { IS_EMBEDDED, tellParent, navigateTop, setEmbedLockHandler } from './embed';
import { passkeySupported, registerPasskey, unlockWithPasskey } from './passkey';
import { setIndexKeyProvider, sealRecord, openRecord, openRecords, sealPatch, sealLegacyRows } from './localIndex';
import { ensureAccessToken, setPendingSsoToken, logoutSession, clearTresorPostLogout, isTresorPostLogout } from './authSession';
import {
  markTresorCustomerSession,
  clearTresorCustomerSession,
  hadTresorCustomerSession,
  getCachedTresorStatus,
  redirectToDatenputzerLogin,
  getTresorLoginUrl,
} from './tresorSession';
import { TRESOR_TIERS, TIER_BY_ID, getUpgradeTargets, tierPriceMonthly, tierChargeAmount, cycleLabel, chargeUnitLabel, comparePlans } from './plans';
import SiteFooter from './SiteFooter.jsx';
import ComparisonTable from './marketing/ComparisonTable.jsx';
import FAQSection from './marketing/FAQSection.jsx';
import UspBullets from './marketing/UspBullets.jsx';
import HeroVaultPreview from './marketing/HeroVaultPreview.jsx';
import Maschinenraum from './Maschinenraum.jsx';
import EncryptionFlow from './EncryptionFlow.jsx';
import { clearMachineLog, pushMachineEvent } from './machineLog';
import {
  generateMasterBits, importMasterKeyFromBits,
  deriveKekFromMnemonic, deriveKekFromPassword, wrapMasterBits, unwrapMasterBits,
  deriveRecoveryProofKey, exportRecoveryProofKey, buildRecoveryResetProofPayload, signRecoveryResetProof,
  deriveRecoveryProofKeyPair, signRecoveryResetProofEd25519, supportsEd25519, buildRecoveryProofUpgradePayload,
  unlockWithPassword,
  generateMnemonic, isValidMnemonic, hasValidMnemonicChecksum, mnemonicsMatch,
  fileKeyFor, getVaultKeyring, buildDeleteProofPayload, signDeleteProof,
  encryptBlob,
} from './crypto';
import { decryptBlobStream } from './cryptoClient';
import {
  readAutoLockMinutes, writeAutoLockMinutes, AUTO_LOCK_OPTIONS,
  readLockUploadPolicy, writeLockUploadPolicy, LOCK_UPLOAD_POLICIES,
  readLockOnTabHidden, writeLockOnTabHidden,
} from './securityPrefs';
import { getThemePreference, setThemePreference as persistTheme, applySystemTheme, applyUserTheme } from './theme';
import PasswordStrengthMeter from './PasswordStrengthMeter.jsx';
import { getPasswordStrengthError } from './passwordStrength.js';


/** Max. sichtbare Zeilen im Ordner-Grid, bevor „Erweitern“ nötig ist. */
const FOLDER_GRID_MAX_ROWS = 3;

function validateFolderName(name) {
  const trimmed = name.trim();
  if (!trimmed) return { ok: false, error: 'Bitte einen Ordnernamen eingeben.' };
  if (trimmed.length > MAX_FOLDER_NAME_LENGTH) {
    return { ok: false, error: `Ordnername darf maximal ${MAX_FOLDER_NAME_LENGTH} Zeichen haben.` };
  }
  if (/[\\/:*?"<>|]/.test(trimmed)) {
    return { ok: false, error: 'Ordnername enthält ungültige Zeichen (\\/:*?"<>|).' };
  }
  return { ok: true, name: trimmed };
}

/**
 * Folder names typed by a user are validated and rejected on error. Names coming from a
 * dropped directory tree cannot be rejected the same way, because that would abort a whole
 * upload over one awkward character, so they get repaired instead.
 */
function sanitizeFolderSegment(segment) {
  const cleaned = String(segment ?? '').replace(/[\\/:*?"<>|]/g, '-').trim();
  if (!cleaned) return 'Ordner';
  return cleaned.length > MAX_FOLDER_NAME_LENGTH
    ? cleaned.slice(0, MAX_FOLDER_NAME_LENGTH).trim()
    : cleaned;
}

/** Signals that MAX_VAULT_FOLDERS was reached while building a dropped tree. */
const FOLDER_LIMIT_SIGNAL = 'tresor_folder_limit';

/**
 * Collect every file of a dropped directory tree, each with its path relative to the drop.
 * readEntries hands out roughly 100 children per call, so it has to be drained in a loop
 * until it returns an empty batch, otherwise large folders silently lose their tail.
 */
async function collectDroppedEntry(entry, prefix, out) {
  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
    out.push({ file, path: `${prefix}${file.name}` });
    return;
  }
  if (!entry.isDirectory) return;
  const dirPrefix = `${prefix}${entry.name}/`;
  const reader = entry.createReader();
  for (;;) {
    const batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
    if (!batch.length) break;
    for (const child of batch) await collectDroppedEntry(child, dirPrefix, out);
  }
}

/** Folder IDs to delete, deepest children first, then root folder last. */
// Recycle-bin window: trashed items stay restorable this long, then the client
// permanently deletes them (server keeps a longer backstop for abandoned accounts).
const RECYCLE_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

// Upload speed / ETA formatting (live throughput readout during transfer).
function formatSpeed(bps) {
  if (!bps || bps <= 0) return '';
  const mbps = bps / (1024 * 1024);
  if (mbps >= 1) return `${mbps.toFixed(1)} MB/s`;
  return `${Math.max(1, Math.round(bps / 1024))} KB/s`;
}
function formatEta(seconds) {
  if (seconds == null || !isFinite(seconds)) return '';
  const s = Math.round(seconds);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}:${String(s % 60).padStart(2, '0')} Min`;
  const h = Math.floor(m / 60);
  return `${h} h ${m % 60} min`;
}

function collectFolderDeleteOrder(rootId, allFolders) {
  const order = [];
  const walk = (parentKey) => {
    const children = allFolders.filter((f) => normalizeFolderId(f.parentId) === normalizeFolderId(parentKey));
    for (const child of children) {
      walk(child.id);
      order.push(child.id);
    }
  };
  walk(rootId);
  order.push(Number(rootId));
  return order;
}

// ============================================================
// Brand motifs for the "Sicherheitsdruck" (security-print) look
// ============================================================

// Guilloché rosette — the fine interfering line-work used on banknotes/passports.
function Guilloche({ className = '', stroke = 'currentColor', opacity = 0.5 }) {
  const rings = [];
  for (let i = 0; i < 9; i++) {
    const rx = 120 - i * 6;
    const ry = 60 - i * 3;
    const rot = i * 20;
    rings.push(
      <ellipse key={`e${i}`} cx="150" cy="150" rx={rx} ry={ry}
        transform={`rotate(${rot} 150 150)`} />
    );
  }
  return (
    <svg className={className} viewBox="0 0 300 300" fill="none"
      strokeWidth="0.6" style={{ opacity, stroke }} aria-hidden="true">
      <g>
        {[0, 30, 60, 90, 120, 150].map(r => (
          <g key={`g${r}`} transform={`rotate(${r} 150 150)`}>{rings}</g>
        ))}
        <circle cx="150" cy="150" r="118" />
        <circle cx="150" cy="150" r="92" />
      </g>
    </svg>
  );
}

// Wax/intaglio seal — circular stamp with a shield, used as the "popular" mark.
function Seal({ className = '', label = 'ZERO·KNOWLEDGE', sub = 'DATENPUTZER' }) {
  return (
    <svg className={className} viewBox="0 0 120 120" fill="none" aria-hidden="true">
      <defs>
        <path id="sealArcTop" d="M60 60 m-44 0 a44 44 0 1 1 88 0" />
        <path id="sealArcBot" d="M60 60 m44 0 a44 44 0 1 1 -88 0" />
      </defs>
      <circle cx="60" cy="60" r="56" stroke="currentColor" strokeWidth="1" />
      <circle cx="60" cy="60" r="50" stroke="currentColor" strokeWidth="2" />
      <circle cx="60" cy="60" r="36" stroke="currentColor" strokeWidth="0.75" strokeDasharray="1 3" />
      <text fontSize="7.5" fill="currentColor" letterSpacing="2"
        fontFamily="ui-monospace, monospace">
        <textPath href="#sealArcTop" startOffset="50%" textAnchor="middle">{label}</textPath>
      </text>
      <text fontSize="7.5" fill="currentColor" letterSpacing="2"
        fontFamily="ui-monospace, monospace">
        <textPath href="#sealArcBot" startOffset="50%" textAnchor="middle">{sub}</textPath>
      </text>
      {/* shield */}
      <path d="M60 40 l16 6 v12 c0 11-8 17-16 21 c-8-4-16-10-16-21 V46 z"
        stroke="currentColor" strokeWidth="1.5" fill="none" />
      <path d="M53 59 l5 5 9-10" stroke="currentColor" strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

function App() {
  // App Routing State
  // loading -> landing (no token) -> upsell (token, no sub) -> auth (token, sub, locked) -> vault (unlocked)
  const [appState, setAppState] = useState('loading'); 

  // Vault State
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isSetupMode, setIsSetupMode] = useState(false);
  const [loginMode, setLoginMode] = useState('password'); // 'password', 'recovery', 'reset-password'
  const [resetStep, setResetStep] = useState('mnemonic');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordConfirmInput, setPasswordConfirmInput] = useState('');
  const [passwordRecheckInput, setPasswordRecheckInput] = useState('');
  const [recoveryInput, setRecoveryInput] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [capsLockOn, setCapsLockOn] = useState(false);
  // Passkeys: dritter Weg zum Entsperren (passkey.js)
  const [passkeyOk, setPasskeyOk] = useState(false);
  const [passkeys, setPasskeys] = useState([]);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyAddOpen, setPasskeyAddOpen] = useState(false);
  const [passkeyPw, setPasskeyPw] = useState('');
  const [passkeyInfoOpen, setPasskeyInfoOpen] = useState(false);
  // Seitenleiste einklappbar (Desktop), wie bei GitLab. Der Zustand gilt nur fuer dieses Geraet.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('tresor_sidebar_collapsed') === '1'; } catch (_) { return false; }
  });
  const toggleSidebarCollapsed = () => setSidebarCollapsed((v) => {
    const next = !v;
    try { localStorage.setItem('tresor_sidebar_collapsed', next ? '1' : '0'); } catch (_) { /* egal */ }
    return next;
  });
  // Welches Feld gerade getippt wird: die Warnung steht direkt darunter, nicht irgendwo im Formular.
  const [capsField, setCapsField] = useState(null);

  // Multi-step setup wizard: password → recheck → show mnemonic → verify mnemonic
  const [setupStep, setSetupStep] = useState('password');
  const [generatedMnemonic, setGeneratedMnemonic] = useState(null);
  const [mnemonicVerifyInput, setMnemonicVerifyInput] = useState('');
  const [mnemonicAcknowledged, setMnemonicAcknowledged] = useState(false);

  // Data State
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [totalFileCount, setTotalFileCount] = useState(0);
  const [totalFolderCount, setTotalFolderCount] = useState(0);
  const [currentFolderId, setCurrentFolderId] = useState('root');
  const [allFoldersCache, setAllFoldersCache] = useState([]);
  const [allFilesCache, setAllFilesCache] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [openFolderMenuId, setOpenFolderMenuId] = useState(null);
  const [renameFolderTarget, setRenameFolderTarget] = useState(null);
  const [renameFolderName, setRenameFolderName] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [confirmPurgeId, setConfirmPurgeId] = useState(null);
  const [foldersExpanded, setFoldersExpanded] = useState(false);
  const [folderGridCols, setFolderGridCols] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  // Building the folder tree happens before the first byte is queued; on a big directory
  // that is a noticeable pause, so the UI says what is going on.
  const [isPreparingFolderUpload, setIsPreparingFolderUpload] = useState(false);
  const [draggingFileId, setDraggingFileId] = useState(null);
  const [draggingFolderId, setDraggingFolderId] = useState(null);
  // Kontextmenue (Rechtsklick, langes Druecken, ⋮): { kind: 'file'|'folder', item, x, y, sheet }
  const [ctxMenu, setCtxMenu] = useState(null);
  const [renameFileTarget, setRenameFileTarget] = useState(null);
  const [renameFileName, setRenameFileName] = useState('');
  // Verschieben-Dialog: { kind, item }, Ziel in moveDest
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveDest, setMoveDest] = useState(null);
  const [moveBusy, setMoveBusy] = useState(false);
  const longPressRef = useRef({ timer: null, x: 0, y: 0, firedAt: 0, touching: false });
  const [folderDropTargetId, setFolderDropTargetId] = useState(null);
  const [fileSort, setFileSort] = useState(readStoredFileSort);
  const [sessionKey, setSessionKey] = useState(null);
  const [isEncrypting, setIsEncrypting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // { pendingId: { fileName, percent } }
  const [quotaInfo, setQuotaInfo] = useState({
    limit: 0, used: 0, salt: null, passwordWrapped: null, passwordIv: null,
    passwordEnvelopeConfigured: true,
    email: null, tresorActive: false,
    plan: 'starter', planLabel: '200 GB', canUpgrade: true, upgradeOptions: ['pro', 'max'],
    cancelPending: false, cancelAt: null, dataDeleteBy: null,
    prepaidUntil: null, prepaidDeleteBy: null,
    isTrial: false, readonly: false, trialEndsAt: null, trialDeleteBy: null, trialGraceDays: 60
  });
  // Abrechnungswährung nur für die Anzeige. Die Beträge sind in Euro und Franken
  // zahlengleich (3/Monat, 30/Jahr je 200 GB), es wechselt nur das Zeichen.
  // Startwert Euro, damit der Preis nie leer ist, falls die Abfrage scheitert.
  const [billingCurrency, setBillingCurrency] = useState('eur');
  const currencyLabel = billingCurrency === 'chf' ? 'CHF' : '€';
  // Abrechnungsintervall des bestehenden Abos. null = es gibt keins, dann ist die Wahl frei.
  const [accountCycle, setAccountCycle] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  // Speicher erweitern = Vertragsaenderung: erst mit bestaetigten AGB buchbar
  const [storageAgbAccepted, setStorageAgbAccepted] = useState(false);
  const [upgradeInProgress, setUpgradeInProgress] = useState(null);
  // Buchungsdialog der Testphase: vorbelegt auf 200 GB jaehrlich, alles Weitere
  // liegt hinter "Mehr Optionen".
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [trialPlan, setTrialPlan] = useState('starter');
  const [trialCycle, setTrialCycle] = useState('annual');
  const [trialMoreOptions, setTrialMoreOptions] = useState(false);
  const [trialAgbAccepted, setTrialAgbAccepted] = useState(false);
  const [trialPrivacyAccepted, setTrialPrivacyAccepted] = useState(false);
  const trialPromptShownRef = useRef(false);
  // useCallback-Abhaengigkeiten sollen den Upload-Lauf nicht neu aufbauen, deshalb
  // liest maybeOfferTrialBooking den Status ueber eine Referenz.
  const quotaInfoRef = useRef(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingPlan, setBookingPlan] = useState(null);
  const [bookingCycle, setBookingCycle] = useState('annual');
  const [bookingMode, setBookingMode] = useState('subscribe');
  const [bookingAgbAccepted, setBookingAgbAccepted] = useState(false);
  const [bookingPrivacyAccepted, setBookingPrivacyAccepted] = useState(false);
  const [bookingCouponCode, setBookingCouponCode] = useState('');
  const [bookingCouponOpen, setBookingCouponOpen] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelInProgress, setCancelInProgress] = useState(false);
  const [reactivateInProgress, setReactivateInProgress] = useState(false);
  const [lightningRenewInProgress, setLightningRenewInProgress] = useState(false);
  // Lightning-Neubuchung nur, wenn das Hauptbackend sie freigibt (LIGHTNING_NEUKAUF, seit 23.10.2026 pausiert).
  const [lightningNeukauf, setLightningNeukauf] = useState(false);
  useEffect(() => {
    fetch('/api/tresor/lightning-status', { cache: 'no-store' }).then((r) => r.json())
      .then((d) => setLightningNeukauf(!!(d && d.neukauf))).catch(() => {});
  }, []);
  const [upsellUnits, setUpsellUnits] = useState(1); // 1 unit = 200GB
  const [billingCycle, setBillingCycle] = useState('annual'); // 'monthly' or 'annual'
  const [notification, setNotification] = useState(null);
  const [cloudStorageReady, setCloudStorageReady] = useState(false);
  const [showEncryptionGuide, setShowEncryptionGuide] = useState(false);
  const [encryptionGuideStep, setEncryptionGuideStep] = useState(0);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [lockKeepsUploads, setLockKeepsUploads] = useState(false);
  const [themePreference, setThemePreference] = useState(getThemePreference);
  const [showUploadDetail, setShowUploadDetail] = useState(() => localStorage.getItem('tresor_upload_detail') !== 'off');
  const [allowLargeFiles, setAllowLargeFiles] = useState(() => localStorage.getItem('tresor_allow_large') === 'on');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  // Die Wartungsklappe. Standardmäßig zu, sie soll beim normalen Arbeiten nicht auffallen.
  const [showMaschinenraum, setShowMaschinenraum] = useState(false);
  const [autoLockMinutes, setAutoLockMinutes] = useState(() => readAutoLockMinutes());
  const [lockUploadPolicy, setLockUploadPolicy] = useState(() => readLockUploadPolicy());
  const [lockOnTabHidden, setLockOnTabHidden] = useState(() => readLockOnTabHidden());
  const [clearingCache, setClearingCache] = useState(false);
  const [deviceSessions, setDeviceSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const backgroundKeyRef = useRef(null);
  const autoLockTimerRef = useRef(null);
  const lockUploadPolicyRef = useRef(readLockUploadPolicy());
  const handleLockRef = useRef(null);
  const appStateRef = useRef('loading');
  const profileMenuRef = useRef(null);
  const folderMenuRef = useRef(null);
  const breadcrumbScrollRef = useRef(null);
  const [breadcrumbScroll, setBreadcrumbScroll] = useState({ canLeft: false, canRight: false });
  const dragCounterRef = useRef(0);
  const uploadQueueRef = useRef([]);
  const queueRunningRef = useRef(false);
  const cancelledUploadsRef = useRef(new Set());
  const cancelControllersRef = useRef(new Map());
  const currentFolderIdRef = useRef('root');
  const authBackTrapInstalledRef = useRef(false);
  const progressRemoveTimersRef = useRef({});

  const showNotification = useCallback((text, type = 'info') => {
    setNotification({ text, type });
  }, []);

  const syncCapsLockFromEvent = useCallback((e) => {
    if (typeof e.getModifierState === 'function') {
      setCapsLockOn(e.getModifierState('CapsLock'));
    }
  }, []);

  // Aus Passwortfeldern laesst sich nichts herauskopieren oder ausschneiden. Einfuegen bleibt
  // (Passwortmanager), siehe SECURITY_REVIEW_KRYPTO_2026-09-09.md.
  const blockPasswordCopy = useCallback((e) => {
    e.preventDefault();
    showNotification('Passwörter lassen sich hier aus Sicherheitsgründen nicht kopieren.', 'info');
  }, [showNotification]);
  // Chrome loest bei Passwortfeldern kein copy-Ereignis aus, Strg+C passiert dort einfach still.
  // Damit der Nutzer trotzdem sieht, warum nichts in der Zwischenablage landet, die Taste selbst abfangen.
  const isCopyShortcut = (e) => (e.ctrlKey || e.metaKey) && !e.altKey && ['c', 'x'].includes(String(e.key).toLowerCase());
  const passwordInputEvents = useMemo(() => ({
    onKeyDown: (e) => {
      setCapsField(e.currentTarget.placeholder);
      syncCapsLockFromEvent(e);
      if (isCopyShortcut(e)) blockPasswordCopy(e);
    },
    onKeyUp: (e) => { setCapsField(e.currentTarget.placeholder); syncCapsLockFromEvent(e); },
    onFocus: (e) => setCapsField(e.currentTarget.placeholder),
    onMouseDown: (e) => { setCapsField(e.currentTarget.placeholder); syncCapsLockFromEvent(e); },
    onBlur: () => setCapsLockOn(false),
    onCopy: blockPasswordCopy,
    onCut: blockPasswordCopy,
  }), [syncCapsLockFromEvent, blockPasswordCopy]);

  const hasActiveUploads = useCallback(() => {
    return isEncrypting || Object.keys(uploadProgress).length > 0;
  }, [isEncrypting, uploadProgress]);

  const getEncryptionKey = useCallback(() => {
    return backgroundKeyRef.current || sessionKey;
  }, [sessionKey]);

  const finishBackgroundLockIfNeeded = useCallback(() => {
    if (
      backgroundKeyRef.current &&
      appStateRef.current === 'auth' &&
      !isEncrypting &&
      Object.keys(uploadProgress).length === 0
    ) {
      backgroundKeyRef.current = null;
      setSessionKey(null);
      setLockKeepsUploads(false);
    }
  }, [isEncrypting, uploadProgress]);

  useEffect(() => {
    appStateRef.current = appState;
  }, [appState]);

  useEffect(() => {
    dropLegacyPlaintextResumeCopies();
  }, []);

  // Der lokale Index wird mit dem Schluessel des offenen Tresors verschluesselt. Waehrend ein
  // Upload nach dem Sperren noch zu Ende laeuft (finish_current), gilt der Hintergrundschluessel.
  useEffect(() => {
    setIndexKeyProvider(() => getVaultKeyring(getEncryptionKey())?.indexKey || null);
  }, [getEncryptionKey]);

  // Eingebettet: Dashboard darf sperren (z. B. beim Abmelden) und bekommt den Zustand als Zahlen.
  useEffect(() => {
    if (!IS_EMBEDDED) return undefined;
    setEmbedLockHandler(() => handleLockRef.current?.());
    return () => setEmbedLockHandler(null);
  }, []);
  useEffect(() => {
    if (!IS_EMBEDDED) return;
    tellParent('state', {
      access: true,
      unlocked: !!isUnlocked,
      used: Number(quotaInfo?.used) || 0,
      limit: Number(quotaInfo?.limit) || 0,
      uploading: !!(isEncrypting || Object.keys(uploadProgress).length > 0)
    });
  }, [isUnlocked, quotaInfo?.used, quotaInfo?.limit, isEncrypting, uploadProgress]);

  useEffect(() => {
    quotaInfoRef.current = quotaInfo;
  }, [quotaInfo]);

  // Ist die Testphase abgelaufen, geht der Dialog beim Betreten des Tresors von selbst
  // auf. Bewusst schliessbar: Verkaufsseite und Ablaufmails sagen zu, dass die Dokumente
  // les- und herunterladbar bleiben. Ein Riegel davor waere ein gebrochenes Versprechen.
  const trialLockPromptedRef = useRef(false);
  useEffect(() => {
    if (appState !== 'vault' || !isUnlocked) return;
    if (!quotaInfo.isTrial || !quotaInfo.readonly) return;
    if (trialLockPromptedRef.current) return;
    trialLockPromptedRef.current = true;
    setTrialPlan('starter');
    setTrialCycle('annual');
    setTrialMoreOptions(false);
    setTrialAgbAccepted(false);
    setTrialPrivacyAccepted(false);
    setShowTrialModal(true);
  }, [appState, isUnlocked, quotaInfo.isTrial, quotaInfo.readonly]);

  useEffect(() => {
    if (appState === 'auth' || appState === 'vault') {
      applyUserTheme();
    } else {
      applySystemTheme();
    }
  }, [appState]);

  useEffect(() => {
    if (appState === 'auth' || appState === 'vault') {
      persistTheme(themePreference);
    }
  }, [themePreference, appState]);

  useEffect(() => {
    if (!isSidebarOpen || appState !== 'vault') return undefined;
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (!isMobile) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isSidebarOpen, appState]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setCtxMenu(null);
      setRenameFileTarget(null);
      setMoveTarget(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
      if (folderMenuRef.current && !folderMenuRef.current.contains(e.target)) {
        setOpenFolderMenuId(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!notification) return;
    const timer = setTimeout(() => setNotification(null), 6000);
    return () => clearTimeout(timer);
  }, [notification]);

  useEffect(() => {
    lockUploadPolicyRef.current = lockUploadPolicy;
  }, [lockUploadPolicy]);

  useEffect(() => {
    setCapsLockOn(false);
  }, [loginMode, setupStep, resetStep, isSetupMode]);

  const resetAutoLockTimer = useCallback(() => {
    if (autoLockTimerRef.current) clearTimeout(autoLockTimerRef.current);
    if (!isUnlocked || autoLockMinutes === 0) return;
    autoLockTimerRef.current = setTimeout(() => {
      handleLockRef.current?.();
      showNotification(
        `Tresor automatisch gesperrt nach ${autoLockMinutes} Minuten Inaktivität.`,
        'info'
      );
    }, autoLockMinutes * 60 * 1000);
  }, [isUnlocked, autoLockMinutes, showNotification]);

  useEffect(() => {
    if (!isUnlocked || autoLockMinutes === 0) {
      if (autoLockTimerRef.current) clearTimeout(autoLockTimerRef.current);
      return;
    }
    const events = ['mousemove', 'keydown', 'click', 'touchstart'];
    const onActivity = () => resetAutoLockTimer();
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    resetAutoLockTimer();
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      if (autoLockTimerRef.current) clearTimeout(autoLockTimerRef.current);
    };
  }, [isUnlocked, autoLockMinutes, resetAutoLockTimer]);

  useEffect(() => {
    if (isUnlocked && isEncrypting) resetAutoLockTimer();
  }, [isUnlocked, isEncrypting, resetAutoLockTimer]);

  useEffect(() => {
    if (!lockOnTabHidden || !isUnlocked) return;
    const onVisibility = () => {
      if (document.hidden) handleLockRef.current?.();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [lockOnTabHidden, isUnlocked]);

  useEffect(() => {
    const warnOnLeave = (e) => {
      if (!hasActiveUploads()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warnOnLeave);
    return () => window.removeEventListener('beforeunload', warnOnLeave);
  }, [hasActiveUploads]);

  // --- API Helper ---
  const authenticatedFetch = async (url, options = {}) => {
    const headers = {
      ...options.headers
    };

    await ensureAccessToken();
    return fetch(url, { ...options, headers, credentials: 'include' });
  };

  // Einfuegen UND Ziehen sperren: onPaste allein liess sich mit Drag & Drop umgehen.
  const blockMnemonicPaste = useCallback((e) => {
    e.preventDefault();
    showNotification('Einfügen ist hier gesperrt. Bitte die 12 Wörter von Ihrem Zettel abtippen.', 'info');
  }, [showNotification]);

  // Die Anzeige der 12 Woerter hatte select-all: ein Klick markierte alles, Strg+C kopierte.
  // Das widerspricht der Anweisung zwei Zeilen darueber, sie handschriftlich zu notieren.
  // Ein Stupser, kein Riegel: Screenshot und Kamera bleiben, das ist bekannt und in Ordnung.
  // Gleiches Problem bei den 12 Woertern: nicht markierbarer Text erzeugt kein copy-Ereignis.
  useEffect(() => {
    if (setupStep !== 'mnemonic-display') return undefined;
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && ['c', 'x'].includes(String(e.key).toLowerCase())
          && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault();
        showNotification('Kopieren ist hier gesperrt. Schreiben Sie die 12 Wörter auf Papier, ein Screenshot landet wieder in einer Cloud.', 'info');
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [setupStep, showNotification]);

  const blockMnemonicCopy = useCallback((e) => {
    e.preventDefault();
    showNotification('Kopieren ist hier gesperrt. Schreiben Sie die 12 Wörter auf Papier, ein Screenshot landet wieder in einer Cloud.', 'info');
  }, [showNotification]);

  // Schweizer Kunden rechnen in Franken ab, diese App liegt aber auf einer
  // eigenen Domain und weiss nichts von Stripe. Erst ab einem angemeldeten
  // Zustand fragen: die anonyme Landingpage hat keine Sitzung und liefe in
  // einen 401. Die Anmeldung laeuft ueber HttpOnly-Cookies, es gibt also
  // bewusst keinen Token im localStorage, den man vorher pruefen koennte.
  const billingCurrencyLoadedRef = useRef(false);
  useEffect(() => {
    if (appState !== 'vault' && appState !== 'auth' && appState !== 'upsell') return;
    if (billingCurrencyLoadedRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        billingCurrencyLoadedRef.current = true;
        const res = await authenticatedFetch('/api/tresor/billing-currency');
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        setBillingCurrency(data?.currency === 'chf' ? 'chf' : 'eur');
        setHasSubscription(!!data?.hasSubscription);
        if (data?.cycle === 'monthly' || data?.cycle === 'annual') {
          setAccountCycle(data.cycle);
          // Stripe laesst je Abo nur ein Intervall zu: die Auswahl im Buchungsdialog
          // waere sonst eine Anzeige, die der Server hinterher ueberstimmt.
          setTrialCycle(data.cycle);
        }
      } catch (_) { /* Euro bleibt stehen */ }
    })();
    return () => { cancelled = true; };
  }, [appState]);

  const applyTresorStatus = useCallback((statusData) => {
    setQuotaInfo({
      limit: statusData.limit,
      used: statusData.used,
      email: statusData.email || null,
      salt: statusData.salt,
      // Die Huelle kommt nicht mehr aus dem Status, sondern erst beim Entsperren aus
      // /api/tresor/unlock-material. Hier steht nur noch, OB es eine gibt.
      passwordEnvelopeConfigured: statusData.passwordEnvelopeConfigured !== false,
      passwordWrapped: null,
      passwordIv: null,
      tresorActive: !!statusData.active,
      plan: statusData.plan || 'starter',
      planLabel: statusData.planLabel || '200 GB',
      canUpgrade: statusData.canUpgrade !== false,
      upgradeOptions: statusData.upgradeOptions || [],
      cancelPending: !!statusData.cancelPending,
      cancelAt: statusData.cancelAt || null,
      dataDeleteBy: statusData.dataDeleteBy || null,
      prepaidUntil: statusData.prepaidUntil || null,
      prepaidDeleteBy: statusData.prepaidDeleteBy || null,
      isTrial: !!statusData.isTrial,
      readonly: !!statusData.readonly,
      trialEndsAt: statusData.trialEndsAt || null,
      trialDeleteBy: statusData.trialDeleteBy || null,
      trialGraceDays: statusData.trialGraceDays || 60,
      deleteProofKey: statusData.deleteProofKey || null,
      passkeyCount: Number(statusData.passkeyCount || 0),
      bundle: !!statusData.bundle,
      bundleEnded: !!statusData.bundleEnded,
      readonlyWindowUntil: statusData.readonlyWindowUntil || null,
      windowDownloadLeft: typeof statusData.windowDownloadLeft === 'number' ? statusData.windowDownloadLeft : null
    });
    setCloudStorageReady(statusData.storageConfigured !== false);
  }, []);

  const enterVaultAuth = useCallback((statusData) => {
    applyTresorStatus(statusData);
    markTresorCustomerSession(statusData);
    setAppState('auth');
    checkVaultSetup(statusData.salt);
  }, [applyTresorStatus]);

  useEffect(() => {
    async function initApp() {
      const logoutParams = new URLSearchParams(window.location.search);
      if (logoutParams.get('logged_out') === '1') {
        localStorage.removeItem('dp_token');
        clearTresorCustomerSession();
        window.history.replaceState(null, '', window.location.pathname);
        setAppState('landing');
        return;
      }

      const restoreAuthFromCache = () => {
        if (isTresorPostLogout()) return false;
        const cached = getCachedTresorStatus();
        if (cached?.active) {
          enterVaultAuth(cached);
          return true;
        }
        return false;
      };

      const handleMissingCustomerSession = (preferLoginRedirect = false) => {
        if (restoreAuthFromCache()) return;
        if (preferLoginRedirect || hadTresorCustomerSession()) {
          redirectToDatenputzerLogin();
          return;
        }
        setAppState('landing');
      };

      // Legacy client device IDs are ignored — server issues device via HttpOnly cookie (F-14)
      localStorage.removeItem('dp_device_id');

      // 1. Check for SSO token in URL hash (30s TTL — must exchange for access token)
      let justReceivedSso = false;
      if (window.location.hash.startsWith('#token=')) {
        const token = window.location.hash.substring(7);
        if (token) {
          justReceivedSso = true;
          clearTresorPostLogout();
          setPendingSsoToken(decodeURIComponent(token));
          window.history.replaceState(null, null, window.location.pathname + window.location.search);
        }
      }

      // 2. Exchange SSO / refresh cookie → long-lived access token
      const currentToken = await ensureAccessToken();
      if (!currentToken) {
        handleMissingCustomerSession(justReceivedSso);
        return;
      }

      // 3. Verify Subscription Status via Backend
      try {
        const statusRes = await authenticatedFetch('/api/tresor/status');
        
        if (!statusRes.ok) {
          const errData = await statusRes.json().catch(() => ({}));
          if (statusRes.status === 401) {
            localStorage.removeItem('dp_token');
            handleMissingCustomerSession(true);
            return;
          }
          if (statusRes.status === 403) {
            if (errData.error === 'Device limit reached' || errData.error === 'Session limit reached') {
              setErrorMsg(errData.message || 'Gerätelimit erreicht. Bitte ein anderes Gerät abmelden.');
              setAppState('device-blocked');
              return;
            }
            if (errData.error === 'Subscription required' || errData.error === 'Forbidden') {
              markTresorCustomerSession({ active: false });
              // Ohne Tresor gehoert der Besucher auf die Startseite des Safes, nicht auf
              // einen zweiten Verkaufsschirm. /safe/index.html statt /safe/, weil der
              // exakte nginx-Match auf /safe/ nach / umleitet (Endlosschleife).
              // Eingebettet bleibt das Dashboard stehen; es erfaehrt nur, dass kein Zugang besteht.
              if (IS_EMBEDDED) { tellParent('state', { access: false }); return; }
              window.location.replace('/safe/index.html');
              return;
            }
            localStorage.removeItem('dp_token');
            handleMissingCustomerSession(true);
            return;
          }
          throw new Error('API Error');
        }

        const statusData = await statusRes.json();

        if (!statusData.active) {
          markTresorCustomerSession(statusData);
          if (IS_EMBEDDED) { tellParent('state', { access: false }); return; }
          window.location.replace('/safe/index.html');
          return;
        }

        clearTresorPostLogout();
        // User is authenticated AND has active subscription. Proceed to Vault Auth.
        enterVaultAuth(statusData);

      } catch (err) {
        console.error("Failed to check status", err);
        if (hadTresorCustomerSession() && restoreAuthFromCache()) return;
        if (hadTresorCustomerSession()) {
          redirectToDatenputzerLogin();
          return;
        }
        setAppState('landing');
      }
    }
    initApp();
  }, [applyTresorStatus, enterVaultAuth]);

  // Browser back from the decrypt screen must not land on the SSO redirect loader (history/bfcache).
  useEffect(() => {
    // Eingebettet gehoert die Zurueck-Taste dem Dashboard, nicht dieser Falle.
    if (IS_EMBEDDED || appState !== 'auth' || authBackTrapInstalledRef.current) return undefined;
    authBackTrapInstalledRef.current = true;
    window.history.pushState({ tresorAuth: true }, '', window.location.pathname + window.location.search);
    const onPopState = () => {
      window.location.replace('https://datenputzer.de/dashboard');
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [appState]);

  // Landing/Upsell: if user is logged in with an active Tresor, load status for smart plan buttons.
  useEffect(() => {
    if (appState !== 'landing' && appState !== 'upsell') return;
    let cancelled = false;
    (async () => {
      const token = await ensureAccessToken();
      if (!token || cancelled) return;
      try {
        const res = await authenticatedFetch('/api/tresor/status');
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (data.active && !cancelled) applyTresorStatus(data);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [appState, applyTresorStatus]);

  async function checkVaultSetup(serverSalt) {
      // M13: Never auto-unlock and never trust a locally stored key.
      // Purge any plaintext master key that older builds may have persisted to IndexedDB.
      await db.keyStore.delete('master_key');

      // Setup vs. unlock is decided by server truth (per-user salt), not by local file presence —
      // otherwise a returning user on a new device would be pushed into setup and overwrite their salt.
      setIsSetupMode(!serverSalt);
      setLoginMode('password');
      if (!serverSalt) {
        setSetupStep('password');
        setPasswordInput('');
        setPasswordConfirmInput('');
        setPasswordRecheckInput('');
        setGeneratedMnemonic(null);
        setMnemonicVerifyInput('');
        setMnemonicAcknowledged(false);
      }
  }

  const updateUploadProgress = useCallback((pendingId, data) => {
    const { remove, ...entry } = data;
    if (remove) {
      setUploadProgress((prev) => ({
        ...prev,
        [pendingId]: {
          ...prev[pendingId],
          ...entry,
          phase: 'done',
          percent: 100
        }
      }));
      clearTimeout(progressRemoveTimersRef.current[pendingId]);
      progressRemoveTimersRef.current[pendingId] = setTimeout(() => {
        setUploadProgress((prev) => {
          const next = { ...prev };
          delete next[pendingId];
          return next;
        });
        delete progressRemoveTimersRef.current[pendingId];
      }, 2200);
      return;
    }
    setUploadProgress((prev) => {
      const prevEntry = prev[pendingId];
      const now = Date.now();
      const bytesDone = entry.bytesDone || 0;
      let speedBps = prevEntry?.speedBps || 0;
      let etaSeconds = prevEntry?.etaSeconds ?? null;
      // Sample throughput ONLY between consecutive 'uploading' emits — that interval spans a
      // full chunk cycle (PUT + next encrypt), i.e. real transfer speed. Comparing against the
      // immediately-preceding 'encrypting' emit would only measure encryption time (<<1 chunk).
      let sampleT = prevEntry?._sT;
      let sampleB = prevEntry?._sB;
      if (entry.phase === 'uploading') {
        if (sampleT == null) {
          sampleT = now; sampleB = bytesDone;
        } else if (bytesDone > (sampleB || 0)) {
          const dt = (now - sampleT) / 1000;
          if (dt > 0.2) {
            const inst = (bytesDone - sampleB) / dt;
            speedBps = speedBps > 0 ? speedBps * 0.6 + inst * 0.4 : inst; // EMA smoothing
            const remaining = Math.max(0, (entry.totalBytes || 0) - bytesDone);
            // +15% buffer: linear extrapolation ignores per-chunk sign/encrypt overhead, TCP
            // ramp-up and the final complete step, so a raw ETA reads consistently too low.
            etaSeconds = speedBps > 0 ? (remaining / speedBps) * 1.15 : null;
            sampleT = now; sampleB = bytesDone;
          }
        }
      }
      if (entry.phase === 'waiting' || entry.phase === 'done') {
        speedBps = 0; etaSeconds = null; sampleT = undefined; sampleB = undefined;
      }
      return { ...prev, [pendingId]: { ...entry, speedBps, etaSeconds, _sT: sampleT, _sB: sampleB } };
    });
  }, []);

  const setUploadDetailPreference = (enabled) => {
    setShowUploadDetail(enabled);
    localStorage.setItem('tresor_upload_detail', enabled ? 'on' : 'off');
  };

  const setAllowLargeFilesPreference = (enabled) => {
    setAllowLargeFiles(enabled);
    localStorage.setItem('tresor_allow_large', enabled ? 'on' : 'off');
  };

  const UPLOAD_PHASE_LABELS = {
    waiting: 'Vorbereiten',
    encrypting: 'Verschlüsseln',
    uploading: 'Übertragen',
    storing: 'Lokal sichern',
    finalizing: 'Abschließen',
    done: 'Fertig'
  };

  const renderUploadEncryptionLive = (entry) => {
    const isActive = ['waiting', 'encrypting', 'storing', 'uploading'].includes(entry.phase);
    if (!isActive) return null;

    const liveLabel = {
      waiting: 'Schlüssel bereit, Verschlüsselung startet…',
      encrypting: 'AES-GCM Verschlüsselung auf Ihrem Gerät',
      storing: 'Verschlüsselte Daten werden gesichert',
      uploading: 'Verschlüsselte Stücke werden übertragen'
    }[entry.phase] || 'Verschlüsselung aktiv';

    return (
      <div className={`vault-encrypt-live mb-3 ${entry.phase === 'encrypting' ? 'is-encrypting' : ''}`}>
        <div className="vault-encrypt-live-icon">
          <KeyRound className={`w-5 h-5 vault-text-accent ${entry.phase === 'encrypting' ? 'vault-upload-live-encrypt' : ''}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold vault-text-accent">{liveLabel}</div>
          <div className="text-[11px] vault-text-muted mt-0.5">Zero-Knowledge · Klartext verlässt nie Ihr Gerät</div>
          <div className="vault-encrypt-scramble-track mt-2" aria-hidden="true">
            <div className="vault-encrypt-scramble-fill" />
          </div>
          {entry.chunkHash?.length > 0 && (
            <>
              <div className="vault-encrypt-hex mt-1.5" aria-hidden="true">
                {entry.chunkHash.map((h, i) => <span key={i}>{h}</span>)}
              </div>
              <div className="text-[10px] vault-text-muted mt-1 font-mono">
                Prüfsumme Block {(entry.chunkHashIndex ?? 0) + 1} · aus dem AES-GCM-Auth-Tag
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderUploadProgressEntry = (id, entry, { preview = false } = {}) => {
    const phaseLabel = UPLOAD_PHASE_LABELS[entry.phase] || 'Aktiv';
    const showChunks = showUploadDetail && entry.totalChunks > 0 && entry.phase !== 'waiting';
    const bytesLabel = showUploadDetail && entry.totalBytes
      ? `${formatBytes(entry.bytesDone || 0)} / ${formatBytes(entry.totalBytes)}`
      : null;

    return (
      <div key={id} className="vault-upload-card p-4">
        {showUploadDetail && renderUploadEncryptionLive(entry)}
        <div className="flex justify-between items-start gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <div className="font-medium vault-text truncate">{entry.fileName}</div>
            <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full vault-upload-phase-badge ${entry.phase === 'encrypting' ? 'is-encrypting' : ''}`}>
                {showUploadDetail && entry.phase === 'encrypting' ? <KeyRound className="w-3 h-3 vault-upload-live-encrypt" /> : <UploadCloud className="w-3 h-3" />}
                {phaseLabel}
              </span>
              {showChunks && (
                <span className="font-mono vault-text-muted">
                  Chunk {Math.min(entry.chunkIndex || 0, entry.totalChunks)} / {entry.totalChunks}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="font-mono text-lg vault-text-accent tabular-nums">{entry.percent}%</span>
            {!preview && entry.phase !== 'done' && (
              <button
                type="button"
                onClick={() => cancelUpload(id)}
                className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg border vault-text-subtle hover:text-red-500 hover:border-red-500/50 transition-colors"
                style={{ borderColor: 'var(--vault-border-subtle)' }}
                aria-label="Upload abbrechen"
                title="Upload abbrechen"
              >
                <X className="w-3.5 h-3.5" />
                Abbrechen
              </button>
            )}
          </div>
        </div>
        <div className={`h-2 w-full vault-progress-track mb-2 ${!preview && entry.phase !== 'done' && (entry.bytesDone || 0) === 0 ? 'is-indeterminate' : ''}`}>
          <div className={`vault-upload-bar-fill vault-progress-fill ${showUploadDetail && entry.phase === 'encrypting' ? 'is-encrypting' : ''}`} style={{ width: `${entry.percent}%` }} />
        </div>
        {showUploadDetail ? (
          <div className="flex justify-between text-[11px] vault-text-subtle font-mono tabular-nums">
            <span>{bytesLabel || 'AES-GCM · 256 Bit'}</span>
            {entry.phase === 'uploading' && entry.speedBps > 0 ? (
              <span>{formatSpeed(entry.speedBps)}{entry.etaSeconds != null ? ` · noch ${formatEta(entry.etaSeconds)}` : ''}</span>
            ) : (
              <span>Zero-Knowledge</span>
            )}
          </div>
        ) : (
          <div className="flex justify-between items-center gap-2 text-[11px] vault-text-subtle">
            <span>{preview ? 'Kompakt: nur Fortschritt' : 'Verschlüsselung läuft'}</span>
            {entry.phase === 'uploading' && entry.speedBps > 0 && (
              <span className="font-mono tabular-nums shrink-0">
                {formatSpeed(entry.speedBps)}{entry.etaSeconds != null ? ` · noch ${formatEta(entry.etaSeconds)}` : ''}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderTrashView = () => (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="text-sm font-semibold vault-text-subtle uppercase tracking-wider flex items-center gap-2">
          <Trash2 className="w-4 h-4" /> Papierkorb ({trashRoots.length})
        </h2>
        <button type="button" onClick={() => setShowTrash(false)} className="vault-btn-ghost text-sm whitespace-nowrap px-3 py-1.5">
          Zurück zum Tresor
        </button>
      </div>
      <p className="text-xs vault-text-muted mb-4">
        Gelöschte Objekte bleiben 14 Tage wiederherstellbar und zählen bis dahin weiter zu Ihrem Speicher.
      </p>
      {trashRoots.length === 0 ? (
        <div className="text-center py-20 vault-empty-state">
          <Trash2 className="w-12 h-12 vault-text-subtle mx-auto mb-3" />
          <p className="vault-text font-medium">Der Papierkorb ist leer.</p>
          <p className="vault-text-subtle text-sm mt-1">Gelöschte Dateien und Ordner landen hier.</p>
        </div>
      ) : (
        <div className="vault-file-list vault-glass rounded-xl overflow-hidden shadow-xl">
          {trashRoots.map((item) => {
            const daysLeft = Math.max(0, Math.ceil((RECYCLE_WINDOW_MS - (Date.now() - (item.trashedAt || 0))) / (24 * 60 * 60 * 1000)));
            const rowKey = `${item.kind}-${item.id}`;
            return (
              <div key={rowKey} className="vault-file-row flex items-center gap-3 px-4 py-3">
                <span className="shrink-0">
                  {item.kind === 'folder'
                    ? <Folder className="w-5 h-5 vault-text-accent" fill="currentColor" fillOpacity={0.2} />
                    : item.type === 'pdf' ? <FileText className="w-5 h-5 text-red-500" />
                    : item.type === 'img' ? <FileImage className="w-5 h-5 text-blue-500" />
                    : <File className="w-5 h-5 vault-text-subtle" />}
                </span>
                <div className="min-w-0 flex-1">
                  <span className="font-medium vault-text truncate block">{item.name}</span>
                  <span className="text-xs vault-text-muted">
                    {item.kind === 'folder' ? 'Ordner' : 'Datei'} · noch {daysLeft} Tag{daysLeft === 1 ? '' : 'e'}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-auto">
                  <button
                    type="button"
                    className="vault-btn-ghost text-xs whitespace-nowrap px-3 py-1.5"
                    onClick={() => restoreTrashItem(item)}
                  >
                    Wiederherstellen
                  </button>
                  {confirmPurgeId === rowKey ? (
                    <button
                      type="button"
                      className="vault-btn-ghost vault-text-danger text-xs whitespace-nowrap px-3 py-1.5"
                      onClick={async () => {
                        setConfirmPurgeId(null);
                        const ok = await permanentDeleteTrashItem(item);
                        if (ok) showNotification(`„${item.name}" endgültig gelöscht.`, 'success');
                      }}
                    >
                      Wirklich?
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="vault-btn-ghost vault-text-danger text-xs whitespace-nowrap px-3 py-1.5"
                      onClick={() => setConfirmPurgeId(rowKey)}
                    >
                      Endgültig löschen
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderUploadDetailPreview = () => {
    const sample = {
      fileName: 'beispiel-dokument.pdf',
      percent: 42,
      phase: 'encrypting',
      chunkIndex: 3,
      totalChunks: 8,
      bytesDone: 12 * 1024 * 1024,
      totalBytes: 28 * 1024 * 1024,
    };
    return (
      <div className="mt-3">
        <p className="text-[10px] vault-text-subtle uppercase tracking-wide mb-2">Vorschau</p>
        {renderUploadProgressEntry('upload-preview', sample, { preview: true })}
      </div>
    );
  };

  const renderUploadProgressList = () => {
    if (Object.keys(uploadProgress).length === 0) return null;
    return (
      <div className="mb-8 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xs font-semibold vault-text-accent uppercase tracking-wider">Aktive Uploads</h2>
          <button
            type="button"
            onClick={() => setShowMaschinenraum(true)}
            className="vault-btn-ghost text-xs px-2 py-1 flex items-center gap-1"
            title="Zeigt live, was gerade verschlüsselt und übertragen wird"
          >
            <Wrench className="w-3.5 h-3.5" />
            Maschinenraum
          </button>
        </div>
        {Object.entries(uploadProgress).map(([id, entry]) => renderUploadProgressEntry(id, entry))}
      </div>
    );
  };

  const recordJournalFile = useCallback(async (event) => {
    const key = getEncryptionKey();
    if (!key) return;
    await appendFileAdd(authenticatedFetch, key, event);
  }, [getEncryptionKey]);

  const loadDeviceSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await authenticatedFetch('/api/tresor/sessions');
      if (res.ok) {
        const data = await res.json();
        setDeviceSessions(data.sessions || []);
      }
    } catch (err) {
      console.error('Failed to load sessions', err);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  const revokeDeviceSession = async (sessionId) => {
    const res = await authenticatedFetch(`/api/tresor/sessions/${sessionId}`, { method: 'DELETE' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      showNotification(data.message || data.error || 'Gerät konnte nicht abgemeldet werden.', 'error');
      return;
    }
    showNotification('Gerät abgemeldet.', 'success');
    await loadDeviceSessions();
  };

  const loadData = useCallback(async () => {
    const requestedParentKey = normalizeFolderId(currentFolderIdRef.current);
    let allFolders;
    let allFiles;
    try {
      allFolders = await openRecords('folder', await db.folders.toArray());
      allFiles = await openRecords('file', await db.files.toArray());
    } catch (err) {
      // Gesperrt oder fremder Indexschluessel: lieber nichts anzeigen als Chiffretext.
      if (err?.message !== 'INDEX_LOCKED') console.error('Local index unreadable', err);
      return;
    }
    setAllFoldersCache(allFolders);
    setAllFilesCache(allFiles);
    // Trashed items are hidden from the normal vault views and counts (they live in the
    // Papierkorb) but still exist locally + on the server (quota stays counted).
    setTotalFileCount(allFiles.filter((f) => !f.trashedAt).length);
    setTotalFolderCount(allFolders.filter((f) => !f.trashedAt).length);
    // Ignore stale responses when the user navigated while an async load was in flight
    // (e.g. upload finished in a subfolder, then user returned to overview).
    const activeParentKey = normalizeFolderId(currentFolderIdRef.current);
    if (activeParentKey !== requestedParentKey) return;
    setFolders(allFolders.filter((f) => !f.trashedAt && normalizeFolderId(f.parentId) === activeParentKey));
    setFiles(allFiles.filter((f) => !f.trashedAt && normalizeFolderId(f.folderId) === activeParentKey));
  }, []);

  const isSearchActive = searchQuery.trim().length > 0;

  const getFolderPathLabel = useCallback((folderId) => {
    const key = normalizeFolderId(folderId);
    if (key === 'root') return 'Mein Tresor';
    const chain = [];
    let id = key;
    while (id !== 'root') {
      const folder = allFoldersCache.find((f) => String(f.id) === String(id));
      if (!folder) break;
      chain.unshift(folder.name);
      id = normalizeFolderId(folder.parentId);
    }
    return chain.length ? chain.join(' / ') : 'Mein Tresor';
  }, [allFoldersCache]);

  const sortFileList = useCallback((list) => {
    const dir = fileSort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (fileSort.key === 'name') {
        return dir * a.name.localeCompare(b.name, 'de', { sensitivity: 'base' });
      }
      if (fileSort.key === 'size') {
        return dir * (getFileSizeBytes(a) - getFileSizeBytes(b));
      }
      return dir * (a.createdAt - b.createdAt);
    });
  }, [fileSort]);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return null;
    return {
      folders: allFoldersCache.filter((f) => !f.trashedAt && f.name.toLowerCase().includes(q)),
      files: sortFileList(allFilesCache.filter((f) => !f.trashedAt && f.name.toLowerCase().includes(q))),
    };
  }, [searchQuery, allFoldersCache, allFilesCache, sortFileList]);

  /**
   * Meldet Luecken in der Journalreihe. Der Tresor wird dabei NICHT gesperrt: die Dateien
   * sind unversehrt, nur die Liste ist womoeglich unvollstaendig. Wer hier ausgesperrt wird,
   * kommt an nichts mehr heran, obwohl alles noch da ist.
   */
  const reportJournalProblems = useCallback((result) => {
    const problems = result?.problems || [];
    if (problems.length === 0) {
      if (result?.checked) {
        pushMachineEvent({ kind: 'journal-audit', entries: result.checked, legacy: result.legacy || 0, verdict: 'lueckenlos' });
      }
      return;
    }
    pushMachineEvent({
      kind: 'journal-audit', entries: result.checked || 0, legacy: result.legacy || 0,
      verdict: 'Luecke', problems: problems.length, detail: problems.slice(0, 3).map((p) => p.detail).join('; ')
    });
    showNotification(
      `Achtung: Die Reihenfolge Ihrer Tresor-Einträge ist nicht lückenlos (${problems[0].detail}). Ihre Dateien sind unversehrt, aber die Liste ist möglicherweise unvollständig. Bitte melden Sie sich bei uns.`,
      'error'
    );
  }, [showNotification]);

  // Am Ende einer Ueberbelegungs-Frist entfernt der Server den Ueberschuss. Er kennt keine Dateinamen,
  // deshalb raeumt der Browser die betroffenen Eintraege aus Liste und Journal und bestaetigt das.
  const applyTrimmedUploads = async (key) => {
    try {
      const res = await authenticatedFetch('/api/tresor/trimmed');
      if (!res.ok) return;
      const { uploads = [] } = await res.json();
      if (!uploads.length) return;
      const ids = new Set(uploads.map((u) => u.upload_id));
      const all = await openRecords('file', await db.files.toArray());
      const betroffen = all.filter((f) => f.uploadId && ids.has(f.uploadId));
      for (const f of betroffen) {
        if (f.clientId) {
          try { await appendFileDelete(authenticatedFetch, key, { clientId: f.clientId }); } catch (_) { /* Journal holt das naechste Geraet nach */ }
        }
        await db.files.delete(f.id);
        await db.fileBlobs.delete(f.id);
      }
      await authenticatedFetch('/api/tresor/trimmed/ack', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uploadIds: [...ids] })
      });
      const namen = betroffen.map((f) => f.name).filter(Boolean);
      showNotification(
        `${uploads.length} Datei${uploads.length === 1 ? ' wurde' : 'en wurden'} entfernt, weil mehr Speicher belegt war als gebucht`
        + (namen.length ? `: ${namen.slice(0, 5).join(', ')}${namen.length > 5 ? ' und weitere' : ''}.` : '.'),
        'error'
      );
    } catch (err) {
      console.error('Applying trimmed uploads failed', err);
    }
  };

  const syncVaultJournal = useCallback(async (key) => {
    if (!key) return;
    // Index-Schluessel direkt aus diesem Schluessel, nicht ueber den State: der ist beim
    // Entsperren noch nicht nachgezogen.
    setIndexKeyProvider(() => getVaultKeyring(getEncryptionKey() || key)?.indexKey || null);
    try {
      const sealedNow = await sealLegacyRows(db);
      if (sealedNow > 0) console.info(`[Tresor] ${sealedNow} lokale Eintraege nachtraeglich verschluesselt.`);
    } catch (err) {
      console.error('Sealing legacy index rows failed', err);
    }
    // Loeschschluessel abgleichen bzw. bei Alt-Tresoren nachtragen. Blockiert nichts.
    ensureDeleteProofKey(authenticatedFetch, key, quotaInfoRef.current?.deleteProofKey || null)
      .then((state) => {
        if (state === 'mismatch') {
          showNotification('Achtung: Auf dem Server ist ein fremder Löschschlüssel für Ihren Tresor hinterlegt. Löschen ist gesperrt. Bitte melden Sie sich beim Support.', 'error');
        } else if (state === 'registered') {
          setQuotaInfo((prev) => ({ ...prev, deleteProofKey: getVaultKeyring(key)?.deleteProof?.publicKeyBase64url || null }));
        }
      })
      .catch((err) => console.error('Delete proof key check failed', err));
    try {
      const result = await syncJournalFromServer(authenticatedFetch, key);
      await bootstrapLocalJournal(authenticatedFetch, key, { getFileType, formatBytes });
      await applyTrimmedUploads(key);
      await loadData();
      reportJournalProblems(result);
    } catch (err) {
      console.error('Journal sync failed', err);
      showNotification('Cloud-Sync fehlgeschlagen. Lokale Daten bleiben verfügbar.', 'error');
    }
  }, [loadData, showNotification, reportJournalProblems]);

  /**
   * Leert den lokalen Zwischenspeicher (Dateiliste, Ordner, entschluesselte Dateien) und baut
   * ihn danach komplett aus dem verschluesselten Cloud-Journal neu auf.
   *
   * Sicherheitsgurt: Geloescht wird nur, wenn der Server ueberhaupt ein Journal hat. Bei einem
   * leeren Journal waere die lokale Liste die einzige existierende Kopie, und "leeren" wuerde sie
   * unwiederbringlich vernichten statt nur einen Cache zu raeumen. Laufende Uploads blockieren
   * ebenfalls, weil sie waehrenddessen in dieselben Tabellen schreiben.
   */
  const handleClearLocalCache = async () => {
    const key = sessionKey;
    if (!key) {
      showNotification('Dafür muss der Tresor entsperrt sein.', 'error');
      return;
    }
    if (hasActiveUploads()) {
      showNotification('Es laufen noch Uploads. Warten Sie, bis sie fertig sind, dann geht es.', 'error');
      return;
    }
    const ok = window.confirm(
      'Lokalen Zwischenspeicher leeren?\n\n'
      + 'Die Dateiliste und alle bereits heruntergeladenen Dateien werden aus diesem Browser '
      + 'entfernt und anschließend frisch aus Ihrem Tresor geladen. In der Cloud wird nichts '
      + 'gelöscht.'
    );
    if (!ok) return;

    setClearingCache(true);
    let cleared = false;
    try {
      const res = await authenticatedFetch('/api/tresor/journal?since=0');
      if (!res.ok) throw new Error('journal_unreachable');
      const data = await res.json();
      if (!(data.events || []).length) {
        showNotification(
          'Abgebrochen. Ihr Tresor hat noch kein Cloud-Journal, die lokale Liste ist gerade die einzige Kopie.',
          'error'
        );
        return;
      }

      await db.transaction('rw', db.files, db.folders, db.fileBlobs, db.meta, async () => {
        await db.files.clear();
        await db.folders.clear();
        await db.fileBlobs.clear();
        await db.meta.put({ key: META_LAST_EVENT, value: 0 });
      });
      cleared = true;

      const result = await syncJournalFromServer(authenticatedFetch, key);
      await loadData();
      showNotification('Lokaler Zwischenspeicher geleert und aus Ihrem Tresor neu aufgebaut.', 'success');
      reportJournalProblems(result);
    } catch (err) {
      console.error('Clear local cache failed', err);
      showNotification(
        cleared
          ? 'Geleert, aber der Neuaufbau ist fehlgeschlagen. Sperren Sie den Tresor und entsperren Sie ihn erneut, dann wird er nachgeholt. In der Cloud fehlt nichts.'
          : 'Leeren fehlgeschlagen. Es wurde nichts gelöscht.',
        'error'
      );
    } finally {
      setClearingCache(false);
    }
  };

  const sortedFiles = useMemo(() => sortFileList(files), [files, sortFileList]);

  const visibleFolders = isSearchActive ? (searchResults?.folders ?? []) : folders;
  const visibleFiles = isSearchActive ? (searchResults?.files ?? []) : sortedFiles;

  // Recycle-bin roots: a trashed folder whose parent is NOT itself trashed (its subtree is
  // shown/restored as one unit) plus files trashed individually (parent folder not trashed).
  const trashRoots = useMemo(() => {
    const folderByKey = new Map(allFoldersCache.map((f) => [normalizeFolderId(f.id), f]));
    const parentTrashed = (parentId) => {
      const pk = normalizeFolderId(parentId);
      if (pk === 'root') return false;
      const parent = folderByKey.get(pk);
      return !!(parent && parent.trashedAt);
    };
    const folders = allFoldersCache
      .filter((f) => f.trashedAt && !parentTrashed(f.parentId))
      .map((f) => ({ kind: 'folder', ...f }));
    const files = allFilesCache
      .filter((f) => f.trashedAt && !parentTrashed(f.folderId))
      .map((f) => ({ kind: 'file', ...f }));
    return [...folders, ...files].sort((a, b) => (b.trashedAt || 0) - (a.trashedAt || 0));
  }, [allFoldersCache, allFilesCache]);

  // Client-driven permanent deletion once the 14-day window elapses (also emits the
  // journal file_delete other devices need). Idempotent + safe if two devices both fire.
  useEffect(() => {
    if (appState !== 'vault' || !isUnlocked) return;
    const now = Date.now();
    const expired = trashRoots.filter((it) => it.trashedAt && now - it.trashedAt > RECYCLE_WINDOW_MS);
    if (expired.length === 0) return;
    (async () => {
      for (const it of expired) {
        try { await permanentDeleteTrashItem(it); }
        catch (err) { console.error('Auto-purge failed', err); }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appState, isUnlocked, trashRoots.length]);

  useEffect(() => {
    const updateCols = () => {
      if (window.matchMedia('(min-width: 1024px)').matches) setFolderGridCols(4);
      else if (window.matchMedia('(min-width: 768px)').matches) setFolderGridCols(3);
      else if (window.matchMedia('(min-width: 640px)').matches) setFolderGridCols(2);
      else setFolderGridCols(1);
    };
    updateCols();
    window.addEventListener('resize', updateCols);
    return () => window.removeEventListener('resize', updateCols);
  }, []);

  useEffect(() => {
    setFoldersExpanded(false);
  }, [currentFolderId, searchQuery]);

  const folderCollapseLimit = folderGridCols * FOLDER_GRID_MAX_ROWS;
  const canExpandFolders = visibleFolders.length > folderCollapseLimit;
  const displayedFolders = foldersExpanded || !canExpandFolders
    ? visibleFolders
    : visibleFolders.slice(0, folderCollapseLimit);
  const hiddenFolderCount = Math.max(0, visibleFolders.length - folderCollapseLimit);

  const toggleFileSort = (key) => {
    setFileSort((prev) => {
      const next = prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'name' ? 'asc' : 'desc' };
      localStorage.setItem('tresor_file_sort', JSON.stringify(next));
      return next;
    });
  };

  const moveFileToFolder = useCallback(async (fileId, targetFolderId) => {
    const numericId = Number(fileId);
    const file = await openRecord('file', await db.files.get(numericId));
    if (!file) {
      showNotification('Datei nicht gefunden.', 'error');
      return;
    }
    const targetKey = normalizeFolderId(targetFolderId);
    const currentKey = normalizeFolderId(file.folderId);
    if (targetKey === currentKey) return true;

    const targetFolderClientId = targetKey === 'root'
      ? 'root'
      : ((await db.folders.get(Number(targetFolderId)))?.clientId || null);
    if (!targetFolderClientId) {
      // Ohne Kennung wuerde das Journal „Mein Tresor“ melden, waehrend die Datei hier im Ordner
      // laege: auf dem naechsten Geraet stuende sie woanders.
      showNotification('Dieser Ordner lässt sich gerade nicht als Ziel verwenden. Bitte den Tresor einmal sperren und wieder entsperren.', 'error');
      return false;
    }

    await db.files.update(numericId, { folderId: targetKey, folderClientId: targetFolderClientId });

    const viewKey = normalizeFolderId(currentFolderIdRef.current);
    if (viewKey === currentKey) {
      setFiles((prev) => prev.filter((f) => f.id !== numericId));
    } else if (viewKey === targetKey) {
      setFiles((prev) => [...prev, { ...file, folderId: targetKey, folderClientId: targetFolderClientId }]);
    }

    let journalFailed = false;
    const key = getEncryptionKey();
    if (key && file.clientId) {
      try {
        await appendFileMove(authenticatedFetch, key, {
          clientId: file.clientId,
          parentClientId: targetFolderClientId
        });
      } catch (err) {
        console.error('Journal file_move failed', err);
        journalFailed = true;
      }
    }

    await loadData();

    const folderLabel = targetKey === 'root'
      ? 'Mein Tresor'
      : ((await openRecord('folder', await db.folders.get(Number(targetFolderId))))?.name || 'Ordner');
    showNotification(
      journalFailed
        ? `„${file.name}" lokal nach „${folderLabel}" verschoben (Cloud-Sync offen).`
        : `„${file.name}" nach „${folderLabel}" verschoben.`,
      journalFailed ? 'info' : 'success'
    );
    return true;
  }, [loadData, showNotification, getEncryptionKey]);

  // Ordner samt Inhalt verschieben. Nie in sich selbst oder einen eigenen Unterordner.
  const moveFolderToFolder = useCallback(async (folderId, targetFolderId) => {
    const numericId = Number(folderId);
    const raw = await db.folders.get(numericId);
    const folder = await openRecord('folder', raw);
    if (!folder) { showNotification('Ordner nicht gefunden.', 'error'); return false; }
    const targetKey = normalizeFolderId(targetFolderId);
    if (targetKey === normalizeFolderId(folder.parentId)) return true;
    const allFolders = await openRecords('folder', await db.folders.toArray());
    const subtree = new Set(collectFolderDeleteOrder(numericId, allFolders).map(String));
    if (targetKey !== 'root' && subtree.has(String(targetKey))) {
      showNotification('Ein Ordner kann nicht in sich selbst oder einen seiner Unterordner verschoben werden.', 'error');
      return false;
    }
    let targetClientId = 'root';
    let targetName = 'Mein Tresor';
    if (targetKey !== 'root') {
      const target = allFolders.find((f) => String(f.id) === String(targetKey));
      if (!target || target.trashedAt || !target.clientId) {
        showNotification('Dieser Ordner lässt sich gerade nicht als Ziel verwenden.', 'error');
        return false;
      }
      targetClientId = target.clientId;
      targetName = target.name;
    }
    if (!folder.clientId) {
      showNotification('Dieser Ordner lässt sich gerade nicht verschieben. Bitte den Tresor einmal sperren und wieder entsperren.', 'error');
      return false;
    }
    const duplicate = allFolders.some((f) => f.id !== numericId && !f.trashedAt
      && normalizeFolderId(f.parentId) === targetKey
      && f.name.toLowerCase() === folder.name.toLowerCase());
    if (duplicate) {
      showNotification(`In „${targetName}" gibt es bereits einen Ordner „${folder.name}".`, 'error');
      return false;
    }
    await db.folders.update(numericId, {
      parentId: targetKey === 'root' ? 'root' : Number(targetKey),
      parentClientId: targetClientId
    });
    let journalFailed = false;
    const key = getEncryptionKey();
    try {
      if (!key) throw new Error('locked');
      await appendFolderMove(authenticatedFetch, key, { clientId: folder.clientId, parentClientId: targetClientId });
    } catch (err) {
      console.error('Journal folder_move failed', err);
      journalFailed = true;
    }
    await loadData();
    showNotification(
      journalFailed
        ? `Ordner „${folder.name}" lokal nach „${targetName}" verschoben (Cloud-Sync offen).`
        : `Ordner „${folder.name}" nach „${targetName}" verschoben.`,
      journalFailed ? 'info' : 'success'
    );
    return true;
  }, [loadData, showNotification, getEncryptionKey]);

  const startRenameFile = (file) => {
    setCtxMenu(null);
    setRenameFileTarget(file);
    setRenameFileName(file.name);
  };

  const submitRenameFile = async (e) => {
    e?.preventDefault();
    if (!renameFileTarget) return;
    const validation = validateFileName(renameFileName);
    if (!validation.ok) { showNotification(validation.error, 'error'); return; }
    if (validation.name === renameFileTarget.name) { setRenameFileTarget(null); return; }
    try {
      const raw = await db.files.get(renameFileTarget.id);
      if (!raw) { showNotification('Datei nicht gefunden.', 'error'); return; }
      const fileType = getFileType(validation.name);
      await db.files.update(renameFileTarget.id, await sealPatch('file', raw, { name: validation.name, type: fileType }));
      let journalFailed = false;
      const key = getEncryptionKey();
      if (key && raw.clientId) {
        try {
          await appendFileRename(authenticatedFetch, key, { clientId: raw.clientId, name: validation.name, fileType });
        } catch (err) {
          console.error('Journal file_rename failed', err);
          journalFailed = true;
        }
      } else journalFailed = true;
      setRenameFileTarget(null);
      setRenameFileName('');
      await loadData();
      showNotification(
        journalFailed ? 'Datei lokal umbenannt (Cloud-Sync offen).' : `Datei in „${validation.name}" umbenannt.`,
        journalFailed ? 'info' : 'success'
      );
    } catch (err) {
      console.error('File rename failed', err);
      showNotification('Datei konnte nicht umbenannt werden.', 'error');
    }
  };

  const startMove = (kind, item) => {
    setCtxMenu(null);
    setMoveTarget({ kind, item });
    setMoveDest(null);
  };

  const submitMove = async () => {
    if (!moveTarget || moveDest === null || moveBusy) return;
    setMoveBusy(true);
    try {
      const ok = moveTarget.kind === 'folder'
        ? await moveFolderToFolder(moveTarget.item.id, moveDest)
        : await moveFileToFolder(moveTarget.item.id, moveDest);
      if (ok) { setMoveTarget(null); setMoveDest(null); }
    } finally {
      setMoveBusy(false);
    }
  };

  // --- Kontextmenue: Rechtsklick, langes Druecken (Touch), ⋮-Knopf ---
  const openCtxMenu = (kind, item, x, y, sheet) => {
    setOpenFolderMenuId(null);
    setCtxMenu({ kind, item, x, y, sheet: sheet ?? isCoarsePointer() });
  };
  const openCtxFromButton = (e, kind, item) => {
    e.stopPropagation();
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    openCtxMenu(kind, item, r.right, r.bottom);
  };
  const onItemContextMenu = (e, kind, item) => {
    e.preventDefault();
    e.stopPropagation();
    // Android meldet langes Druecken zusaetzlich als contextmenu: nicht doppelt oeffnen.
    if (ctxMenu && ctxMenu.kind === kind && ctxMenu.item.id === item.id) return;
    // Nur bei Touch folgt noch ein Klick, den es zu schlucken gilt. Bei der Maus nicht.
    if (longPressRef.current.touching) longPressRef.current.firedAt = Date.now();
    openCtxMenu(kind, item, e.clientX, e.clientY);
  };
  const longPressHandlers = (kind, item) => ({
    onTouchStart: (e) => {
      const t = e.touches[0];
      const lp = longPressRef.current;
      clearTimeout(lp.timer);
      lp.firedAt = 0;
      lp.touching = true;
      lp.x = t.clientX;
      lp.y = t.clientY;
      lp.timer = setTimeout(() => {
        lp.firedAt = Date.now();
        try { navigator.vibrate?.(10); } catch (_) { /* egal */ }
        openCtxMenu(kind, item, lp.x, lp.y, true);
      }, 500);
    },
    onTouchMove: (e) => {
      const t = e.touches[0];
      const lp = longPressRef.current;
      if (Math.abs(t.clientX - lp.x) > 10 || Math.abs(t.clientY - lp.y) > 10) clearTimeout(lp.timer);
    },
    onTouchEnd: (e) => {
      const lp = longPressRef.current;
      clearTimeout(lp.timer);
      lp.touching = false;
      if (lp.firedAt) e.preventDefault(); // kein Klick hinterher, also kein Download
    },
    onTouchCancel: () => { clearTimeout(longPressRef.current.timer); longPressRef.current.touching = false; }
  });
  // Klick nach langem Druecken schlucken (falls der Browser ihn trotzdem schickt).
  const swallowLongPressClick = () => {
    const lp = longPressRef.current;
    const swallow = lp.firedAt && Date.now() - lp.firedAt < 1500;
    lp.firedAt = 0;
    return !!swallow;
  };

  const foldersAtLimit = totalFolderCount >= MAX_VAULT_FOLDERS;

  const openNewFolderModal = () => {
    if (foldersAtLimit) {
      showNotification(`Maximal ${MAX_VAULT_FOLDERS} Ordner im Tresor erlaubt.`, 'error');
      return;
    }
    setShowFolderModal(true);
  };

  const isDropTargetActive = (targetId) => {
    if (folderDropTargetId === null || folderDropTargetId === undefined) return false;
    return String(folderDropTargetId) === String(targetId);
  };

  useEffect(() => {
    currentFolderIdRef.current = currentFolderId;
  }, [currentFolderId]);

  useEffect(() => {
    if (appState === 'vault' && isUnlocked) loadData();
  }, [currentFolderId, appState, isUnlocked, loadData]);

  const getBreadcrumbs = useCallback(() => {
    const crumbs = [{ id: 'root', name: 'Mein Tresor' }];
    // Folder ids arrive as numbers from Dexie but as strings from normalizeFolderId (that is
    // how parentId is stored and how navigateToFolder is called after an upload). A strict
    // comparison therefore missed the very first hop, so the bar showed either nothing at all
    // or only the deepest folder instead of the full path.
    const startKey = normalizeFolderId(currentFolderId);
    if (startKey === 'root') return crumbs;

    const chain = [];
    const seen = new Set();
    let id = startKey;
    while (id !== 'root' && !seen.has(id)) {
      seen.add(id);
      const folder = allFoldersCache.find((f) => String(f.id) === id);
      if (!folder) break;
      chain.unshift({ id: folder.id, name: folder.name });
      id = normalizeFolderId(folder.parentId);
    }
    return [...crumbs, ...chain];
  }, [currentFolderId, allFoldersCache]);

  const openFolder = (folder) => {
    setSearchQuery('');
    setOpenFolderMenuId(null);
    setCurrentFolderId(folder.id);
    if (isSidebarOpen) setIsSidebarOpen(false);
  };

  const navigateToFolder = (folderId) => {
    setSearchQuery('');
    setOpenFolderMenuId(null);
    setShowTrash(false);
    setCurrentFolderId(folderId);
    if (isSidebarOpen) setIsSidebarOpen(false);
  };

  const startRenameFolder = (folder) => {
    setOpenFolderMenuId(null);
    setRenameFolderTarget(folder);
    setRenameFolderName(folder.name);
  };

  const submitRenameFolder = async (e) => {
    e?.preventDefault();
    if (!renameFolderTarget) return;
    const validation = validateFolderName(renameFolderName);
    if (!validation.ok) {
      showNotification(validation.error, 'error');
      return;
    }
    const duplicate = allFoldersCache.some(
      (f) => f.id !== renameFolderTarget.id
        && normalizeFolderId(f.parentId) === normalizeFolderId(renameFolderTarget.parentId)
        && f.name.toLowerCase() === validation.name.toLowerCase()
    );
    if (duplicate) {
      showNotification('In diesem Ordner gibt es bereits einen Ordner mit diesem Namen.', 'error');
      return;
    }
    try {
      await db.folders.update(renameFolderTarget.id, await sealPatch('folder', await db.folders.get(renameFolderTarget.id), { name: validation.name }));
      let journalFailed = false;
      const key = getEncryptionKey();
      if (key && renameFolderTarget.clientId) {
        try {
          await appendFolderRename(authenticatedFetch, key, {
            clientId: renameFolderTarget.clientId,
            name: validation.name
          });
        } catch (err) {
          console.error('Journal folder_rename failed', err);
          journalFailed = true;
        }
      }
      setRenameFolderTarget(null);
      setRenameFolderName('');
      await loadData();
      showNotification(
        journalFailed
          ? `Ordner lokal umbenannt (Cloud-Sync offen).`
          : `Ordner in „${validation.name}" umbenannt.`,
        journalFailed ? 'info' : 'success'
      );
    } catch (err) {
      console.error('Folder rename failed', err);
      showNotification('Ordner konnte nicht umbenannt werden.', 'error');
    }
  };

  const getFolderDeleteStats = useCallback((folder) => {
    const folderIds = collectFolderDeleteOrder(folder.id, allFoldersCache);
    const idSet = new Set(folderIds.map(String));
    const fileCount = allFilesCache.filter((f) => {
      const key = normalizeFolderId(f.folderId);
      return key !== 'root' && idSet.has(String(key));
    }).length;
    return { subfolderCount: folderIds.length - 1, fileCount };
  }, [allFoldersCache, allFilesCache]);

  const requestDeleteFolder = (folder) => {
    setOpenFolderMenuId(null);
    setPendingDelete({ type: 'folder', item: folder, stats: getFolderDeleteStats(folder) });
  };

  const requestDeleteFile = (file) => {
    setPendingDelete({ type: 'file', item: file });
  };

  const waitForUploadQueueIdle = async (maxMs = 20000) => {
    const start = Date.now();
    while (queueRunningRef.current && Date.now() - start < maxMs) {
      await new Promise((r) => setTimeout(r, 80));
    }
    return !queueRunningRef.current;
  };

  const removeQueuedUploadsForFolders = (folderIds) => {
    const idSet = new Set(folderIds.map((id) => normalizeFolderId(id)));
    uploadQueueRef.current = uploadQueueRef.current.filter((job) => {
      const fid = normalizeFolderId(job.folderId ?? currentFolderIdRef.current);
      return !idSet.has(fid);
    });
  };

  const clearUploadProgressIds = (uploadIds) => {
    const idSet = new Set(uploadIds.filter(Boolean));
    if (idSet.size === 0) return;
    for (const pendingId of Object.keys(progressRemoveTimersRef.current)) {
      if (idSet.has(pendingId)) {
        clearTimeout(progressRemoveTimersRef.current[pendingId]);
        delete progressRemoveTimersRef.current[pendingId];
      }
    }
    setUploadProgress((prev) => {
      const next = { ...prev };
      for (const id of idSet) delete next[id];
      return next;
    });
  };

  const deleteFileRecord = async (file, key) => {
    let journalFailed = false;
    if (file.uploadId) {
      await deleteCloudUpload(authenticatedFetch, file.uploadId, { masterKey: key });
    }
    if (key && file.clientId) {
      try {
        await appendFileDelete(authenticatedFetch, key, { clientId: file.clientId });
      } catch (err) {
        console.error('Journal file_delete failed', err);
        journalFailed = true;
      }
    }
    await db.files.delete(file.id);
    await db.fileBlobs.delete(file.id);
    if (file.uploadId) clearUploadProgressIds([file.uploadId]);
    return journalFailed;
  };

  // ---------- Recycle bin (Papierkorb) ----------
  // Soft-delete: the server DELETE route now keeps quota + chunks; locally we mark the
  // record trashedAt (it stays visible in the Papierkorb) and journal a file_trash event.
  const trashFileRecord = async (file, key) => {
    let journalFailed = false;
    if (file.uploadId) {
      await deleteCloudUpload(authenticatedFetch, file.uploadId, { masterKey: key });
    }
    if (key && file.clientId) {
      try {
        await appendFileTrash(authenticatedFetch, key, { clientId: file.clientId });
      } catch (err) {
        console.error('Journal file_trash failed', err);
        journalFailed = true;
      }
    }
    await db.files.update(file.id, { trashedAt: Date.now() });
    if (file.uploadId) clearUploadProgressIds([file.uploadId]);
    return journalFailed;
  };

  // One request for a whole subtree instead of N per-file calls — otherwise a folder with
  // more files than the strictLimiter cap (20/min) hits 429 on later calls, and a swallowed
  // 429 would diverge client from server (restored file later backstop-purged; stuck quota).
  const batchUploadOp = async (op, uploadIds) => {
    const ids = (uploadIds || []).filter(Boolean);
    if (ids.length === 0) return true;
    try {
      // Papierkorb und endgueltiges Loeschen tragen den Loeschnachweis, Wiederherstellen nicht.
      const deleteProof = op === 'restore'
        ? undefined
        : await createDeleteProof(authenticatedFetch, getEncryptionKey(), op, ids);
      const res = await authenticatedFetch('/api/tresor/upload/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ op, uploadIds: ids, deleteProof })
      });
      return res.ok;
    } catch (err) {
      console.error('Batch op failed', op, err);
      return false;
    }
  };

  const trashFolder = async (folder, key) => {
    let journalFailed = false;
    const subtreeIds = collectFolderDeleteOrder(folder.id, allFoldersCache);
    // Stop in-flight/queued uploads targeting the subtree first, else a late upload would
    // land as an un-trashed file inside a trashed folder.
    removeQueuedUploadsForFolders(subtreeIds);
    await abortPendingUploadsForFolders(authenticatedFetch, subtreeIds);
    const idKeys = new Set(subtreeIds.map((id) => normalizeFolderId(id)));
    // Schon einzeln Geloeschtes behaelt seinen Zeitpunkt und kommt beim Wiederherstellen des
    // Ordners nicht ungefragt mit zurueck (gleiche Regel wie beim Nachspielen auf anderen Geraeten).
    const filesInSubtree = allFilesCache.filter((f) => !f.trashedAt && idKeys.has(normalizeFolderId(f.folderId)));
    const uploadIds = filesInSubtree.map((f) => f.uploadId).filter(Boolean);
    // Server first: only mutate local state if the server accepted the whole batch.
    const ok = await batchUploadOp('trash', uploadIds);
    if (!ok) throw new Error('Der Papierkorb-Vorgang wurde vom Server abgelehnt.');
    if (key && folder.clientId) {
      try {
        await appendFolderTrash(authenticatedFetch, key, { clientId: folder.clientId });
      } catch (err) {
        console.error('Journal folder_trash failed', err);
        journalFailed = true;
      }
    }
    const ts = Date.now();
    for (const fid of subtreeIds) {
      const f = allFoldersCache.find((x) => String(x.id) === String(fid));
      if (f && f.trashedAt) continue;
      await db.folders.update(Number(fid), { trashedAt: ts }).catch(() => {});
    }
    for (const file of filesInSubtree) await db.files.update(file.id, { trashedAt: ts });
    clearUploadProgressIds(uploadIds);
    return journalFailed;
  };

  // Liegt der Elternordner nicht mehr im Tresor (geloescht oder im Papierkorb), kommt das
  // Wiederhergestellte nach „Mein Tresor“, sonst waere es unsichtbar.
  const parentGone = (parentId) => {
    const pk = normalizeFolderId(parentId);
    if (pk === 'root') return false;
    const parent = allFoldersCache.find((f) => String(f.id) === String(pk));
    return !parent || !!parent.trashedAt;
  };

  const restoreTrashItem = async (item) => {
    const key = getEncryptionKey();
    let toRoot = false;
    try {
      if (item.kind === 'folder') {
        const since = item.trashedAt || 0;
        const subtreeIds = collectFolderDeleteOrder(item.id, allFoldersCache);
        const idKeys = new Set(subtreeIds.map((id) => normalizeFolderId(id)));
        const filesInSubtree = allFilesCache.filter((f) => f.trashedAt && f.trashedAt >= since && idKeys.has(normalizeFolderId(f.folderId)));
        const ok = await batchUploadOp('restore', filesInSubtree.map((f) => f.uploadId).filter(Boolean));
        if (!ok) { showNotification('Wiederherstellen fehlgeschlagen. Bitte erneut versuchen.', 'error'); return; }
        if (key && item.clientId) {
          try { await appendFolderRestore(authenticatedFetch, key, { clientId: item.clientId }); }
          catch (err) { console.error('Journal folder_restore failed', err); }
        }
        for (const fid of subtreeIds) {
          const f = allFoldersCache.find((x) => String(x.id) === String(fid));
          if (f && f.trashedAt && f.trashedAt >= since) await db.folders.update(Number(fid), { trashedAt: null }).catch(() => {});
        }
        for (const file of filesInSubtree) await db.files.update(file.id, { trashedAt: null });
        if (parentGone(item.parentId)) {
          toRoot = true;
          await db.folders.update(Number(item.id), { parentId: 'root', parentClientId: 'root' });
          if (key && item.clientId) {
            try { await appendFolderMove(authenticatedFetch, key, { clientId: item.clientId, parentClientId: 'root' }); }
            catch (err) { console.error('Journal folder_move (restore) failed', err); }
          }
        }
      } else {
        if (item.uploadId) {
          const res = await authenticatedFetch(`/api/tresor/upload/${item.uploadId}/restore`, { method: 'POST' });
          if (!res.ok) { showNotification('Wiederherstellen fehlgeschlagen. Bitte erneut versuchen.', 'error'); return; }
        }
        if (key && item.clientId) {
          try { await appendFileRestore(authenticatedFetch, key, { clientId: item.clientId }); }
          catch (err) { console.error('Journal file_restore failed', err); }
        }
        await db.files.update(item.id, { trashedAt: null });
        if (parentGone(item.folderId)) {
          toRoot = true;
          await db.files.update(item.id, { folderId: 'root', folderClientId: 'root' });
          if (key && item.clientId) {
            try { await appendFileMove(authenticatedFetch, key, { clientId: item.clientId, parentClientId: 'root' }); }
            catch (err) { console.error('Journal file_move (restore) failed', err); }
          }
        }
      }
      await loadData();
      showNotification(
        toRoot
          ? `„${item.name}" wiederhergestellt. Der ursprüngliche Ordner ist nicht mehr da, deshalb liegt es jetzt in „Mein Tresor“.`
          : `„${item.name}" wiederhergestellt.`,
        'success'
      );
    } catch (err) {
      console.error('Restore failed', err);
      showNotification('Wiederherstellen fehlgeschlagen.', 'error');
    }
  };

  // Permanent delete: releases quota + deletes chunks. Returns true only if the server
  // confirmed — local state is NOT mutated on failure (item stays in the bin, retried
  // later / by the 30-day backstop). Idempotent, so cross-device/auto-purge races are safe.
  const permanentDeleteTrashItem = async (item) => {
    const key = getEncryptionKey();
    if (item.kind === 'folder') {
      const subtreeIds = collectFolderDeleteOrder(item.id, allFoldersCache);
      const idKeys = new Set(subtreeIds.map((id) => normalizeFolderId(id)));
      const filesInSubtree = allFilesCache.filter((f) => idKeys.has(normalizeFolderId(f.folderId)));
      const ok = await batchUploadOp('permanent', filesInSubtree.map((f) => f.uploadId).filter(Boolean));
      if (!ok) { showNotification('Endgültiges Löschen fehlgeschlagen. Bitte erneut versuchen.', 'error'); return false; }
      for (const fid of subtreeIds) {
        const f = allFoldersCache.find((x) => String(x.id) === String(fid));
        if (key && f?.clientId) {
          try { await appendFolderDelete(authenticatedFetch, key, { clientId: f.clientId }); }
          catch (err) { console.error('Journal folder_delete failed', err); }
        }
      }
      for (const file of filesInSubtree) { await db.files.delete(file.id); await db.fileBlobs.delete(file.id); }
      for (const fid of subtreeIds) await db.folders.delete(Number(fid)).catch(() => {});
      clearUploadProgressIds(filesInSubtree.map((f) => f.uploadId).filter(Boolean));
    } else {
      if (item.uploadId) {
        let res;
        try {
          const deleteProof = await createDeleteProof(authenticatedFetch, key, 'permanent', [item.uploadId]);
          res = await authenticatedFetch(`/api/tresor/upload/${item.uploadId}/permanent`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ deleteProof })
          });
        } catch (proofErr) {
          showNotification(proofErr?.code === 'DELETE_PROOF_UNAVAILABLE' ? proofErr.message : 'Endgültiges Löschen fehlgeschlagen. Bitte erneut versuchen.', 'error');
          return false;
        }
        if (!res.ok) { showNotification('Endgültiges Löschen fehlgeschlagen. Bitte erneut versuchen.', 'error'); return false; }
      }
      if (key && item.clientId) {
        try { await appendFileDelete(authenticatedFetch, key, { clientId: item.clientId }); }
        catch (err) { console.error('Journal file_delete failed', err); }
      }
      await db.files.delete(item.id); await db.fileBlobs.delete(item.id);
      if (item.uploadId) clearUploadProgressIds([item.uploadId]);
    }
    await loadData();
    await refreshQuota();
    return true;
  };

  const confirmPendingDelete = async () => {
    if (!pendingDelete || deleteInProgress) return;
    setDeleteInProgress(true);
    let success = false;
    try {
      // No queue-idle gate here: moving to the recycle bin is a soft-delete (only sets
      // trashed_at, touches no S3 chunks, applies only to completed vault files), so it
      // cannot race an in-flight upload. Folder trash separately aborts uploads into the
      // trashed subtree. (The old gate hard-deleted chunks and needed the wait.)
      const key = getEncryptionKey();
      if (pendingDelete.type === 'file') {
        const file = (await openRecord('file', await db.files.get(pendingDelete.item.id))) || pendingDelete.item;
        let journalFailed = false;
        try {
          journalFailed = await trashFileRecord(file, key);
        } catch (err) {
          console.error('File trash failed', err);
          showNotification(err.message || 'Datei konnte nicht in den Papierkorb verschoben werden.', 'error');
          return;
        }
        await loadData();
        showNotification(
          journalFailed
            ? 'Datei in den Papierkorb verschoben (Cloud-Sync offen).'
            : `„${file.name}" in den Papierkorb verschoben.`,
          journalFailed ? 'info' : 'success'
        );
        success = true;
      } else {
        const folder = pendingDelete.item;
        const subtreeIds = collectFolderDeleteOrder(folder.id, allFoldersCache);
        let journalFailed = false;
        try {
          journalFailed = await trashFolder(folder, key);
        } catch (err) {
          console.error('Folder trash failed', err);
          showNotification(err.message || 'Ordner konnte nicht in den Papierkorb verschoben werden.', 'error');
          return;
        }
        const trashedIds = new Set(subtreeIds.map(String));
        if (trashedIds.has(String(currentFolderIdRef.current))) {
          setCurrentFolderId('root');
        }
        await loadData();
        showNotification(
          journalFailed
            ? 'Ordner in den Papierkorb verschoben (Cloud-Sync offen).'
            : `Ordner „${folder.name}" in den Papierkorb verschoben.`,
          journalFailed ? 'info' : 'success'
        );
        success = true;
      }
    } catch (err) {
      console.error('Delete failed', err);
      showNotification(err.message || 'Löschen fehlgeschlagen.', 'error');
    } finally {
      setDeleteInProgress(false);
      if (success) setPendingDelete(null);
    }
  };

  const updateBreadcrumbScroll = useCallback(() => {
    const el = breadcrumbScrollRef.current;
    if (!el) return;
    setBreadcrumbScroll({
      canLeft: el.scrollLeft > 4,
      canRight: el.scrollLeft + el.clientWidth < el.scrollWidth - 4
    });
  }, []);

  const scrollBreadcrumbs = (direction) => {
    const el = breadcrumbScrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction * Math.max(160, el.clientWidth * 0.55),
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    if (appState !== 'vault' || !isUnlocked) return;
    const el = breadcrumbScrollRef.current;
    if (!el) return;
    el.scrollLeft = el.scrollWidth;
    updateBreadcrumbScroll();
    const onScroll = () => updateBreadcrumbScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(() => updateBreadcrumbScroll());
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [appState, isUnlocked, currentFolderId, allFoldersCache, updateBreadcrumbScroll]);

  // --- Auth / Unlock Handlers ---
  const randomHex = (n) => Array.from(window.crypto.getRandomValues(new Uint8Array(n)))
    .map(b => b.toString(16).padStart(2, '0')).join('');

  /**
   * Bestandstresor still von HMAC auf Ed25519 umstellen.
   *
   * Nur moeglich, solange die 12 Woerter im Speicher stehen, denn beide Schluessel werden
   * daraus abgeleitet. Wird an den zwei Stellen aufgerufen, an denen der Nutzer sie ohnehin
   * eingegeben hat. Bestbemuehen: schlaegt irgendetwas fehl, bleibt alles wie es war und der
   * naechste Versuch kommt beim naechsten Mal. Der Kunde bekommt davon nichts mit, es aendert
   * fuer ihn nichts ausser dass in unserer Datenbank kein verwertbares Geheimnis mehr liegt.
   */
  const upgradeRecoveryProofIfLegacy = async (mnemonic, recoverySalt) => {
    try {
      if (!mnemonic || !recoverySalt) return;
      if (!(await supportsEd25519())) return;

      const chRes = await authenticatedFetch('/api/tresor/recovery-challenge', { method: 'POST' });
      if (!chRes.ok) return;
      const challenge = await chRes.json();
      if (challenge.proofAlg !== 'hmac') return; // schon umgestellt

      const pair = await deriveRecoveryProofKeyPair(mnemonic, recoverySalt);
      const payload = buildRecoveryProofUpgradePayload({
        userId: challenge.userId,
        challenge: challenge.challenge,
        issuedAt: challenge.issuedAt,
        newProofKey: pair.publicKeyBase64url
      });
      const oldKey = await deriveRecoveryProofKey(mnemonic, recoverySalt);
      const res = await authenticatedFetch('/api/tresor/upgrade-recovery-proof', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          newProofKey: pair.publicKeyBase64url,
          newProofSignature: await signRecoveryResetProofEd25519(pair.privateKey, payload),
          recoveryChallenge: challenge.challenge,
          recoveryChallengeIssuedAt: challenge.issuedAt,
          recoveryProof: await signRecoveryResetProof(oldKey, payload)
        })
      });
      if (res.ok) console.info('[Tresor] Recovery-Nachweis auf Ed25519 umgestellt.');
    } catch (_) {
      // Bewusst still: das ist eine Verbesserung im Hintergrund, kein Arbeitsschritt des Nutzers.
    }
  };

  const completeVaultSetup = async () => {
    const newSalt = randomHex(16);
    const recoverySalt = randomHex(16);

    const masterBits = generateMasterBits();
    const recoveryKek = await deriveKekFromMnemonic(generatedMnemonic, recoverySalt);
    const { wrappedHex, ivHex } = await wrapMasterBits(masterBits, recoveryKek);
    // Neue Tresore hinterlegen nur den oeffentlichen Schluessel. Kann der Browser kein
    // Ed25519 (aeltere Fassungen), bleibt es beim bisherigen HMAC-Wert: lieber ein
    // schwaecherer Nachweis als ein Kunde, der seinen Tresor nicht einrichten kann.
    let recoveryProofValue;
    let recoveryProofAlg = 'hmac';
    if (await supportsEd25519()) {
      try {
        const pair = await deriveRecoveryProofKeyPair(generatedMnemonic, recoverySalt);
        recoveryProofValue = pair.publicKeyBase64url;
        recoveryProofAlg = 'ed25519';
      } catch (_) { /* Rueckfall unten */ }
    }
    if (!recoveryProofValue) {
      recoveryProofValue = exportRecoveryProofKey(await deriveRecoveryProofKey(generatedMnemonic, recoverySalt));
    }
    const passwordKek = await deriveKekFromPassword(passwordInput, newSalt);
    const { wrappedHex: passwordWrapped, ivHex: passwordIv } = await wrapMasterBits(masterBits, passwordKek);
    // Schluesselbund schon jetzt, damit der oeffentliche Loeschschluessel mit der Einrichtung
    // hinterlegt wird. Ein Tresor soll nie ohne ihn dastehen.
    const masterKey = await importMasterKeyFromBits(masterBits);
    const deleteProofKey = getVaultKeyring(masterKey)?.deleteProof?.publicKeyBase64url || null;

    const setupRes = await authenticatedFetch('/api/tresor/setup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        salt: newSalt,
        recoverySalt,
        recoveryWrapped: wrappedHex,
        recoveryIv: ivHex,
        recoveryProofKey: recoveryProofValue,
        recoveryProofAlg,
        passwordWrapped,
        passwordIv,
        deleteProofKey
      })
    });
    if (!setupRes.ok) throw new Error('setup_failed');

    setQuotaInfo(prev => ({
      ...prev,
      salt: newSalt,
      passwordWrapped,
      passwordIv,
      passwordEnvelopeConfigured: true,
      deleteProofKey
    }));
    return masterKey;
  };

  const handleSetupNext = async (e) => {
    if (e) e.preventDefault();
    setErrorMsg('');

    if (setupStep === 'password') {
      const strengthErr = getPasswordStrengthError(passwordInput);
      if (strengthErr) return setErrorMsg(strengthErr);
      if (passwordInput !== passwordConfirmInput) return setErrorMsg('Die Passwörter stimmen nicht überein.');
      setPasswordRecheckInput('');
      setSetupStep('password-recheck');
      return;
    }

    if (setupStep === 'password-recheck') {
      if (passwordRecheckInput !== passwordInput) return setErrorMsg('Das Passwort stimmt nicht mit Schritt 1 überein.');
      const mnemonic = await generateMnemonic();
      setGeneratedMnemonic(mnemonic);
      setMnemonicAcknowledged(false);
      setSetupStep('mnemonic-display');
      return;
    }

    if (setupStep === 'mnemonic-display') {
      if (!mnemonicAcknowledged) return setErrorMsg('Bitte bestätigen Sie, dass Sie die 12 Wörter sicher notiert haben.');
      setMnemonicVerifyInput('');
      setSetupStep('mnemonic-verify');
    }
  };

  const handleSetupComplete = async (e) => {
    if (e) e.preventDefault();
    setErrorMsg('');

    if (!generatedMnemonic || !mnemonicsMatch(mnemonicVerifyInput, generatedMnemonic)) {
      return setErrorMsg('Die eingegebene Phrase stimmt nicht. Bitte prüfen Sie Ihre Notizen und versuchen Sie es erneut.');
    }
    try {
      const key = await completeVaultSetup();
      setSessionKey(key);
      setIsUnlocked(true);
      setIsSetupMode(false);
      setAppState('vault');
      setPasswordInput('');
      setPasswordConfirmInput('');
      setPasswordRecheckInput('');
      setMnemonicVerifyInput('');
      setGeneratedMnemonic(null);
      await syncVaultJournal(key);
      await loadData();
      resumePendingUploads();
    } catch (err) {
      console.error('Setup failed', err);
      setErrorMsg('Setup fehlgeschlagen. Bitte erneut versuchen.');
    }
  };

  // Gemeinsamer Abschluss nach dem Entsperren (Passwort, 12 Woerter, Passkey)
  const finishUnlock = async (key) => {
    setSessionKey(key);
    setIsUnlocked(true);
    setIsSetupMode(false);
    setAppState('vault');
    setPasswordInput('');
    setPasswordConfirmInput('');
    setPasswordRecheckInput('');
    setRecoveryInput('');
    resetAutoLockTimer();
    await syncVaultJournal(key);
    await loadData();
    resumePendingUploads();
  };

  const handlePasskeyUnlock = async () => {
    setErrorMsg('');
    setPasskeyBusy(true);
    try {
      const key = await unlockWithPasskey(authenticatedFetch);
      await finishUnlock(key);
    } catch (err) {
      console.error('Passkey unlock failed', err);
      if (err?.name === 'NotAllowedError') setErrorMsg('Passkey abgebrochen oder nicht bestätigt.');
      else if (err?.name === 'OperationError') setErrorMsg('Mit diesem Passkey ließ sich der Tresor nicht öffnen.');
      else setErrorMsg(err?.message || 'Entsperren mit Passkey fehlgeschlagen.');
    } finally {
      setPasskeyBusy(false);
    }
  };

  const loadPasskeys = useCallback(async () => {
    try {
      const res = await authenticatedFetch('/api/tresor/passkeys');
      if (res.ok) setPasskeys((await res.json()).passkeys || []);
    } catch (_) { /* Anzeige bleibt leer */ }
  }, []);

  // Einrichten: Passwort bestaetigen, damit der Browser die echten Schluesselbytes hat. Der offene
  // Tresor allein reicht nicht, sein Schluessel ist nicht auslesbar (und eine gestohlene Sitzung
  // soll keinen eigenen Passkey eintragen koennen).
  const handleAddPasskey = async (e) => {
    if (e) e.preventDefault();
    setPasskeyBusy(true);
    try {
      const matRes = await authenticatedFetch('/api/tresor/unlock-material');
      if (!matRes.ok) throw new Error('Passwortdaten konnten nicht geladen werden.');
      const material = await matRes.json();
      let bits;
      try {
        const kek = await deriveKekFromPassword(passkeyPw, material.salt);
        bits = await unwrapMasterBits(material.passwordWrapped, material.passwordIv, kek);
      } catch (_) {
        throw new Error('Das Passwort stimmt nicht.');
      }
      try {
        const ua = navigator.userAgent;
        const geraet = /iPhone|iPad/.test(ua) ? 'iPhone/iPad' : /Android/.test(ua) ? 'Android' : /Mac/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : 'Gerät';
        await registerPasskey(authenticatedFetch, { masterBits: bits, email: quotaInfo.email, label: `${geraet}, ${new Date().toLocaleDateString('de-DE')}` });
      } finally {
        bits.fill(0);
      }
      setPasskeyAddOpen(false);
      setPasskeyPw('');
      await loadPasskeys();
      setQuotaInfo((prev) => ({ ...prev, passkeyCount: (prev.passkeyCount || 0) + 1 }));
      showNotification('Passkey eingerichtet. Sie können den Tresor jetzt auch damit entsperren.', 'success');
    } catch (err) {
      console.error('Add passkey failed', err);
      if (err?.name === 'NotAllowedError') showNotification('Passkey abgebrochen.', 'info');
      else if (err?.name === 'SecurityError' && IS_EMBEDDED) showNotification('Ihr Browser erlaubt das Einrichten hier nicht. Bitte den Tresor im eigenen Fenster öffnen und dort einrichten.', 'error');
      else showNotification(err?.message || 'Passkey konnte nicht eingerichtet werden.', 'error');
    } finally {
      setPasskeyBusy(false);
    }
  };

  const handleRemovePasskey = async (pk) => {
    if (!window.confirm(`Passkey „${pk.label || 'ohne Namen'}“ entfernen? Entsperren geht dann weiter mit Passwort oder 12 Wörtern.`)) return;
    try {
      const deleteProof = await createDeleteProof(authenticatedFetch, getEncryptionKey(), 'passkey-remove', [String(pk.id)]);
      const res = await authenticatedFetch(`/api/tresor/passkeys/${pk.id}`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ deleteProof })
      });
      if (!res.ok) throw new Error('remove failed');
      await loadPasskeys();
      setQuotaInfo((prev) => ({ ...prev, passkeyCount: Math.max(0, (prev.passkeyCount || 1) - 1) }));
      showNotification('Passkey entfernt.', 'success');
    } catch (err) {
      showNotification(err?.code === 'DELETE_PROOF_UNAVAILABLE' ? err.message : 'Passkey konnte nicht entfernt werden.', 'error');
    }
  };

  useEffect(() => { passkeySupported().then(setPasskeyOk).catch(() => setPasskeyOk(false)); }, []);
  // Download-Helfer vorab anmelden, damit grosse Downloads ohne Verzoegerung starten.
  useEffect(() => { if (isUnlocked && streamDownloadSupported()) prepareStreamDownload().catch(() => {}); }, [isUnlocked]);
  useEffect(() => { if (showSettingsPanel && isUnlocked) loadPasskeys(); }, [showSettingsPanel, isUnlocked, loadPasskeys]);

  const handleUnlock = async (e) => {
    if (e) e.preventDefault();
    setErrorMsg('');

    let key = null;

    try {
      if (isSetupMode) {
        return;
      } else if (loginMode === 'password') {
        if (!quotaInfo.salt) return setErrorMsg('Kritischer Fehler: Salt fehlt.');
        if (passwordInput.length < 1) return setErrorMsg('Bitte Passwort eingeben.');
        if (!quotaInfo.passwordEnvelopeConfigured) {
          return setErrorMsg(
            'Passwort-Verifikation fehlt noch. Bitte einmal mit Ihrer 12-Wörter Recovery-Phrase entsperren oder unter „Neues Passwort mit Recovery-Phrase setzen" Ihr Passwort neu hinterlegen.'
          );
        }
        // Salz und Huelle erst jetzt holen, nicht schon beim Statusabruf. Beides zusammen
        // erlaubt einen Offline-Rateangriff; es soll so selten wie moeglich uebertragen werden.
        let material = { salt: quotaInfo.salt, passwordWrapped: quotaInfo.passwordWrapped, passwordIv: quotaInfo.passwordIv };
        if (!material.passwordWrapped || !material.passwordIv) {
          const matRes = await authenticatedFetch('/api/tresor/unlock-material');
          if (matRes.status === 409) {
            return setErrorMsg(
              'Passwort-Verifikation fehlt noch. Bitte einmal mit Ihrer 12-Wörter Recovery-Phrase entsperren oder unter „Neues Passwort mit Recovery-Phrase setzen" Ihr Passwort neu hinterlegen.'
            );
          }
          if (matRes.status === 429) {
            return setErrorMsg('Zu viele Versuche. Bitte kurz warten und erneut probieren.');
          }
          if (!matRes.ok) {
            return setErrorMsg('Entsperren gerade nicht möglich. Bitte später erneut versuchen.');
          }
          material = await matRes.json();
        }
        key = await unlockWithPassword(
          passwordInput,
          material.salt || quotaInfo.salt,
          material.passwordWrapped,
          material.passwordIv
        );

      } else if (loginMode === 'recovery') {
        if (!isValidMnemonic(recoveryInput)) return setErrorMsg('Ungültige 12-Wörter Recovery-Phrase.');
        // BIP39-Pruefsumme: faengt ein einzelnes vertipptes Wort hier ab, statt es als
        // "Entschluesselung fehlgeschlagen" zurueckkommen zu lassen. Diese Meldung liest
        // sich wie "deine Dateien sind weg", dabei ist nur ein Wort falsch.
        if (!(await hasValidMnemonicChecksum(recoveryInput))) {
          return setErrorMsg('Die Prüfsumme der 12 Wörter stimmt nicht. Meist ist ein einzelnes Wort vertippt oder zwei sind vertauscht. Bitte den Zettel noch einmal vergleichen.');
        }

        // M3: Fetch this account's wrapped master secret and unwrap it with the entered phrase.
        const infoRes = await authenticatedFetch('/api/tresor/recovery-info');
        if (!infoRes.ok) throw new Error('recovery_info_failed');
        const info = await infoRes.json();
        if (!info.recoveryWrapped) return setErrorMsg('Für dieses Konto ist keine Recovery-Phrase hinterlegt.');

        const kek = await deriveKekFromMnemonic(recoveryInput, info.recoverySalt);
        const masterBits = await unwrapMasterBits(info.recoveryWrapped, info.recoveryIv, kek);
        key = await importMasterKeyFromBits(masterBits);

        // Die Woerter stimmen und liegen gerade vor: guenstigster Moment, einen
        // Bestandstresor still auf Ed25519 umzustellen. Blockiert das Entsperren nicht.
        upgradeRecoveryProofIfLegacy(recoveryInput, info.recoverySalt);
      }

      if (!key) return;

      await finishUnlock(key);

      if (loginMode === 'recovery' && !quotaInfo.passwordEnvelopeConfigured) {
        showNotification(
          'Entsperrt per Recovery-Phrase. Für Passwort-Login: Einstellungen → Passwort mit Recovery-Phrase ändern.',
          'info'
        );
      }
    } catch (err) {
      console.error('Unlock failed', err);
      if (err?.message === 'PASSWORD_ENVELOPE_MISSING') {
        setErrorMsg(
          'Passwort-Verifikation fehlt noch. Bitte Recovery-Phrase nutzen oder Passwort über „Neues Passwort mit Recovery-Phrase setzen" neu hinterlegen.'
        );
      } else if (loginMode === 'recovery') {
        setErrorMsg('Recovery fehlgeschlagen. Bitte Phrase prüfen.');
      } else {
        setErrorMsg('Passwort inkorrekt.');
      }
    }
  };

  const handleResetNext = async (e) => {
    if (e) e.preventDefault();
    setErrorMsg('');

    if (resetStep === 'mnemonic') {
      if (!isValidMnemonic(recoveryInput)) return setErrorMsg('Ungültige 12-Wörter Recovery-Phrase.');
      // BIP39-Pruefsumme: faengt ein einzelnes vertipptes Wort hier ab, statt es als
      // "Entschluesselung fehlgeschlagen" zurueckkommen zu lassen. Diese Meldung liest
      // sich wie "deine Dateien sind weg", dabei ist nur ein Wort falsch.
      if (!(await hasValidMnemonicChecksum(recoveryInput))) {
        return setErrorMsg('Die Prüfsumme der 12 Wörter stimmt nicht. Meist ist ein einzelnes Wort vertippt oder zwei sind vertauscht. Bitte den Zettel noch einmal vergleichen.');
      }
      try {
        const infoRes = await authenticatedFetch('/api/tresor/recovery-info');
        if (!infoRes.ok) throw new Error('recovery_info_failed');
        const info = await infoRes.json();
        if (!info.recoveryWrapped) return setErrorMsg('Für dieses Konto ist keine Recovery-Phrase hinterlegt.');
        const kek = await deriveKekFromMnemonic(recoveryInput, info.recoverySalt);
        await unwrapMasterBits(info.recoveryWrapped, info.recoveryIv, kek);
      } catch {
        return setErrorMsg('Recovery-Phrase konnte nicht verifiziert werden.');
      }
      setPasswordInput('');
      setPasswordConfirmInput('');
      setPasswordRecheckInput('');
      setResetStep('password');
      return;
    }

    if (resetStep === 'password') {
      const strengthErr = getPasswordStrengthError(passwordInput);
      if (strengthErr) return setErrorMsg(strengthErr);
      if (passwordInput !== passwordConfirmInput) return setErrorMsg('Die Passwörter stimmen nicht überein.');
      setPasswordRecheckInput('');
      setResetStep('password-recheck');
    }
  };

  const handleResetComplete = async (e) => {
    if (e) e.preventDefault();
    setErrorMsg('');

    if (passwordRecheckInput !== passwordInput) {
      return setErrorMsg('Das Passwort stimmt nicht mit Schritt 2 überein.');
    }

    try {
      const infoRes = await authenticatedFetch('/api/tresor/recovery-info');
      if (!infoRes.ok) throw new Error('recovery_info_failed');
      const info = await infoRes.json();
      const recoveryKek = await deriveKekFromMnemonic(recoveryInput, info.recoverySalt);
      const masterBits = await unwrapMasterBits(info.recoveryWrapped, info.recoveryIv, recoveryKek);

      const newSalt = randomHex(16);
      const passwordKek = await deriveKekFromPassword(passwordInput, newSalt);
      const { wrappedHex, ivHex } = await wrapMasterBits(masterBits, passwordKek);
      const challengeRes = await authenticatedFetch('/api/tresor/recovery-challenge', { method: 'POST' });
      if (!challengeRes.ok) {
        if (challengeRes.status === 409) throw new Error('recovery_proof_not_configured');
        const challengeErr = await challengeRes.json().catch(() => ({}));
        throw new Error(challengeErr.error || 'recovery_challenge_failed');
      }
      const challenge = await challengeRes.json();
      const proofPayload = buildRecoveryResetProofPayload({
        userId: challenge.userId,
        challenge: challenge.challenge,
        issuedAt: challenge.issuedAt,
        salt: newSalt,
        passwordWrapped: wrappedHex,
        passwordIv: ivHex
      });
      // Womit unterschrieben wird, sagt der Server: Bestandstresore per HMAC, neue mit
      // Ed25519. Raten waere hier fatal, ein falsches Verfahren sieht aus wie eine falsche
      // Recovery-Phrase.
      let recoveryProof;
      if (challenge.proofAlg === 'ed25519') {
        if (!(await supportsEd25519())) {
          throw new Error('ed25519_unsupported');
        }
        const pair = await deriveRecoveryProofKeyPair(recoveryInput, info.recoverySalt);
        recoveryProof = await signRecoveryResetProofEd25519(pair.privateKey, proofPayload);
      } else {
        const proofKey = await deriveRecoveryProofKey(recoveryInput, info.recoverySalt);
        recoveryProof = await signRecoveryResetProof(proofKey, proofPayload);
      }

      const resetRes = await authenticatedFetch('/api/tresor/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          salt: newSalt,
          passwordWrapped: wrappedHex,
          passwordIv: ivHex,
          recoveryChallenge: challenge.challenge,
          recoveryChallengeIssuedAt: challenge.issuedAt,
          recoveryProof
        })
      });
      if (!resetRes.ok) throw new Error('reset_failed');

      // Zweiter Moment mit den Woertern im Speicher: auch hier den Bestandstresor
      // still umstellen, falls er noch auf dem alten Verfahren steht.
      upgradeRecoveryProofIfLegacy(recoveryInput, info.recoverySalt);

      const key = await importMasterKeyFromBits(masterBits);
      setQuotaInfo(prev => ({
        ...prev,
        salt: newSalt,
        passwordWrapped: wrappedHex,
        passwordIv: ivHex,
        passwordEnvelopeConfigured: true
      }));
      setSessionKey(key);
      setIsUnlocked(true);
      setLoginMode('password');
      setResetStep('mnemonic');
      setAppState('vault');
      setPasswordConfirmInput('');
      setPasswordRecheckInput('');
      setRecoveryInput('');
      setPasswordInput('');
      await loadData();
      resumePendingUploads();
    } catch (err) {
      console.error('Password reset failed', err);
      if (err.message === 'recovery_proof_not_configured') {
        setErrorMsg('Dieser Tresor wurde vor der neuen Recovery-Prüfung eingerichtet. Bitte Tresor neu einrichten, bevor Passwort-Reset sicher genutzt werden kann.');
        return;
      }
      if (err.message === 'ed25519_unsupported') {
        setErrorMsg('Dieser Browser kann das Signaturverfahren Ihres Tresors nicht. Bitte den Browser aktualisieren oder es mit einem aktuellen Firefox, Chrome, Edge oder Safari versuchen. Ihre Dateien sind davon nicht betroffen.');
        return;
      }
      setErrorMsg('Passwort-Reset fehlgeschlagen. Bitte Recovery-Phrase und Eingaben prüfen.');
    }
  };

  const applyStatusToQuota = (d) => {
    setQuotaInfo((prev) => ({
      ...prev,
      limit: d.limit,
      used: d.used,
      tresorActive: d.active != null ? !!d.active : prev.tresorActive,
      plan: d.plan || prev.plan,
      planLabel: d.planLabel || prev.planLabel,
      canUpgrade: d.canUpgrade !== false,
      upgradeOptions: d.upgradeOptions || [],
      cancelPending: d.cancelPending != null ? !!d.cancelPending : prev.cancelPending,
      cancelAt: d.cancelAt != null ? d.cancelAt : prev.cancelAt,
      dataDeleteBy: d.dataDeleteBy != null ? d.dataDeleteBy : prev.dataDeleteBy,
      prepaidUntil: d.prepaidUntil !== undefined ? d.prepaidUntil : prev.prepaidUntil,
      prepaidDeleteBy: d.prepaidDeleteBy !== undefined ? d.prepaidDeleteBy : prev.prepaidDeleteBy,
      isTrial: d.isTrial != null ? !!d.isTrial : prev.isTrial,
      readonly: d.readonly != null ? !!d.readonly : prev.readonly,
      trialEndsAt: d.trialEndsAt !== undefined ? d.trialEndsAt : prev.trialEndsAt,
      trialDeleteBy: d.trialDeleteBy !== undefined ? d.trialDeleteBy : prev.trialDeleteBy,
      trialGraceDays: d.trialGraceDays || prev.trialGraceDays
    }));
    if (typeof d.storageConfigured === 'boolean') setCloudStorageReady(d.storageConfigured);
  };

  const refreshQuota = async () => {
    try {
      const statusRes = await authenticatedFetch('/api/tresor/status');
      if (statusRes.ok) applyStatusToQuota(await statusRes.json());
    } catch (_) { /* ignore */ }
  };

  const formatGermanDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch (_) {
      return null;
    }
  };

  const handleTresorCancel = async () => {
    setCancelInProgress(true);
    try {
      const res = await authenticatedFetch('/api/tresor/cancel', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(data.message || data.error || 'Kündigung fehlgeschlagen.', 'error');
        return;
      }
      await refreshQuota();
      setShowCancelModal(false);
      setShowSettingsPanel(false);
      showNotification(data.message || 'Tresor-Tarif gekündigt.', 'success');
    } catch (_) {
      showNotification('Kündigung konnte nicht durchgeführt werden. Bitte später erneut versuchen.', 'error');
    } finally {
      setCancelInProgress(false);
    }
  };

  const handleTresorReactivate = async () => {
    setReactivateInProgress(true);
    try {
      const res = await authenticatedFetch('/api/tresor/reactivate', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(data.message || data.error || 'Rücknahme fehlgeschlagen.', 'error');
        return;
      }
      await refreshQuota();
      showNotification(data.message || 'Ihre Kündigung wurde zurückgenommen.', 'success');
    } catch (_) {
      showNotification('Rücknahme konnte nicht durchgeführt werden. Bitte später erneut versuchen.', 'error');
    } finally {
      setReactivateInProgress(false);
    }
  };

  const handleLightningRenew = async () => {
    setLightningRenewInProgress(true);
    try {
      const res = await authenticatedFetch('/api/tresor/lightning-renew', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: quotaInfo.plan })
      });
      const d = await res.json().catch(() => ({}));
      if (d.checkoutLink) navigateTop(d.checkoutLink, { replace: false });
      else throw new Error(d.error || 'no link');
    } catch (e) {
      showNotification('Lightning-Verlängerung derzeit nicht verfügbar. Bitte später erneut versuchen.', 'error');
    } finally {
      setLightningRenewInProgress(false);
    }
  };

  const extractApiError = (data, fallback = 'Aktion fehlgeschlagen.') => {
    if (!data) return fallback;
    if (typeof data.message === 'string' && data.message) return data.message;
    if (typeof data.error === 'string' && data.error) return data.error;
    if (Array.isArray(data.errors) && data.errors[0]?.msg) return data.errors[0].msg;
    return fallback;
  };

  const closeBookingModal = () => {
    if (upgradeInProgress) return;
    setShowBookingModal(false);
    setBookingPlan(null);
    setBookingAgbAccepted(false);
    setBookingPrivacyAccepted(false);
    setBookingCouponCode('');
    setBookingCouponOpen(false);
  };

  const openBookingModal = (plan, cycle, mode = 'subscribe') => {
    setBookingPlan(plan);
    setBookingCycle(cycle);
    setBookingMode(mode);
    setBookingAgbAccepted(false);
    setBookingPrivacyAccepted(false);
    setBookingCouponCode('');
    setBookingCouponOpen(false);
    setShowBookingModal(true);
  };

  const executeTresorBooking = async (targetPlan, { coupon, reloadOnSuccess = false } = {}) => {
    setUpgradeInProgress(targetPlan);
    try {
      const res = await authenticatedFetch('/api/tresor/upgrade', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: targetPlan,
          coupon: coupon?.trim() || undefined
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(extractApiError(data, 'Buchung fehlgeschlagen.'), 'error');
        return false;
      }
      closeBookingModal();
      setShowUpgradeModal(false);
      if (reloadOnSuccess) {
        showNotification(data.message || `Tresor mit ${TIER_BY_ID[targetPlan]?.size || targetPlan} aktiviert.`, 'success');
        window.location.reload();
        return true;
      }
      await refreshQuota();
      showNotification(
        data.message || `Speicher auf ${data.storageGb || TIER_BY_ID[targetPlan]?.gb} GB erweitert.`,
        'success'
      );
      return true;
    } catch (_) {
      showNotification('Buchung konnte nicht durchgeführt werden. Bitte später erneut versuchen.', 'error');
      return false;
    } finally {
      setUpgradeInProgress(null);
    }
  };

  const handleBookingConfirm = async () => {
    if (!bookingPlan) return;
    if (!bookingAgbAccepted || !bookingPrivacyAccepted) {
      showNotification('Bitte bestätigen Sie AGB und Datenschutzerklärung.', 'error');
      return;
    }
    await executeTresorBooking(bookingPlan, {
      coupon: bookingCouponCode,
      reloadOnSuccess: bookingMode === 'subscribe'
    });
  };

  const handleLightningBooking = async () => {
    if (!bookingPlan) return;
    if (!bookingAgbAccepted || !bookingPrivacyAccepted) {
      showNotification('Bitte bestätigen Sie AGB und Datenschutzerklärung.', 'error');
      return;
    }
    setLightningRenewInProgress(true);
    try {
      const res = await authenticatedFetch('/api/tresor/lightning-renew', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: bookingPlan })
      });
      const d = await res.json().catch(() => ({}));
      if (d.checkoutLink) navigateTop(d.checkoutLink, { replace: false });
      else throw new Error(d.error || 'no link');
    } catch (e) {
      showNotification('Lightning-Zahlung derzeit nicht verfügbar. Bitte später erneut versuchen.', 'error');
    } finally {
      setLightningRenewInProgress(false);
    }
  };

  const handleStorageUpgrade = async (targetPlan) => {
    if (!storageAgbAccepted) {
      showNotification('Bitte bestätigen Sie zuerst die AGB.', 'error');
      return;
    }
    await executeTresorBooking(targetPlan);
  };

  const openTrialModal = () => {
    setTrialPlan('starter');
    setTrialCycle('annual');
    setTrialMoreOptions(false);
    setTrialAgbAccepted(false);
    setTrialPrivacyAccepted(false);
    setShowTrialModal(true);
  };

  /** Weg zum bezahlten Tarif aus der Testphase heraus.
   *
   *  Zwei Faelle, die der Server auseinanderhaelt, nicht wir:
   *  1. Der Kunde hat schon ein Datenputzer-Abo -> /api/tresor/upgrade haengt den Tresor
   *     als Position hinein, die hinterlegte Zahlungsmethode zieht. Ein Klick, fertig.
   *  2. Der Kunde kam ueber die Safe-Seite und hat kein Abo -> der Endpunkt antwortet mit
   *     'no_subscription'. Dann uebernimmt der eingebettete Stripe-Checkout auf
   *     datenputzer.de, der Zahlungsdaten und Namen einsammelt.
   *  Deshalb wird hier nicht geraten, sondern die Antwort ausgewertet. */
  const handleTrialBooking = async () => {
    if (!trialAgbAccepted || !trialPrivacyAccepted) {
      showNotification('Bitte bestätigen Sie AGB und Datenschutzerklärung.', 'error');
      return;
    }
    const targetPlan = trialPlan;
    setUpgradeInProgress(targetPlan);
    try {
      const res = await authenticatedFetch('/api/tresor/upgrade', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: targetPlan, cycle: trialCycle })
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        setShowTrialModal(false);
        // Stripe laesst je Abo nur ein Abrechnungsintervall zu. Haengt der Tresor in einem
        // bestehenden Datenputzer-Abo, gewinnt dessen Intervall. Das sagen wir, statt es
        // stillschweigend anders abzurechnen als angeklickt.
        const serverCycle = data.cycle || trialCycle;
        showNotification(
          serverCycle !== trialCycle
            ? `Ihr Tresor ist gebucht. Abgerechnet wird ${cycleLabel(serverCycle)}, weil Ihr bestehendes Datenputzer-Abo ${cycleLabel(serverCycle)} läuft.`
            : (data.message || 'Ihr Tresor ist gebucht.'),
          'success'
        );
        window.setTimeout(() => window.location.reload(), serverCycle !== trialCycle ? 4000 : 800);
        return;
      }

      if (data?.error === 'no_subscription') {
        goToCheckout(targetPlan, trialCycle);
        return;
      }

      showNotification(extractApiError(data, 'Buchung fehlgeschlagen.'), 'error');
    } catch (_) {
      showNotification('Buchung konnte nicht durchgeführt werden. Bitte später erneut versuchen.', 'error');
    } finally {
      setUpgradeInProgress(null);
    }
  };

  const handleLock = async () => {
    await db.keyStore.delete('master_key');

    const uploadsRunning = hasActiveUploads();
    const policy = lockUploadPolicyRef.current;

    if (uploadsRunning && policy === 'finish_current') {
      backgroundKeyRef.current = sessionKey;
      setLockKeepsUploads(true);
      showNotification(
        'Tresor gesperrt. Laufende Uploads werden im Hintergrund weiter verschlüsselt und übertragen, neue Uploads sind pausiert.',
        'info'
      );
    } else {
      backgroundKeyRef.current = null;
      setLockKeepsUploads(false);
      // Der Mitschnitt des Maschinenraums verschwindet mit dem Sperren, aber nur wenn der
      // Schlüssel wirklich weg ist. Bei 'finish_current' laufen Uploads im Hintergrund
      // weiter und schreiben weiter Ereignisse; ein Leeren würde mitten hineinschneiden.
      clearMachineLog();
      if (uploadsRunning && policy === 'strict') {
        showNotification(
          'Tresor streng gesperrt. Laufende Uploads wurden sofort pausiert; entsperren Sie erneut, um fortzufahren.',
          'info'
        );
      }
    }

    setSessionKey(null);
    setIsUnlocked(false);
    setAppState('auth');
    setFolders([]);
    setFiles([]);
    setCurrentFolderId('root');
    setAllFoldersCache([]);
    setPasswordInput('');
    setRecoveryInput('');
    setPasswordConfirmInput('');
    setPasswordRecheckInput('');
    setLoginMode('password');
    setIsDragging(false);
    setDraggingFileId(null);
    setFolderDropTargetId(null);
    if (autoLockTimerRef.current) clearTimeout(autoLockTimerRef.current);
  };

  useEffect(() => {
    handleLockRef.current = handleLock;
  });

  const isExternalFileDrag = (e) => {
    const types = Array.from(e.dataTransfer?.types || []);
    return types.includes('Files');
  };

  // --- Drag & Drop Handlers (external upload + internal file move) ---
  const handleDragEnter = (e) => {
    e.preventDefault();
    if (!isUnlocked || draggingFileId || draggingFolderId) return;
    if (!isExternalFileDrag(e)) return;
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isUnlocked) return;
    if (draggingFileId || draggingFolderId) {
      e.dataTransfer.dropEffect = 'move';
      return;
    }
    if (isExternalFileDrag(e)) e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (draggingFileId || draggingFolderId) return;
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragging(false);

    if (!isUnlocked) return;
    if (e.dataTransfer.getData(TRESOR_FILE_DRAG_TYPE) || e.dataTransfer.getData(TRESOR_FOLDER_DRAG_TYPE)) return;
    if (!getEncryptionKey()) return;

    // webkitGetAsEntry has to be called before this handler yields: the DataTransferItemList
    // is emptied as soon as the drop event finishes, so the entries are grabbed synchronously
    // and only then walked asynchronously.
    const entries = (e.dataTransfer.items ? Array.from(e.dataTransfer.items) : [])
      .filter((item) => item.kind === 'file')
      .map((item) => (item.webkitGetAsEntry ? item.webkitGetAsEntry() : null))
      .filter(Boolean);

    if (entries.some((entry) => entry.isDirectory)) {
      const collected = [];
      for (const entry of entries) {
        try {
          await collectDroppedEntry(entry, '', collected);
        } catch (err) {
          console.error('Ordner konnte nicht gelesen werden', err);
        }
      }
      await enqueueFolderUpload(collected);
      return;
    }

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      enqueueFiles(e.dataTransfer.files);
    }
  };

  const handleFileDragStart = (e, file) => {
    e.dataTransfer.setData(TRESOR_FILE_DRAG_TYPE, String(file.id));
    e.dataTransfer.effectAllowed = 'move';
    setDraggingFileId(file.id);
    setIsDragging(false);
    dragCounterRef.current = 0;
  };

  const handleFileDragEnd = () => {
    setDraggingFileId(null);
    setDraggingFolderId(null);
    setFolderDropTargetId(null);
  };

  const handleFolderDragStart = (e, folder) => {
    e.dataTransfer.setData(TRESOR_FOLDER_DRAG_TYPE, String(folder.id));
    e.dataTransfer.effectAllowed = 'move';
    setDraggingFolderId(folder.id);
    setIsDragging(false);
    dragCounterRef.current = 0;
  };

  const handleFolderDragEnter = (e, folderId) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggingFileId || (draggingFolderId && String(draggingFolderId) !== String(folderId))) setFolderDropTargetId(folderId);
  };

  const handleFolderDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setFolderDropTargetId(null);
    }
  };

  const handleFolderDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggingFileId || draggingFolderId) e.dataTransfer.dropEffect = 'move';
  };

  const handleFolderDrop = async (e, folderId) => {
    e.preventDefault();
    e.stopPropagation();
    const fileId = e.dataTransfer.getData(TRESOR_FILE_DRAG_TYPE) || draggingFileId;
    const draggedFolderId = e.dataTransfer.getData(TRESOR_FOLDER_DRAG_TYPE) || draggingFolderId;
    setDraggingFileId(null);
    setDraggingFolderId(null);
    setFolderDropTargetId(null);
    if (fileId) await moveFileToFolder(fileId, folderId);
    else if (draggedFolderId && String(draggedFolderId) !== String(folderId)) await moveFolderToFolder(draggedFolderId, folderId);
  };

  const renderSortHeader = (label, sortKey, className = '') => {
    const active = fileSort.key === sortKey;
    return (
      <button
        type="button"
        onClick={() => toggleFileSort(sortKey)}
        className={`vault-sort-header ${active ? 'is-active' : ''} ${className}`}
        title={`Nach ${label} sortieren`}
      >
        <span>{label}</span>
        {active ? (
          <ChevronUp className={`w-3.5 h-3.5 shrink-0 transition-transform ${fileSort.dir === 'desc' ? 'rotate-180' : ''}`} />
        ) : (
          <ArrowUpDown className="w-3.5 h-3.5 shrink-0 opacity-40" />
        )}
      </button>
    );
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0 && getEncryptionKey()) {
      enqueueFiles(e.target.files);
      e.target.value = '';
    }
  };

  const handleFolderInput = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (picked.length === 0 || !getEncryptionKey()) return;
    await enqueueFolderUpload(picked.map((file) => ({
      file,
      path: file.webkitRelativePath || file.name
    })));
  };

  const submitNewFolder = async (e) => {
    e?.preventDefault();
    const validation = validateFolderName(newFolderName);
    if (!validation.ok) {
      showNotification(validation.error, 'error');
      return;
    }
    const name = validation.name;
    const duplicate = allFoldersCache.some(
      (f) => normalizeFolderId(f.parentId) === normalizeFolderId(currentFolderId)
        && f.name.toLowerCase() === name.toLowerCase()
    );
    if (duplicate) {
      showNotification('In diesem Ordner gibt es bereits einen Ordner mit diesem Namen.', 'error');
      return;
    }
    try {
      const folderCount = await db.folders.count();
      if (folderCount >= MAX_VAULT_FOLDERS) {
        showNotification(`Maximal ${MAX_VAULT_FOLDERS} Ordner im Tresor erlaubt.`, 'error');
        return;
      }
      const parentKey = normalizeFolderId(currentFolderId);
      const parentClientId = parentKey === 'root'
        ? 'root'
        : ((await db.folders.get(Number(currentFolderId)))?.clientId || 'root');
      const clientId = createClientId();
      await db.folders.add(await sealRecord('folder', {
        clientId,
        parentClientId,
        parentId: parentKey,
        name,
        createdAt: Date.now()
      }));
      let journalFailed = false;
      const key = getEncryptionKey();
      if (key) {
        try {
          await appendFolderCreate(authenticatedFetch, key, { clientId, parentClientId, name });
        } catch (err) {
          console.error('Journal folder_create failed', err);
          journalFailed = true;
        }
      }
      setNewFolderName('');
      setShowFolderModal(false);
      await loadData();
      showNotification(
        journalFailed
          ? `Ordner „${name}" lokal erstellt (Cloud-Sync offen).`
          : `Ordner „${name}" erstellt.`,
        journalFailed ? 'info' : 'success'
      );
    } catch (err) {
      console.error('Folder create failed', err);
      showNotification('Ordner konnte nicht erstellt werden.', 'error');
    }
  };

  const downloadFile = async (fileMeta) => {
    const sessionMasterKey = getEncryptionKey();
    if (!sessionMasterKey) {
      showNotification('Tresor ist gesperrt. Bitte zuerst entsperren.', 'error');
      return;
    }
    // Dateien ab dem 22.10.26 haben einen eigenen Schluessel, aeltere den Hauptschluessel.
    let activeKey;
    try {
      activeKey = await fileKeyFor(sessionMasterKey, fileMeta);
    } catch (keyErr) {
      console.error(keyErr);
      showNotification('Der Schlüssel dieser Datei ließ sich nicht öffnen.', 'error');
      return;
    }
    const size = fileMeta.sizeBytes || 0;
    const isCloud = !!(fileMeta.uploadId && fileMeta.totalChunks);
    const LARGE = 2 * 1024 * 1024 * 1024;
    // Ab 256 MB (die nicht mehr zwischengespeichert werden) direkt als Strom auf die Platte:
    // kein Speichern-Dialog, nie die ganze Datei im Arbeitsspeicher, auch im Tab und in Firefox.
    const STREAM_FROM = 256 * 1024 * 1024;
    if (isCloud && cloudStorageReady && size > STREAM_FROM && streamDownloadSupported()) {
      try {
        showNotification(`„${fileMeta.name}" wird heruntergeladen und dabei entschlüsselt.`, 'info');
        await streamToDownload({
          name: fileMeta.name,
          size,
          bloecke: decryptedChunksInOrder(authenticatedFetch, activeKey, fileMeta, null)
        });
        showNotification(`„${fileMeta.name}" ist entschlüsselt und an Ihren Browser übergeben.`, 'success');
      } catch (err) {
        if (err?.code === 'ABORTED') showNotification('Download abgebrochen.', 'info');
        else if (err?.code === 'EGRESS_LIMIT') showNotification(err.message, 'error');
        else showNotification('Download fehlgeschlagen.', 'error');
        console.error(err);
      }
      return;
    }

    // Files >2 GB stream straight to disk so 10 GB never sits in RAM. showSaveFilePicker MUST
    // be the first awaited call (browser user-gesture rule) — before any DB/network work.
    if (isCloud && cloudStorageReady && size > LARGE) {
      // Hierher kommen nur noch Browser ohne Streaming-Download (Safari). Im eingebetteten Rahmen
      // verbietet der Browser den Speichern-Dialog, dort geht es über das eigene Fenster.
      if (IS_EMBEDDED) {
        showNotification('Dateien über 2 GB laden Sie im eigenen Fenster herunter. Es öffnet sich gleich.', 'info');
        window.open('https://tresor.datenputzer.de/', '_blank', 'noopener');
        return;
      }
      if (typeof window.showSaveFilePicker !== 'function') {
        showNotification('Dateien über 2 GB brauchen zum Herunterladen Chrome oder Edge (Streaming auf die Festplatte).', 'error');
        return;
      }
      let handle;
      try {
        handle = await window.showSaveFilePicker({ suggestedName: fileMeta.name });
      } catch (e) {
        if (e?.name === 'AbortError') return; // user cancelled the save dialog — silent no-op
        showNotification('Speicherort konnte nicht gewählt werden.', 'error');
        return;
      }
      const writable = await handle.createWritable();
      try {
        showNotification('Lade & entschlüssele direkt auf die Festplatte…', 'info');
        await streamDecryptToWritable(authenticatedFetch, activeKey, fileMeta, writable, null);
        await writable.close();
        showNotification(`„${fileMeta.name}" gespeichert.`, 'success');
      } catch (err) {
        try { await writable.abort(); } catch (_) { /* leave no truncated file */ }
        showNotification(err?.code === 'EGRESS_LIMIT' ? err.message : 'Download fehlgeschlagen.', 'error');
        console.error(err);
      }
      return;
    }

    // ≤2 GB: in-RAM path (works in every browser; cached locally only when small).
    try {
      let decryptedBlob;
      const blobRecord = await db.fileBlobs.get(fileMeta.id);
      if (blobRecord?.blob) {
        decryptedBlob = await decryptBlobStream(blobRecord.blob, activeKey, fileMeta.originalType, fileMeta.encryptionId);
      } else if (isCloud && cloudStorageReady) {
        showNotification('Lade verschlüsselte Datei aus der Cloud…', 'info');
        const cacheLocally = size <= 256 * 1024 * 1024;
        decryptedBlob = await fetchAndDecryptFile(authenticatedFetch, activeKey, fileMeta, { cacheLocally });
      } else {
        showNotification('Datei nicht lokal verfügbar und kein Cloud-Download möglich.', 'error');
        return;
      }

      const url = URL.createObjectURL(decryptedBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileMeta.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err?.code === 'EGRESS_LIMIT') {
        showNotification(err.message || 'Monatliches Download-Limit erreicht.', 'error');
      } else {
        showNotification('Entschlüsselung fehlgeschlagen.', 'error');
      }
      console.error(err);
    }
  };

  // --- Helpers ---
  const getFileType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['pdf'].includes(ext)) return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'img';
    if (['txt', 'md', 'csv'].includes(ext)) return 'txt';
    return 'file';
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  const formatDate = (ts) => {
    const d = new Date(ts);
    return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`;
  };

  /** Nach dem ersten erfolgreichen Upload einer Testphase einmalig den Buchungsdialog
   *  anbieten. Der Merker haengt am Konto, damit ein zweites Geraet nicht erneut fragt. */
  const maybeOfferTrialBooking = useCallback(() => {
    if (trialPromptShownRef.current) return;
    const q = quotaInfoRef.current;
    if (!q?.isTrial || q.readonly) return;
    const key = `dp_tresor_trial_prompt_${q.email || 'anon'}`;
    try {
      if (localStorage.getItem(key) === 'seen') { trialPromptShownRef.current = true; return; }
      localStorage.setItem(key, 'seen');
    } catch (_) { /* privater Modus: dann eben einmal pro Sitzung */ }
    trialPromptShownRef.current = true;
    setTrialPlan('starter');
    setTrialCycle('annual');
    setTrialMoreOptions(false);
    setTrialAgbAccepted(false);
    setTrialPrivacyAccepted(false);
    setShowTrialModal(true);
  }, []);

  const drainUploadQueue = useCallback(async () => {
    if (queueRunningRef.current) return;
    queueRunningRef.current = true;
    setIsEncrypting(true);
    try {
      while (uploadQueueRef.current.length > 0) {
        const job = uploadQueueRef.current.shift();
        const activeKey = getEncryptionKey();
        if (!activeKey) {
          uploadQueueRef.current.unshift(job);
          showNotification('Kein Verschlüsselungsschlüssel verfügbar. Bitte Tresor entsperren.', 'error');
          break;
        }

        const result = await runUploadJob({
          file: job.kind === 'file' ? job.file : undefined,
          pendingRecord: job.kind === 'resume' ? job.record : undefined,
          authenticatedFetch,
          activeKey,
          folderId: job.folderId ?? currentFolderIdRef.current,
          onProgress: updateUploadProgress,
          getFileType,
          formatBytes,
          getActiveKey: getEncryptionKey,
          onFileRecorded: recordJournalFile,
          isCancelled: (id) => cancelledUploadsRef.current.has(id),
          onCancelReady: (id, controller) => cancelControllersRef.current.set(id, controller),
        });

        if (result.pendingId) cancelControllersRef.current.delete(result.pendingId);

        if (result.reason === 'cancelled') {
          cancelledUploadsRef.current.delete(job.record?.id);
          // user-initiated cancel — cleanup already done by cancelUpload, no notification
        } else if (result.ok) {
          const savedFolder = normalizeFolderId(job.folderId ?? currentFolderIdRef.current);
          const viewFolder = normalizeFolderId(currentFolderIdRef.current);
          await loadData();
          // Jumping to the target folder helps for a single file. During a folder upload it
          // would drag the view into a different subfolder after every finished file, so those
          // jobs keep the user where they started and the new folder just appears in the list.
          if (savedFolder !== viewFolder && !job.keepView) {
            navigateToFolder(savedFolder);
          }
          await refreshQuota();
          showNotification(`„${result.fileName}" erfolgreich hochgeladen.`, 'success');
          // Erst wenn der Tresor benutzt wurde, ist die Frage nach dem Tarif
          // beantwortbar. Einmal je Geraet, danach nur noch ueber die Einstellungen.
          maybeOfferTrialBooking();
        } else {
          if (result.reason === 'storage_offline') setCloudStorageReady(false);
          const type = result.reason === 'network_error' || result.reason === 'cloud_pending' ? 'info' : 'error';
          showNotification(result.message, type);
          // Volle Testphase oder abgelaufener Schreibschutz: der Buchungsdialog ist die
          // Antwort auf beides, sonst bleibt der Nutzer mit einer Meldung allein.
          if ((result.code === 'quota_exceeded' && result.trial) || result.code === 'readonly') {
            await refreshQuota();
            setShowTrialModal(true);
          }
        }
      }
    } finally {
      queueRunningRef.current = false;
      setIsEncrypting(false);
      finishBackgroundLockIfNeeded();
    }
  }, [getEncryptionKey, updateUploadProgress, finishBackgroundLockIfNeeded, showNotification, cloudStorageReady, loadData, recordJournalFile]);

  const isWriteBlocked = useCallback(() => {
    const q = quotaInfoRef.current;
    if (!q?.readonly) return false;
    showNotification(
      q.isTrial
        ? 'Ihre Testphase ist beendet, neue Uploads sind gesperrt. Ansehen und Herunterladen funktioniert weiter.'
        : 'Ihr Tresor ist schreibgeschützt. Ansehen und Herunterladen funktioniert weiter.',
      'error'
    );
    if (q.isTrial) setShowTrialModal(true);
    return true;
  }, [showNotification]);

  const enqueueFiles = useCallback((fileList) => {
    if (isWriteBlocked()) return;
    const twoGB = 2 * 1024 * 1024 * 1024;
    const tenGB = 10 * 1024 * 1024 * 1024;
    const limit = localStorage.getItem('tresor_allow_large') === 'on' ? tenGB : twoGB;
    let rejected = 0;
    for (const file of Array.from(fileList)) {
      if (file.size > limit) { rejected += 1; continue; }
      uploadQueueRef.current.push({ kind: 'file', file, folderId: currentFolderIdRef.current });
    }
    if (rejected > 0) {
      showNotification(
        limit === twoGB
          ? `${rejected} Datei(en) über 2 GB übersprungen. Für größere: Einstellungen → „Große Dateien (bis 10 GB)" aktivieren (Download braucht Chrome/Edge).`
          : `${rejected} Datei(en) über 10 GB übersprungen (Maximum pro Datei).`,
        'error'
      );
    }
    drainUploadQueue();
  }, [drainUploadQueue, showNotification, isWriteBlocked]);

  // Resolve a relative path from a dropped/picked directory to a vault folder id, creating
  // the missing levels on the way. An existing folder with the same name in the same parent
  // is reused (case-insensitive, matching submitNewFolder's duplicate rule) so uploading the
  // same tree twice merges into it instead of producing look-alike duplicates.
  const ensureFolderPath = useCallback(async (segments, baseFolderId, ctx) => {
    let parentKey = normalizeFolderId(baseFolderId);
    let parentClientId = parentKey === 'root'
      ? 'root'
      : ((await db.folders.get(Number(baseFolderId)))?.clientId || 'root');

    for (const rawSegment of segments) {
      const name = sanitizeFolderSegment(rawSegment);
      const cacheKey = `${parentKey}/${name.toLowerCase()}`;
      const cached = ctx.cache.get(cacheKey);
      if (cached) {
        parentKey = normalizeFolderId(cached.id);
        parentClientId = cached.clientId;
        continue;
      }
      // Namen liegen verschluesselt, deshalb erst lesbar machen, dann vergleichen.
      const siblings = await openRecords('folder', await db.folders.where('parentId').equals(parentKey).toArray());
      const existing = siblings.find((f) => !f.trashedAt && String(f.name).toLowerCase() === name.toLowerCase());
      if (existing) {
        ctx.cache.set(cacheKey, { id: existing.id, clientId: existing.clientId });
        parentKey = normalizeFolderId(existing.id);
        parentClientId = existing.clientId;
        continue;
      }
      if ((await db.folders.count()) >= MAX_VAULT_FOLDERS) {
        throw new Error(FOLDER_LIMIT_SIGNAL);
      }
      const clientId = createClientId();
      const parentForEvent = parentClientId;
      const newId = await db.folders.add(await sealRecord('folder', {
        clientId,
        parentClientId: parentForEvent,
        parentId: parentKey,
        name,
        createdAt: Date.now()
      }));
      const key = getEncryptionKey();
      if (key) {
        try {
          await appendFolderCreate(authenticatedFetch, key, { clientId, parentClientId: parentForEvent, name });
        } catch (err) {
          console.error('Journal folder_create failed', err);
          ctx.journalFailed = true;
        }
      }
      ctx.cache.set(cacheKey, { id: newId, clientId });
      parentKey = normalizeFolderId(newId);
      parentClientId = clientId;
    }
    return parentKey;
  }, [getEncryptionKey]);

  // Upload a whole directory. The folder tree is built first and every file is bound to its
  // resolved folder id before anything is queued, so navigating elsewhere mid-upload cannot
  // divert files into the wrong folder.
  const enqueueFolderUpload = useCallback(async (entries) => {
    if (isWriteBlocked()) return;
    if (!entries.length) return;
    const twoGB = 2 * 1024 * 1024 * 1024;
    const tenGB = 10 * 1024 * 1024 * 1024;
    const limit = localStorage.getItem('tresor_allow_large') === 'on' ? tenGB : twoGB;
    const baseFolderId = currentFolderIdRef.current;
    const ctx = { cache: new Map(), journalFailed: false };
    const jobs = [];
    let rejected = 0;
    let limitHit = false;

    setIsPreparingFolderUpload(true);
    try {
      for (const { file, path } of entries) {
        if (file.size > limit) { rejected += 1; continue; }
        const segments = String(path || file.name).split('/').filter(Boolean);
        segments.pop(); // trailing piece is the file itself
        let folderId;
        try {
          folderId = await ensureFolderPath(segments, baseFolderId, ctx);
        } catch (err) {
          if (err?.message === FOLDER_LIMIT_SIGNAL) { limitHit = true; break; }
          throw err;
        }
        jobs.push({ kind: 'file', file, folderId, keepView: true });
      }
    } catch (err) {
      console.error('Ordner-Upload fehlgeschlagen', err);
      showNotification('Ordner konnte nicht vorbereitet werden.', 'error');
      setIsPreparingFolderUpload(false);
      return;
    }
    setIsPreparingFolderUpload(false);

    await loadData();

    if (jobs.length > 0) {
      uploadQueueRef.current.push(...jobs);
      showNotification(`${jobs.length} Datei(en) aus dem Ordner werden hochgeladen…`, 'info');
      drainUploadQueue();
    }
    if (limitHit) {
      showNotification(
        `Ordnerlimit erreicht (maximal ${MAX_VAULT_FOLDERS} Ordner im Tresor). ${jobs.length} Datei(en) werden hochgeladen, der Rest wurde übersprungen.`,
        'error'
      );
    }
    if (rejected > 0) {
      showNotification(
        limit === twoGB
          ? `${rejected} Datei(en) über 2 GB übersprungen. Für größere: Einstellungen → „Große Dateien (bis 10 GB)" aktivieren (Download braucht Chrome/Edge).`
          : `${rejected} Datei(en) über 10 GB übersprungen (Maximum pro Datei).`,
        'error'
      );
    }
    if (ctx.journalFailed) {
      showNotification('Ordner lokal erstellt, Cloud-Sync für die Struktur ist noch offen.', 'info');
    }
    if (!jobs.length && !limitHit && !rejected) {
      showNotification('Der Ordner enthält keine Dateien.', 'info');
    }
  }, [ensureFolderPath, drainUploadQueue, showNotification, loadData, isWriteBlocked]);

  async function resumePendingUploads() {
    if (!cloudStorageReady) return;
    const incomplete = await loadIncompletePendingUploads();
    if (incomplete.length === 0) return;
    for (const record of incomplete) {
      uploadQueueRef.current.push({ kind: 'resume', record, folderId: record.folderId ?? 'root' });
      updateUploadProgress(record.id, {
        fileName: record.fileName,
        percent: Math.round(((record.uploadedChunks || 0) / record.totalChunks) * 100),
        phase: 'uploading',
        chunkIndex: record.uploadedChunks || 0,
        totalChunks: record.totalChunks,
        bytesDone: Math.min((record.uploadedChunks || 0) * (record.chunkSize || 0), record.totalSize || 0),
        totalBytes: record.totalSize || 0
      });
    }
    showNotification(`${incomplete.length} unterbrochene Upload(s) werden fortgesetzt…`, 'info');
    drainUploadQueue();
  }

  // Cancel an active/queued/stuck upload: stop the running job, drop it from the queue,
  // abort the server session (releases quota + DELETES the already-uploaded chunks so no
  // orphan garbage is left), and remove the progress card.
  const cancelUpload = useCallback(async (progressId) => {
    cancelledUploadsRef.current.add(progressId);
    // Abort all in-flight chunk PUTs for this job at once (so nothing lands after the sweep).
    const ctrl = cancelControllersRef.current.get(progressId);
    if (ctrl) { try { ctrl.abort(); } catch (_) { /* already aborted */ } }
    uploadQueueRef.current = uploadQueueRef.current.filter((j) => (j.record?.id ?? null) !== progressId);
    if (!String(progressId).startsWith('local-')) {
      try {
        // For cloud uploads pendingId === uploadId, so both args are the same id.
        await abortPendingUpload(authenticatedFetch, progressId, progressId);
      } catch (err) {
        console.error('Cancel upload failed', err);
      }
    }
    clearTimeout(progressRemoveTimersRef.current[progressId]);
    setUploadProgress((prev) => {
      const next = { ...prev };
      delete next[progressId];
      return next;
    });
    try { await refreshQuota(); } catch (_) { /* non-fatal */ }
    cancelControllersRef.current.delete(progressId);
    setTimeout(() => cancelledUploadsRef.current.delete(progressId), 5000);
    showNotification('Upload abgebrochen.', 'info');
  }, [authenticatedFetch, refreshQuota, showNotification]);

  // ==========================================
  // UI COMPONENTS (Pricing & USPs)
  // ==========================================

  // --- Routing targets ---
  // Login is only for existing customers (Bestandskunden); buying happens via the
  // duplicated cloud checkout on datenputzer.de — NOT via the login.
  const goToCheckout = (plan, cycle) => {
    const params = new URLSearchParams({ plan, cycle });
    if (quotaInfo.email) params.set('email', quotaInfo.email);
    navigateTop(`/checkout?${params.toString()}`, { replace: false });
  };

  const handlePlanSelection = async (plan, cycle) => {
    const token = await ensureAccessToken();
    if (token) {
      try {
        const statusRes = await authenticatedFetch('/api/tresor/status');
        if (statusRes.ok) {
          const status = await statusRes.json();
          if (status.active) {
            applyTresorStatus(status);
            const cmp = comparePlans(plan, status.plan);
            if (cmp === 'same') {
              showNotification(`Sie haben bereits den Tarif ${status.planLabel || TIER_BY_ID[plan]?.size}.`, 'info');
              return;
            }
            if (cmp === 'downgrade') {
              showNotification('Ein Downgrade ist derzeit nicht möglich. Bitte kontaktieren Sie den Support.', 'info');
              return;
            }
            openBookingModal(plan, cycle, 'upgrade');
            return;
          }

          // Logged-in Datenputzer customer without Tresor (e.g. Schutz-Abo).
          openBookingModal(plan, cycle, 'subscribe');
          return;
        }
        if (statusRes.status === 401 || statusRes.status === 403) {
          showNotification('Sitzung abgelaufen. Bitte melden Sie sich erneut an.', 'error');
          redirectToDatenputzerLogin();
          return;
        }
      } catch (_) {
        showNotification('Tarif konnte nicht geprüft werden. Bitte versuchen Sie es erneut.', 'error');
        return;
      }
    }
    goToCheckout(plan, cycle);
  };
  const scrollToTarife = () => document.getElementById('tarife')?.scrollIntoView({ behavior: 'smooth' });

  const revokeCurrentTresorSession = async () => {
    try {
      const res = await authenticatedFetch('/api/tresor/sessions');
      if (!res.ok) return;
      const data = await res.json();
      const current = (data.sessions || []).find((s) => s.isCurrent);
      if (current?.sessionId) {
        await authenticatedFetch(`/api/tresor/sessions/${current.sessionId}`, { method: 'DELETE' });
      }
    } catch (_) { /* best-effort — frees device slot for 2-device limit */ }
  };

  const doLogout = async () => {
    if (hasActiveUploads()) {
      showNotification('Abmeldung bei laufenden Uploads nicht möglich. Bitte warten oder „Tresor sperren“ nutzen.', 'error');
      return;
    }
    backgroundKeyRef.current = null;
    setSessionKey(null);
    setIsUnlocked(false);
    setShowProfileMenu(false);
    setShowSettingsPanel(false);
    if (autoLockTimerRef.current) clearTimeout(autoLockTimerRef.current);
    await revokeCurrentTresorSession();
    clearTresorCustomerSession();
    await logoutSession();
  };
  const isLoggedIn = () => appState !== 'landing' || quotaInfo.tresorActive || hadTresorCustomerSession();

  const quotaPercent = quotaInfo.limit > 0
    ? Math.min(100, Math.round((quotaInfo.used / quotaInfo.limit) * 100))
    : 0;

  const renderNotificationToast = () => {
    if (!notification) return null;
    const styles = {
      success: 'bg-emerald-700/95 border-emerald-400/50',
      error: 'bg-red-700/95 border-red-400/50',
      info: 'bg-slate-800/95 border-slate-500/50',
    };
    return (
      <div
        role="status"
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] max-w-lg w-[calc(100%-2rem)] px-4 py-3 rounded-xl border text-white text-sm shadow-2xl backdrop-blur-md ${styles[notification.type] || styles.info}`}
      >
        {notification.text}
      </div>
    );
  };

  const ENCRYPTION_GUIDE_STEPS = [
    {
      title: 'So wird Ihre Datei geschützt',
      icon: KeyRound,
      body: <EncryptionFlow />
    },
    {
      title: 'Nur Sie können Ihre Dateien öffnen',
      icon: ShieldCheck,
      body: (
        <>
          <p className="mb-3 leading-relaxed">
            Stellen Sie sich den Tresor wie einen Safe vor, dessen Schlüssel <strong>nur auf Ihrem Gerät</strong> entsteht.
            Wir speichern Ihre Dateien, aber ohne Ihr Passwort sind sie für uns nur unlesbarer Datensalat.
            Das nennt man <strong>Zero-Knowledge</strong>: wir haben physisch keinen Zugriff auf Ihre Inhalte.
          </p>
          <p className="text-sm vault-text-muted leading-relaxed">
            Selbst bei einem Server-Hack oder einer behördlichen Anfrage können wir keine Datei für Sie öffnen.
          </p>
        </>
      )
    },
    {
      title: 'Ihr Tresor-Passwort',
      icon: KeyRound,
      body: (
        <>
          <p className="mb-3 leading-relaxed">
            Ihr Passwort wird <strong>nie</strong> an unsere Server gesendet und <strong>nie</strong> gespeichert.
            Daraus berechnet Ihr Gerät einen Schlüssel (PBKDF2 mit 600.000 Runden), der Ihren eigentlichen Tresorschlüssel aufschließt.
          </p>
          <p className="mb-3 leading-relaxed">
            Vergessen Sie das Passwort, gibt es keinen „Passwort vergessen“-Link, das wäre ein Sicherheitsloch.
            Deshalb brauchen Sie den Notfall-Schlüssel aus dem nächsten Schritt.
          </p>
          <p className="text-sm vault-text-muted leading-relaxed">
            Tipp: Nutzen Sie ein langes, einzigartiges Passwort (mindestens 12 Zeichen) und teilen Sie es mit niemandem.
          </p>
        </>
      )
    },
    {
      title: 'Ihre 12-Wörter Recovery-Phrase',
      icon: ShieldAlert,
      body: (
        <>
          <p className="mb-3 leading-relaxed">
            Bei der Einrichtung haben Sie einen <strong>Notfall-Schlüssel aus 12 Wörtern</strong> erhalten.
            Er ist Ihr Backup, falls Sie das Passwort verlieren oder ein neues setzen wollen.
          </p>
          <p className="mb-3 leading-relaxed">
            Schreiben Sie die Wörter auf Papier und bewahren Sie sie sicher auf, <strong>nicht</strong> als Screenshot,
            nicht in E-Mails, nicht in Cloud-Notizen.
          </p>
          <p className="text-sm vault-text-danger leading-relaxed">
            Verlieren Sie Passwort <em>und</em> Recovery-Phrase, sind Ihre Daten unwiderruflich verloren.
            Das ist der Preis echter Privatsphäre.
          </p>
        </>
      )
    },
    {
      title: 'So läuft ein Upload ab',
      icon: UploadCloud,
      body: (
        <>
          <ol className="space-y-3 text-sm leading-relaxed list-decimal list-inside">
            <li>Sie wählen eine Datei, sie bleibt zunächst nur auf Ihrem Gerät.</li>
            <li>Der Tresor teilt sie in Stücke und verschlüsselt jedes Stück mit <strong>AES-GCM</strong> (ein weltweit verbreitetes Standardverfahren).</li>
            <li>Erst die verschlüsselten Stücke werden übertragen, niemals die Klartext-Datei.</li>
            <li>Beim Herunterladen wird alles wieder auf Ihrem Gerät entschlüsselt.</li>
          </ol>
        </>
      )
    },
    {
      title: 'Warum das so sicher ist',
      icon: Lock,
      body: (
        <>
          <p className="mb-3 leading-relaxed">
            Bei Cloudspeichern, die den Schlüssel selbst verwalten, kann der Anbieter Ihre Dateien technisch lesen.
            Beim Tresor liegt der Schlüssel nur bei Ihnen.
          </p>
          <p className="mb-3 leading-relaxed">
            Beim Datenputzer-Tresor existiert der Schlüssel nur in Ihrem Kopf (Passwort + Recovery-Phrase)
            und kurz im Arbeitsspeicher Ihres Browsers, während der Tresor entsperrt ist.
          </p>
          <p className="text-sm vault-text-muted leading-relaxed">
            Jede Datei hat einen eigenen Zufallswert. Selbst zwei identische Dateien sehen nach der Verschlüsselung völlig unterschiedlich aus.
          </p>
        </>
      )
    }
  ];

  const renderPasskeyInfoModal = () => {
    if (!passkeyInfoOpen) return null;
    return (
      <div className="fixed inset-0 z-[98] flex items-center justify-center p-4" role="presentation" onClick={() => setPasskeyInfoOpen(false)}>
        <div className="absolute inset-0" style={{ background: 'var(--vault-overlay)' }} />
        <div className="relative w-full max-w-lg vault-panel rounded-2xl p-6 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="passkey-info-title" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between gap-3 mb-4">
            <h3 id="passkey-info-title" className="text-lg font-semibold vault-text flex items-center gap-2"><KeyRound className="w-5 h-5 vault-text-accent" /> Was ein Passkey bringt</h3>
            <button type="button" className="vault-btn-ghost mr-test-btn p-2" aria-label="Schließen" onClick={() => setPasskeyInfoOpen(false)}><X className="w-4 h-4" /></button>
          </div>
          <div className="space-y-3 text-sm vault-text leading-relaxed">
            <p><strong>Was beim Entsperren passiert:</strong> Ihr Gerät fragt nach Fingerabdruck, Gesicht oder PIN. Danach erzeugt es aus dem Passkey ein Geheimnis, das das Gerät nie verlässt, und Ihr Browser öffnet damit eine eigene Hülle Ihres Tresorschlüssels. Ein Passwort wird dabei nicht getippt.</p>
            <p><strong>Schutz vor Mitlesen beim Tippen:</strong> Was nicht getippt wird, kann kein Keylogger und niemand über die Schulter mitlesen.</p>
            <p><strong>Schutz vor gefälschten Seiten:</strong> Ein Passkey funktioniert nur auf der echten Adresse tresor.datenputzer.de. Eine nachgebaute Seite bekommt nichts, auch wenn sie täuschend echt aussieht.</p>
            <p><strong>Nicht zu erraten:</strong> Die Hülle, die der Passkey öffnet, ist mit 256 zufälligen Bits gesichert. Anders als bei einem Passwort lässt sie sich nicht durchprobieren.</p>
            <p><strong>Was er nicht ersetzt:</strong> Passwort und 12 Wörter bleiben. Verlieren Sie das Gerät, entsperren Sie wie gewohnt und entfernen den Passkey in den Einstellungen.</p>
            <p className="vault-text-muted"><strong>Die ehrliche Grenze:</strong> Schadsoftware, die Ihren Rechner kontrolliert, sieht auch nach dem Entsperren per Passkey, was der geöffnete Tresor anzeigt. Sperren Sie den Tresor, wenn Sie fertig sind.</p>
          </div>
        </div>
      </div>
    );
  };

  const renderEncryptionGuideModal = () => {
    if (!showEncryptionGuide) return null;
    const step = ENCRYPTION_GUIDE_STEPS[encryptionGuideStep];
    const StepIcon = step.icon;
    const isLast = encryptionGuideStep === ENCRYPTION_GUIDE_STEPS.length - 1;

    return (
      <div
        className="fixed inset-0 z-[97] flex items-center justify-center p-4"
        style={{ background: 'var(--vault-overlay)' }}
        onClick={() => { setShowEncryptionGuide(false); setEncryptionGuideStep(0); }}
      >
        <div
          className="w-full max-w-lg vault-panel rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 vault-text-accent" />
              <h3 className="text-lg font-semibold vault-text">So funktioniert die Verschlüsselung</h3>
            </div>
            <button
              type="button"
              onClick={() => { setShowEncryptionGuide(false); setEncryptionGuideStep(0); }}
              className="vault-btn-ghost px-2 py-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex gap-1 mb-5">
            {ENCRYPTION_GUIDE_STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full ${encryptionGuideStep >= i ? 'bg-dp-green' : 'bg-white/10'}`}
              />
            ))}
          </div>

          <p className="text-xs vault-text-subtle mb-4">
            Schritt {encryptionGuideStep + 1} von {ENCRYPTION_GUIDE_STEPS.length}
          </p>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center vault-glass border" style={{ borderColor: 'var(--vault-accent)' }}>
              <StepIcon className="w-6 h-6 vault-text-accent" />
            </div>
            <h4 className="text-xl font-semibold vault-text">{step.title}</h4>
          </div>

          <div className="vault-text text-sm mb-6">{step.body}</div>

          <div className="flex gap-3">
            {encryptionGuideStep > 0 && (
              <button
                type="button"
                onClick={() => setEncryptionGuideStep((s) => s - 1)}
                className="vault-btn-ghost flex-1"
              >
                Zurück
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (isLast) {
                  setShowEncryptionGuide(false);
                  setEncryptionGuideStep(0);
                } else {
                  setEncryptionGuideStep((s) => s + 1);
                }
              }}
              className="vault-btn-primary flex-1"
            >
              {isLast ? 'Verstanden' : 'Weiter'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderFolderModal = () => {
    if (!showFolderModal) return null;
    return (
      <div
        className="fixed inset-0 z-[90] flex items-center justify-center p-4"
        style={{ background: 'var(--vault-overlay)' }}
        onClick={() => { setShowFolderModal(false); setNewFolderName(''); }}
      >
        <form
          onSubmit={submitNewFolder}
          className="w-full max-w-md vault-panel rounded-2xl p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-semibold vault-text mb-1">Neuer Ordner</h3>
          <p className="text-sm vault-text-muted mb-4">
            Ordner werden lokal in Ihrem Tresor gespeichert ({totalFolderCount}/{MAX_VAULT_FOLDERS}).
          </p>
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Ordnername"
            maxLength={MAX_FOLDER_NAME_LENGTH}
            className="vault-input vault-input-plain mb-1"
            autoFocus
          />
          <p className="text-xs vault-text-subtle mb-4">
            Max. {MAX_FOLDER_NAME_LENGTH} Zeichen · keine Sonderzeichen \ / : * ? &quot; &lt; &gt; |
          </p>
          <div className="flex gap-3">
            <button type="button" onClick={() => { setShowFolderModal(false); setNewFolderName(''); }} className="vault-btn-ghost flex-1">
              Abbrechen
            </button>
            <button type="submit" className="vault-btn-primary flex-1">
              Erstellen
            </button>
          </div>
        </form>
      </div>
    );
  };

  const renderRenameFolderModal = () => {
    if (!renameFolderTarget) return null;
    return (
      <div
        className="fixed inset-0 z-[91] flex items-center justify-center p-4"
        style={{ background: 'var(--vault-overlay)' }}
        onClick={() => { setRenameFolderTarget(null); setRenameFolderName(''); }}
      >
        <form
          onSubmit={submitRenameFolder}
          className="w-full max-w-md vault-panel rounded-2xl p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-semibold vault-text mb-1">Ordner umbenennen</h3>
          <p className="text-sm vault-text-muted mb-4">Aktuell: „{renameFolderTarget.name}"</p>
          <input
            type="text"
            value={renameFolderName}
            onChange={(e) => setRenameFolderName(e.target.value)}
            placeholder="Neuer Ordnername"
            maxLength={MAX_FOLDER_NAME_LENGTH}
            className="vault-input vault-input-plain mb-4"
            autoFocus
          />
          <div className="flex gap-3">
            <button type="button" onClick={() => { setRenameFolderTarget(null); setRenameFolderName(''); }} className="vault-btn-ghost flex-1">
              Abbrechen
            </button>
            <button type="submit" className="vault-btn-primary flex-1">
              Speichern
            </button>
          </div>
        </form>
      </div>
    );
  };

  const renderRenameFileModal = () => {
    if (!renameFileTarget) return null;
    const close = () => { setRenameFileTarget(null); setRenameFileName(''); };
    return (
      <div className="fixed inset-0 z-[91] flex items-center justify-center p-4" style={{ background: 'var(--vault-overlay)' }} onClick={close}>
        <form onSubmit={submitRenameFile} className="w-full max-w-md vault-panel rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
          <h3 className="text-lg font-semibold vault-text mb-1">Datei umbenennen</h3>
          <p className="text-sm vault-text-muted mb-4 break-all">Aktuell: „{renameFileTarget.name}"</p>
          <input
            type="text"
            value={renameFileName}
            onChange={(e) => setRenameFileName(e.target.value)}
            onFocus={(e) => {
              // Nur den Namen vor der Endung markieren, wie im Explorer.
              const v = e.target.value;
              const dot = v.lastIndexOf('.');
              e.target.setSelectionRange(0, dot > 0 ? dot : v.length);
            }}
            placeholder="Neuer Dateiname"
            maxLength={MAX_FILE_NAME_LENGTH}
            className="vault-input vault-input-plain mb-4"
            aria-label="Neuer Dateiname"
            autoFocus
          />
          <div className="flex gap-3">
            <button type="button" onClick={close} className="vault-btn-ghost flex-1">Abbrechen</button>
            <button type="submit" className="vault-btn-primary flex-1">Speichern</button>
          </div>
        </form>
      </div>
    );
  };

  const renderMoveModal = () => {
    if (!moveTarget) return null;
    const { kind, item } = moveTarget;
    const live = allFoldersCache.filter((f) => !f.trashedAt);
    const blocked = new Set();
    if (kind === 'folder') for (const id of collectFolderDeleteOrder(item.id, live)) blocked.add(String(id));
    const currentParent = normalizeFolderId(kind === 'folder' ? item.parentId : item.folderId);
    const byParent = new Map();
    for (const f of live) {
      const pk = normalizeFolderId(f.parentId);
      if (!byParent.has(pk)) byParent.set(pk, []);
      byParent.get(pk).push(f);
    }
    for (const list of byParent.values()) list.sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }));
    const rows = [{ id: 'root', name: 'Mein Tresor', depth: 0 }];
    const walk = (pk, depth) => {
      for (const f of byParent.get(pk) || []) {
        rows.push({ id: String(f.id), name: f.name, depth });
        if (!blocked.has(String(f.id))) walk(String(f.id), depth + 1);
      }
    };
    walk('root', 1);
    const close = () => { if (!moveBusy) { setMoveTarget(null); setMoveDest(null); } };
    return (
      <div className="fixed inset-0 z-[91] flex items-center justify-center p-4" style={{ background: 'var(--vault-overlay)' }} onClick={close}>
        <div className="w-full max-w-md vault-panel rounded-2xl p-6 flex flex-col" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Verschieben nach">
          <h3 className="text-lg font-semibold vault-text mb-1">Verschieben nach</h3>
          <p className="text-sm vault-text-muted mb-4 break-all">{kind === 'folder' ? 'Ordner' : 'Datei'} „{item.name}"</p>
          <div className="vault-move-tree mb-4" role="listbox" aria-label="Zielordner">
            {rows.map((r) => {
              const isSelf = blocked.has(r.id);
              const isHere = r.id === currentParent;
              const disabled = isSelf || isHere;
              const selected = moveDest !== null && String(moveDest) === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  disabled={disabled}
                  onClick={() => setMoveDest(r.id)}
                  className={`vault-move-row ${selected ? 'is-selected' : ''}`}
                  style={{ paddingLeft: `${0.75 + r.depth * 1.1}rem` }}
                >
                  {r.id === 'root' ? <HardDrive className="w-4 h-4 shrink-0" /> : <Folder className="w-4 h-4 shrink-0 vault-text-accent" fill="currentColor" fillOpacity={0.2} />}
                  <span className="truncate">{r.name}</span>
                  {isHere && <span className="vault-move-note">liegt hier</span>}
                </button>
              );
            })}
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={close} className="vault-btn-ghost flex-1" disabled={moveBusy}>Abbrechen</button>
            <button type="button" onClick={submitMove} className="vault-btn-primary flex-1" disabled={moveDest === null || moveBusy}>
              {moveBusy ? 'Verschiebe…' : 'Hierher verschieben'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderContextMenu = () => {
    if (!ctxMenu) return null;
    const { kind, item, sheet } = ctxMenu;
    const close = () => setCtxMenu(null);
    const act = (fn) => () => { close(); fn(); };
    const entries = kind === 'file'
      ? [
          { label: 'Herunterladen', icon: Download, run: act(() => downloadFile(item)) },
          { label: 'Umbenennen', icon: Pencil, run: () => startRenameFile(item) },
          { label: 'Verschieben nach…', icon: FolderInput, run: () => startMove('file', item) },
          { label: 'In den Papierkorb', icon: Trash2, danger: true, run: act(() => requestDeleteFile(item)) }
        ]
      : [
          { label: 'Öffnen', icon: FolderOpen, run: act(() => openFolder(item)) },
          { label: 'Umbenennen', icon: Pencil, run: () => { close(); startRenameFolder(item); } },
          { label: 'Verschieben nach…', icon: FolderInput, run: () => startMove('folder', item) },
          { label: 'In den Papierkorb', icon: Trash2, danger: true, run: act(() => requestDeleteFolder(item)) }
        ];
    return (
      <div
        className={`fixed inset-0 z-[92] ${sheet ? 'vault-ctx-backdrop' : ''}`}
        onClick={close}
        onContextMenu={(e) => { e.preventDefault(); close(); }}
        ref={(el) => {
          // Desktop: am Mauszeiger, innerhalb des sichtbaren Bereichs (im Tab ist das Overlay nur
          // der sichtbare Ausschnitt, die Koordinaten kommen aber relativ zum ganzen Rahmen).
          if (!el || sheet) return;
          const menu = el.firstElementChild;
          if (!menu) return;
          const box = el.getBoundingClientRect();
          const w = menu.offsetWidth;
          const h = menu.offsetHeight;
          let left = ctxMenu.x - box.left;
          let top = ctxMenu.y - box.top;
          if (left + w > box.width - 8) left = Math.max(8, box.width - w - 8);
          if (top + h > box.height - 8) top = Math.max(8, top - h);
          menu.style.left = `${left}px`;
          menu.style.top = `${top}px`;
          menu.style.visibility = 'visible';
        }}
      >
        <div
          className={sheet ? 'vault-ctx-sheet' : 'vault-menu vault-ctx-menu'}
          role="menu"
          aria-label={`Aktionen für ${item.name}`}
          onClick={(e) => e.stopPropagation()}
        >
          {sheet && <div className="vault-ctx-title truncate">{item.name}</div>}
          {entries.map((en) => (
            <button key={en.label} type="button" role="menuitem" className={`vault-menu-item w-full text-left ${en.danger ? 'vault-text-danger' : ''}`} onClick={en.run}>
              <en.icon className="w-4 h-4 shrink-0" />
              {en.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderDeleteConfirmModal = () => {
    if (!pendingDelete) return null;
    const isFile = pendingDelete.type === 'file';
    const title = isFile ? 'Datei in den Papierkorb?' : 'Ordner in den Papierkorb?';
    let body;
    if (isFile) {
      body = <>„{pendingDelete.item.name}" wird in den Papierkorb verschoben und ist dort 14 Tage wiederherstellbar (zählt bis dahin weiter zu Ihrem Speicher).</>;
    } else {
      const { fileCount, subfolderCount } = pendingDelete.stats;
      body = (
        <>
          Ordner „{pendingDelete.item.name}" und alle Inhalte werden in den Papierkorb verschoben
          {subfolderCount > 0 ? ` (${subfolderCount} Unterordner` : ''}
          {fileCount > 0 ? `${subfolderCount > 0 ? ', ' : ' ('}${fileCount} Datei${fileCount === 1 ? '' : 'en'}` : ''}
          {(subfolderCount > 0 || fileCount > 0) ? ').' : '.'}
          {' '}Wiederherstellbar für 14 Tage; der Speicher wird erst nach endgültiger Löschung frei.
        </>
      );
    }
    return (
      <div
        className="fixed inset-0 z-[92] flex items-center justify-center p-4"
        style={{ background: 'var(--vault-overlay)' }}
        onClick={() => !deleteInProgress && setPendingDelete(null)}
      >
        <div
          className="w-full max-w-md vault-panel rounded-2xl p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-semibold vault-text mb-2">{title}</h3>
          <p className="text-sm vault-text-muted mb-6">{body}</p>
          <div className="flex gap-3">
            <button
              type="button"
              disabled={deleteInProgress}
              onClick={() => setPendingDelete(null)}
              className="vault-btn-ghost flex-1"
            >
              Abbrechen
            </button>
            <button
              type="button"
              disabled={deleteInProgress}
              onClick={confirmPendingDelete}
              className="vault-btn-primary flex-1"
              style={{ background: 'var(--vault-danger, #dc2626)' }}
            >
              {deleteInProgress ? 'Wird verschoben…' : 'In den Papierkorb'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderBackgroundUploads = () => {
    if (!lockKeepsUploads || (!isEncrypting && Object.keys(uploadProgress).length === 0)) return null;
    return (
      <div className="mb-6 p-4 rounded-xl vault-upload-card text-left">
        <div className="flex items-center gap-2 vault-text-accent font-medium text-sm mb-2">
          <UploadCloud className="w-4 h-4 animate-pulse" />
          Uploads laufen im Hintergrund
        </div>
        <p className="text-xs vault-text-muted mb-3 leading-relaxed">
          Der Tresor ist gesperrt. Dateien sind nicht sichtbar und neue Uploads sind pausiert.
          Bereits gestartete Uploads werden weiter verschlüsselt und übertragen, solange dieser Tab offen bleibt.
        </p>
        {Object.entries(uploadProgress).map(([id, entry]) => (
          <div key={id} className="mb-3 last:mb-0">
            {showUploadDetail && renderUploadEncryptionLive(entry)}
            <div className="flex justify-between text-xs vault-text-muted mb-1 gap-2">
              <span className="truncate">{entry.fileName} · {UPLOAD_PHASE_LABELS[entry.phase] || 'Aktiv'}</span>
              <span className="font-mono vault-text-accent shrink-0">{entry.percent}%</span>
            </div>
            {showUploadDetail && entry.totalChunks > 0 && (
              <div className="text-[10px] vault-text-subtle font-mono mb-1">
                Chunk {entry.chunkIndex || 0}/{entry.totalChunks}
              </div>
            )}
            <div className="h-1 w-full vault-progress-track">
              <div className={`vault-upload-bar-fill vault-progress-fill ${entry.phase === 'encrypting' ? 'is-encrypting' : ''}`} style={{ width: `${entry.percent}%` }} />
            </div>
          </div>
        ))}
        {isEncrypting && Object.keys(uploadProgress).length === 0 && (
          <p className="text-xs vault-text-subtle">Verschlüsselung wird vorbereitet…</p>
        )}
      </div>
    );
  };

  const setThemeAndPersist = (pref) => {
    setThemePreference(pref);
    persistTheme(pref);
  };

  const openPasswordResetFromSettings = () => {
    setShowProfileMenu(false);
    setShowSettingsPanel(false);
    handleLock();
    setLoginMode('reset-password');
    setResetStep('mnemonic');
    setErrorMsg('');
  };



  const renderCapsLockWarning = (forField) => {
    if (!capsLockOn) return null;
    if (forField && capsField !== forField) return null;
    return (
      <div className="vault-capslock-pop" role="alert" aria-live="assertive">
        <AlertTriangle className="w-5 h-5 shrink-0" aria-hidden="true" />
        <span><strong>Feststelltaste ist an.</strong> Sie tippen gerade in Großbuchstaben.</span>
      </div>
    );
  };

  const renderSecuritySelect = ({ label, icon: Icon, value, onChange, options, hint }) => (
    <label className="block mb-3">
      <span className="text-xs vault-text-subtle block mb-1">{label}</span>
      <div className="vault-select-wrap">
        <Icon className="vault-select-icon-left w-4 h-4 vault-text-subtle" aria-hidden="true" />
        <select className="vault-select w-full text-sm" value={value} onChange={onChange}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <ChevronDown className="vault-select-icon-right w-4 h-4 vault-text-subtle" aria-hidden="true" />
      </div>
      {hint ? <span className="text-[10px] vault-text-subtle mt-1 block">{hint}</span> : null}
    </label>
  );

  const renderThemePicker = (compact = false) => (
    <div className={`flex gap-2 ${compact ? '' : 'mt-2'}`}>
      {[
        { id: 'auto', label: 'Auto', icon: Monitor },
        { id: 'light', label: 'Hell', icon: Sun },
        { id: 'dark', label: 'Dunkel', icon: Moon },
      ].map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          type="button"
          onClick={() => setThemeAndPersist(id)}
          className={`vault-theme-option ${themePreference === id ? 'is-active' : ''}`}
        >
          <Icon className="w-4 h-4" />
          {label}
        </button>
      ))}
    </div>
  );

  const renderProfileMenu = () => {
    if (!showProfileMenu) return null;
    return (
      <div className="vault-menu vault-menu-dropdown">
        {quotaInfo.email && (
          <div className="px-3 pt-2 pb-1 text-xs vault-text-muted truncate" title={quotaInfo.email}>
            {quotaInfo.email}
          </div>
        )}
        <div className="vault-menu-section">Darstellung</div>
        <div className="px-2 pb-2">{renderThemePicker(true)}</div>
        <div className="vault-menu-divider" />
        <button type="button" className="vault-menu-item" onClick={() => { setShowProfileMenu(false); setShowSettingsPanel(true); loadDeviceSessions(); }}>
          <Settings className="w-4 h-4 vault-text-muted" />
          Einstellungen
        </button>
        <button type="button" className="vault-menu-item" onClick={() => { setShowProfileMenu(false); setShowMaschinenraum(true); }}>
          <Wrench className="w-4 h-4 vault-text-muted" />
          Maschinenraum
        </button>
        <button type="button" className="vault-menu-item" onClick={() => { setShowProfileMenu(false); handleLock(); }}>
          <Lock className="w-4 h-4 vault-text-muted" />
          Tresor sperren
        </button>
        <a href="https://datenputzer.de/dashboard" className="vault-menu-item vault-embed-hide">
          <ShieldCheck className="w-4 h-4 vault-text-accent" />
          Datenputzer öffnen
        </a>
        <div className="vault-menu-divider vault-embed-hide" />
        <button type="button" className="vault-menu-item vault-text-danger vault-embed-hide" onClick={() => { setShowProfileMenu(false); doLogout(); }}>
          <LogOut className="w-4 h-4" />
          Abmelden
        </button>
      </div>
    );
  };

  const renderSettingsPanel = () => {
    if (!showSettingsPanel) return null;
    return (
      <div
        className="vault-settings-overlay fixed inset-0 z-[95] p-4 sm:p-6"
        style={{ background: 'var(--vault-overlay)' }}
        onClick={() => setShowSettingsPanel(false)}
      >
        <div className="vault-settings-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Einstellungen">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold vault-text">Einstellungen</h3>
            <button type="button" onClick={() => setShowSettingsPanel(false)} className="vault-btn-ghost px-2 py-1">
              <X className="w-5 h-5" />
            </button>
          </div>

          <section className="mb-6">
            <h4 className="text-sm font-semibold vault-text mb-1">Darstellung</h4>
            <p className="text-xs vault-text-subtle mb-2">Automatisch folgt dem System, oder manuell überschreiben.</p>
            {renderThemePicker()}
          </section>

          <section className="mb-6">
            <h4 className="text-sm font-semibold vault-text mb-1">Uploads</h4>
            <p className="text-xs vault-text-subtle mb-3">
              Ein: Verschlüsselungs-Animation, Chunk-Zähler und Byte-Fortschritt. Aus: kompakte Anzeige nur mit Prozent und Phase.
            </p>
            <label className="flex items-center justify-between gap-3 p-3 rounded-lg vault-glass cursor-pointer">
              <span className="text-sm vault-text">Chunk- und Byte-Details</span>
              <input
                type="checkbox"
                checked={showUploadDetail}
                onChange={(e) => setUploadDetailPreference(e.target.checked)}
                className="w-4 h-4 accent-[var(--vault-accent)]"
              />
            </label>
            {renderUploadDetailPreview()}

            <label className="flex items-center justify-between gap-3 p-3 mt-3 rounded-lg vault-glass cursor-pointer">
              <span className="text-sm vault-text">Große Dateien bis 10 GB erlauben</span>
              <input
                type="checkbox"
                checked={allowLargeFiles}
                onChange={(e) => setAllowLargeFilesPreference(e.target.checked)}
                className="w-4 h-4 accent-[var(--vault-accent)]"
              />
            </label>
            <p className="text-xs vault-text-subtle mt-2">
              Standard sind maximal 2 GB pro Datei. Aktiviert erlaubt bis zu 10 GB auf eigene Gefahr.
              Der <strong className="vault-text">Download von Dateien über 2 GB funktioniert nur in Chrome oder Edge</strong>
              {' '}(Streaming direkt auf die Festplatte). Sehr große Uploads lassen sich nach einer Unterbrechung nicht fortsetzen.
            </p>
          </section>

          <section className="mb-6">
            <h4 className="text-sm font-semibold vault-text mb-1">Für wen der Tresor gedacht ist</h4>
            <p className="text-xs vault-text-subtle">
              Der Tresor richtet sich an Privatkunden. Für individuelle Lösungen schreiben Sie an{' '}
              <a href="mailto:mail@datenputzer.de" className="vault-text-accent underline">mail@datenputzer.de</a>.
            </p>
          </section>

          <section className="mb-6">
            <h4 className="text-sm font-semibold vault-text mb-1">Aktive Geräte</h4>
            <p className="text-xs vault-text-subtle mb-3">
              Maximal 4 Geräte gleichzeitig. Entfernen Sie alte Sitzungen, wenn Sie ein neues Gerät nutzen wollen.
            </p>
            <button
              type="button"
              className="vault-btn-ghost w-full text-sm mb-3"
              onClick={loadDeviceSessions}
              disabled={sessionsLoading}
            >
              {sessionsLoading ? 'Lade…' : 'Geräte aktualisieren'}
            </button>
            <div className="space-y-2">
              {deviceSessions.length === 0 && (
                <p className="text-xs vault-text-subtle">Keine Sitzungen geladen.</p>
              )}
              {deviceSessions.map((s) => (
                <div key={s.sessionId} className="flex items-center justify-between gap-2 p-3 rounded-lg vault-glass text-sm">
                  <div className="min-w-0">
                    <div className="vault-text truncate">
                      {s.isCurrent ? 'Dieses Gerät' : `Gerät ${s.sessionId.slice(0, 8)}…`}
                    </div>
                    <div className="text-[11px] vault-text-subtle">
                      Zuletzt aktiv: {s.lastSeen ? new Date(s.lastSeen).toLocaleString('de-DE') : 'unbekannt'}
                    </div>
                  </div>
                  {!s.isCurrent && (
                    <button
                      type="button"
                      className="vault-btn-ghost text-xs shrink-0"
                      onClick={() => revokeDeviceSession(s.sessionId)}
                    >
                      Abmelden
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="mb-6">
            <h4 className="text-sm font-semibold vault-text mb-1">Sicherheit</h4>
            <p className="text-xs vault-text-subtle mb-3">
              Auto-Sperre, Verhalten beim Sperren während Uploads und optionale Tab-Sperre reduzieren die Zeit,
              in der Ihr Schlüssel im RAM liegt.
            </p>
            <p className="text-xs vault-text-subtle mb-3">
              Ihr Tresor-Passwort ändern Sie mit Ihren 12 Wörtern: Tresor sperren, dann „Passwort mit
              12-Wörter-Recovery-Phrase zurücksetzen“. Ohne die Wörter geht es bewusst nicht, sonst
              könnte jede gestohlene Anmeldung Ihr Passwort austauschen.
            </p>

            {renderSecuritySelect({
              label: 'Automatisch sperren nach',
              icon: Timer,
              value: autoLockMinutes,
              onChange: (e) => {
                const v = parseInt(e.target.value, 10);
                setAutoLockMinutes(v);
                writeAutoLockMinutes(v);
              },
              options: AUTO_LOCK_OPTIONS,
            })}

            {renderSecuritySelect({
              label: 'Sperren während Uploads',
              icon: UploadCloud,
              value: lockUploadPolicy,
              onChange: (e) => {
                setLockUploadPolicy(e.target.value);
                writeLockUploadPolicy(e.target.value);
              },
              options: LOCK_UPLOAD_POLICIES,
              hint: 'Streng: Schlüssel sofort löschen, Upload pausieren. Beenden: laufende Chunks fertig verschlüsseln.',
            })}

            <label className="flex items-center justify-between gap-3 p-3 rounded-lg vault-glass cursor-pointer mb-3">
              <span className="text-sm vault-text flex items-center gap-2 min-w-0">
                <PanelTop className="w-4 h-4 vault-text-subtle shrink-0" aria-hidden="true" />
                Sperren wenn Tab im Hintergrund
              </span>
              <input
                type="checkbox"
                checked={lockOnTabHidden}
                onChange={(e) => {
                  setLockOnTabHidden(e.target.checked);
                  writeLockOnTabHidden(e.target.checked);
                }}
                className="w-4 h-4 accent-[var(--vault-accent)]"
              />
            </label>


            <button type="button" className="vault-btn-ghost w-full mb-1" onClick={openPasswordResetFromSettings}>
              <KeyRound className="w-4 h-4" />
              Passwort mit Recovery-Phrase ändern
            </button>
            {/* Es gibt bewusst keinen Weg, das Passwort nur mit dem alten Passwort zu wechseln:
                die Huelle laesst sich nur umschreiben, wer den Nachweis ueber die 12 Woerter
                fuehrt. Das ist eine Staerke (eine gestohlene Sitzung kann es nicht), aber wer
                es nicht weiss, sucht vergeblich und sperrt sich beim Klick selbst aus. */}
            <p className="text-[11px] vault-text-subtle mb-3 leading-relaxed">
              Nur mit Ihren 12 Wörtern. Einen Wechsel allein mit dem alten Passwort gibt es
              nicht, auch nicht über den Support. Halten Sie den Zettel bereit, bevor Sie klicken:
              der Tresor wird dabei gesperrt.
            </p>
            <button type="button" className="vault-btn-ghost vault-btn-lock w-full" onClick={() => { setShowSettingsPanel(false); handleLock(); }}>
              <Lock className="w-4 h-4" />
              Tresor sperren
            </button>
          </section>

          <section className="mb-6">
            <h4 className="text-sm font-semibold vault-text mb-1">Passkey</h4>
            <p className="text-xs vault-text-subtle mb-3">
              Entsperren per Fingerabdruck, Gesichtserkennung oder Geräte-PIN, zusätzlich zu Passwort und 12 Wörtern.{' '}
              <button type="button" className="vault-text-accent underline" onClick={() => setPasskeyInfoOpen(true)}>Was bringt das?</button>
            </p>
            {!passkeyOk && (
              <p className="text-xs vault-text-subtle mb-3">Dieser Browser unterstützt Passkeys zum Entsperren nicht.</p>
            )}
            {passkeys.length > 0 && (
              <ul className="mb-3 space-y-2">
                {passkeys.map((pk) => (
                  <li key={pk.id} className="flex items-center justify-between gap-3 text-sm vault-text">
                    <span className="min-w-0">
                      <span className="block truncate">{pk.label || 'Passkey'}</span>
                      <span className="block text-xs vault-text-subtle">{pk.last_used_at ? `Zuletzt benutzt ${formatGermanDate(pk.last_used_at)}` : 'Noch nicht benutzt'}</span>
                    </span>
                    <button type="button" className="vault-btn-ghost text-xs px-3 py-1.5 mr-test-btn" onClick={() => handleRemovePasskey(pk)}>Entfernen</button>
                  </li>
                ))}
              </ul>
            )}
            {passkeyOk && !passkeyAddOpen && (
              <button type="button" className="vault-btn-secondary w-full text-sm" onClick={() => { setPasskeyAddOpen(true); loadPasskeys(); }}>
                <KeyRound className="w-4 h-4" />
                Passkey hinzufügen
              </button>
            )}
            {passkeyAddOpen && (
              <form onSubmit={handleAddPasskey} className="space-y-2">
                <p className="text-xs vault-text-subtle">Bitte bestätigen Sie mit Ihrem Tresor-Passwort. Danach fragt Ihr Gerät nach Fingerabdruck, Gesicht oder PIN.</p>
                <input
                  type="password"
                  value={passkeyPw}
                  onChange={(e) => setPasskeyPw(e.target.value)}
                  placeholder="Tresor-Passwort bestätigen"
                  className="vault-input"
                  autoComplete="current-password"
                  autoFocus
                  {...passwordInputEvents}
                />
                {renderCapsLockWarning('Tresor-Passwort bestätigen')}
                <div className="flex gap-2">
                  <button type="submit" className="vault-btn-primary text-sm" disabled={passkeyBusy || !passkeyPw}>
                    {passkeyBusy ? 'Wird eingerichtet…' : 'Passkey einrichten'}
                  </button>
                  <button type="button" className="vault-btn-ghost text-sm mr-test-btn" onClick={() => { setPasskeyAddOpen(false); setPasskeyPw(''); }}>Abbrechen</button>
                </div>
              </form>
            )}
          </section>

          <section className="mb-6">
            <h4 className="text-sm font-semibold vault-text mb-1">Lokaler Zwischenspeicher</h4>
            <p className="text-xs vault-text-subtle mb-3">
              Dieser Browser merkt sich Ihre Dateiliste und bereits heruntergeladene Dateien, damit sie
              beim nächsten Mal sofort da sind. Beides liegt hier nur verschlüsselt. Beim Leeren verschwindet
              es aus diesem Browser und wird danach frisch aus Ihrem Tresor geladen. In der Cloud wird nichts gelöscht.
            </p>
            <button
              type="button"
              className="vault-btn-ghost w-full"
              onClick={handleClearLocalCache}
              disabled={clearingCache}
            >
              <RotateCcw className="w-4 h-4" />
              {clearingCache ? 'Wird geleert und neu geladen' : 'Lokalen Zwischenspeicher leeren'}
            </button>
          </section>

          <section>
            <h4 className="text-sm font-semibold vault-text mb-2">Konto</h4>
            {quotaInfo.email && (
              <div className="text-sm vault-text mb-1 break-all">{quotaInfo.email}</div>
            )}
            <div className="text-xs vault-text-subtle mb-3">
              Tarif {quotaInfo.planLabel} · {formatBytes(quotaInfo.used)} von {formatBytes(quotaInfo.limit)} belegt
            </div>
            {quotaInfo.prepaidUntil && !quotaInfo.cancelPending && (
              <div className="text-xs vault-text-muted mb-3 p-3 rounded-lg vault-glass border border-amber-500/30">
                <strong className="vault-text block mb-1">⚡ Per Lightning bezahlt bis {formatGermanDate(quotaInfo.prepaidUntil)}</strong>
                Ohne Verlängerung endet der Zugang an diesem Datum.
                {quotaInfo.prepaidDeleteBy && (
                  <> Ihre Dateien werden danach noch 30 Tage aufbewahrt und spätestens am <strong>{formatGermanDate(quotaInfo.prepaidDeleteBy)}</strong> endgültig gelöscht.</>
                )}
                <button
                  type="button"
                  className="vault-btn-ghost w-full mt-3"
                  onClick={handleLightningRenew}
                  disabled={lightningRenewInProgress}
                >
                  <Zap className="w-4 h-4" />
                  {lightningRenewInProgress ? 'Wird geöffnet…' : 'Zahlungspflichtig 1 Jahr verlängern'}
                </button>
              </div>
            )}
            {quotaInfo.cancelPending && (
              <div className="text-xs vault-text-muted mb-3 p-3 rounded-lg vault-glass border border-amber-500/30">
                <strong className="vault-text block mb-1">Kündigung vorgemerkt</strong>
                Zugang endet am {formatGermanDate(quotaInfo.cancelAt) || 'Laufzeitende'}.
                {quotaInfo.dataDeleteBy && (
                  <> Datenlöschung spätestens am {formatGermanDate(quotaInfo.dataDeleteBy)}.</>
                )}
                <button
                  type="button"
                  className="vault-btn-ghost w-full mt-3"
                  onClick={handleTresorReactivate}
                  disabled={reactivateInProgress}
                >
                  <RotateCcw className="w-4 h-4" />
                  {reactivateInProgress ? 'Wird zurückgenommen…' : 'Kündigung zurücknehmen'}
                </button>
              </div>
            )}
            {quotaInfo.isTrial ? (
              // In der Testphase ist nichts zu "erweitern": es gibt noch keinen Tarif und
              // keine Zahlungsmethode. Der Weg hier ist die Erstbuchung.
              <button type="button" className="vault-btn-ghost w-full mb-3" onClick={() => { setShowSettingsPanel(false); openTrialModal(); }}>
                <HardDrive className="w-4 h-4" />
                Tresor eröffnen (ab 30 € im Jahr)
              </button>
            ) : TRESOR_BUCHBAR && quotaInfo.canUpgrade && (
              <button type="button" className="vault-btn-ghost w-full mb-3" onClick={() => { setShowSettingsPanel(false); setStorageAgbAccepted(false); setShowUpgradeModal(true); }}>
                <HardDrive className="w-4 h-4" />
                Speicher erweitern (max. 1 TB)
              </button>
            )}
            {quotaInfo.tresorActive && !quotaInfo.isTrial && !quotaInfo.cancelPending && !quotaInfo.prepaidUntil && (
              <button
                type="button"
                className="vault-btn-ghost w-full mb-3 vault-text-danger"
                onClick={() => { setShowSettingsPanel(false); setShowCancelModal(true); }}
              >
                <Trash2 className="w-4 h-4" />
                Tresor-Tarif kündigen
              </button>
            )}
            <button type="button" className="vault-btn-ghost w-full vault-text-danger vault-embed-hide" onClick={() => { setShowSettingsPanel(false); doLogout(); }}>
              <LogOut className="w-4 h-4" />
              Abmelden
            </button>
          </section>

          <section className="mt-8 pt-6 border-t" style={{ borderColor: 'var(--vault-border-subtle)' }}>
            <h4 className="text-sm font-semibold vault-text mb-2">Rechtliches</h4>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <a href="/agb" className="text-xs vault-text-muted hover:opacity-80 transition-opacity">AGB</a>
              <a href="https://datenputzer.de/datenschutz#tresor" className="text-xs vault-text-muted hover:opacity-80 transition-opacity">Datenschutz</a>
              <a href="https://datenputzer.de/widerrufsbelehrung" className="text-xs vault-text-muted hover:opacity-80 transition-opacity">Widerrufsbelehrung</a>
              <a href="https://datenputzer.de/kuendigen" className="text-xs vault-text-muted hover:opacity-80 transition-opacity">Verträge hier kündigen</a>
              <a href="https://datenputzer.de/widerruf" className="text-xs ed-widerruf">Vertrag widerrufen</a>
              <a href="https://datenputzer.de/impressum.html" className="text-xs vault-text-muted hover:opacity-80 transition-opacity">Impressum</a>
            </div>
            <p className="text-[10px] vault-text-dim mt-4">
              © 2026 Datenputzer Tresor · Zero-Knowledge Encryption
            </p>
          </section>
        </div>
      </div>
    );
  };

  const renderCancelModal = () => {
    if (!showCancelModal) return null;
    const deleteDate = formatGermanDate(quotaInfo.dataDeleteBy);
    return (
      <div
        className="fixed inset-0 z-[97] flex items-center justify-center p-4"
        style={{ background: 'var(--vault-overlay)' }}
        onClick={() => !cancelInProgress && setShowCancelModal(false)}
      >
        <div className="vault-settings-panel max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold vault-text-danger">Tresor-Tarif kündigen</h3>
            <button type="button" disabled={!!cancelInProgress} onClick={() => setShowCancelModal(false)} className="vault-btn-ghost px-2 py-1">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="rounded-xl p-4 mb-4 vault-glass border border-red-500/30">
            <div className="flex gap-3 mb-2">
              <AlertTriangle className="w-5 h-5 vault-text-danger shrink-0" />
              <p className="text-sm vault-text font-semibold">Nur der Tresor-Speichertarif wird gekündigt</p>
            </div>
            <ul className="text-xs vault-text-muted space-y-2 list-disc pl-5">
              <li>Ihr Datenputzer-Schutz oder andere Leistungen bleiben unberührt.</li>
              <li>Der Tresor bleibt bis zum Ende der bezahlten Laufzeit nutzbar.</li>
              <li>Danach endet der Zugang. Ihre verschlüsselten Dateien werden <strong className="vault-text">spätestens 30 Tage später unwiderruflich gelöscht</strong>.</li>
              <li>Ohne aktiven Tarif und ohne Recovery-Phrase sind die Daten <strong className="vault-text">endgültig verloren</strong>.</li>
            </ul>
          </div>
          {deleteDate && (
            <p className="text-xs vault-text-subtle mb-4">
              Voraussichtliche endgültige Löschung aller Tresor-Daten: <strong className="vault-text">{deleteDate}</strong> (30 Tage nach Laufzeitende).
            </p>
          )}
          <div className="flex flex-col gap-2">
            <button
              type="button"
              disabled={!!cancelInProgress}
              onClick={handleTresorCancel}
              className="vault-btn-ghost w-full vault-text-danger border border-red-500/40"
            >
              {cancelInProgress ? 'Wird gekündigt…' : 'Tresor-Tarif zum Laufzeitende kündigen'}
            </button>
            <button type="button" disabled={!!cancelInProgress} onClick={() => setShowCancelModal(false)} className="vault-btn-ghost w-full">
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderBookingModal = () => {
    if (!showBookingModal || !bookingPlan) return null;
    const tier = TIER_BY_ID[bookingPlan];
    const isSubscribe = bookingMode === 'subscribe';
    const monthlyPrice = tierPriceMonthly(tier?.units || 1, bookingCycle);
    const annualTotal = (tier?.units || 1) * 30;
    // Auch ein Upgrade aendert den kostenpflichtigen Vertrag: Haekchen immer Pflicht
    const canConfirm = !upgradeInProgress && bookingAgbAccepted && bookingPrivacyAccepted;
    const isAnnual = bookingCycle === 'annual';
    const fmt = (value) => value.toFixed(2).replace('.', ',');
    // Bei jährlicher Laufzeit wird einmal im Jahr der volle Betrag abgebucht. Nur den
    // rechnerischen Monatspreis zu zeigen, ließ Kunden mit der falschen Erwartung buchen.
    const headlinePrice = isAnnual ? annualTotal : monthlyPrice;
    const headlineUnit = isAnnual ? '/ Jahr' : '/ Monat';

    return (
      <div
        className="ed-booking-overlay"
        onClick={closeBookingModal}
        role="presentation"
      >
        <div
          className="ed-booking-panel"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="booking-modal-title"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 id="booking-modal-title" className="text-lg font-bold tracking-tight">
              {isSubscribe ? 'Tresor buchen' : 'Speicher erweitern'}
            </h3>
            <button type="button" disabled={!!upgradeInProgress} onClick={closeBookingModal} className="ed-booking-close" aria-label="Schließen">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="ed-booking-summary">
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <div className="font-bold text-xl">{tier?.size || bookingPlan}</div>
                <div className="text-sm ed-muted">{tier?.name}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-bold ed-accent text-lg">
                  {fmt(headlinePrice)} {currencyLabel}
                </div>
                <div className="text-xs ed-muted">{headlineUnit}</div>
                {isAnnual && (
                  <div className="text-[11px] ed-muted mt-0.5">
                    entspricht {fmt(monthlyPrice)} {currencyLabel} pro Monat
                  </div>
                )}
              </div>
            </div>
            {isSubscribe && (
              <p className="text-xs ed-muted mt-3 leading-relaxed">
                Der Tresor wird zu Ihrem bestehenden Datenputzer-Abo hinzugebucht. Ihr Schutz bleibt unverändert aktiv.
                Die Laufzeit richtet sich nach Ihrem Schutz-Abo und ist bei Ihnen <strong>{isAnnual ? 'jährlich' : 'monatlich'}</strong>.
              </p>
            )}
            <p className="text-sm ed-muted mt-3 leading-relaxed">
              {isSubscribe
                ? `Die Abbuchung läuft automatisch über die Zahlungsmethode, die in Ihrem Datenputzer-Konto bereits hinterlegt ist. Sie müssen nichts erneut eingeben. Heute wird nur der anteilige Betrag für die restliche Abrechnungsperiode fällig, danach ${fmt(headlinePrice)} ${currencyLabel} ${isAnnual ? 'pro Jahr' : 'pro Monat'}. Die Rechnung erhalten Sie per E-Mail.`
                : 'Die Differenz wird anteilig über die Zahlungsmethode abgerechnet, die in Ihrem Datenputzer-Konto bereits hinterlegt ist. Sie müssen nichts erneut eingeben. Die Rechnung erhalten Sie per E-Mail.'}
            </p>
          </div>

          {(
            <div className="space-y-3 mb-4">
              <label className="ed-booking-check">
                <input
                  type="checkbox"
                  checked={bookingAgbAccepted}
                  onChange={(e) => setBookingAgbAccepted(e.target.checked)}
                  disabled={!!upgradeInProgress}
                />
                <span>
                  Ich akzeptiere die{' '}
                  <a href="/agb" target="_blank" rel="noopener noreferrer" className="ed-link">AGB</a>{' '}
                  für den Cloud-Tresor.
                </span>
              </label>
              <label className="ed-booking-check">
                <input
                  type="checkbox"
                  checked={bookingPrivacyAccepted}
                  onChange={(e) => setBookingPrivacyAccepted(e.target.checked)}
                  disabled={!!upgradeInProgress}
                />
                <span>
                  Ich habe die{' '}
                  <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="ed-link">Datenschutzerklärung</a>{' '}
                  gelesen.
                </span>
              </label>
              <p className="text-[11px] ed-muted pl-7">
                14 Tage Widerrufsrecht · kostenpflichtiger Vertrag
              </p>
            </div>
          )}

          {isSubscribe && (
            <div className="mb-4">
              <button
                type="button"
                className="text-xs ed-muted hover:text-[var(--ed-ink)] flex items-center gap-1"
                onClick={() => setBookingCouponOpen((v) => !v)}
              >
                <ChevronDown className={`w-3 h-3 transition-transform ${bookingCouponOpen ? 'rotate-180' : ''}`} />
                Gutscheincode vorhanden?
              </button>
              {bookingCouponOpen && (
                <input
                  type="text"
                  value={bookingCouponCode}
                  onChange={(e) => setBookingCouponCode(e.target.value)}
                  disabled={!!upgradeInProgress}
                  placeholder="Code eingeben"
                  className="ed-booking-input"
                  autoComplete="off"
                />
              )}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <button
              type="button"
              disabled={!canConfirm}
              onClick={handleBookingConfirm}
              className="ed-cta w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {upgradeInProgress === bookingPlan
                ? 'Wird gebucht…'
                : isSubscribe
                  ? 'Zahlungspflichtig buchen'
                  : 'Speicher zahlungspflichtig erweitern'}
            </button>
            <button type="button" disabled={!!upgradeInProgress} onClick={closeBookingModal} className="ed-booking-ghost">
              Abbrechen
            </button>
            {isSubscribe && lightningNeukauf && (
              <button
                type="button"
                disabled={!canConfirm || lightningRenewInProgress}
                onClick={handleLightningBooking}
                className="text-xs ed-muted hover:text-[var(--ed-ink)] inline-flex items-center justify-center gap-1.5 mt-1 underline underline-offset-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {lightningRenewInProgress ? 'Wird geöffnet…' : (
                  <>
                    Alternativ zahlungspflichtig mit Lightning bestellen · {fmt(annualTotal)} € für 1 Jahr
                    <Zap className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  /** Schmales Band ueber dem Inhalt: sagt in der Testphase, wie lange sie noch laeuft,
   *  und nach dem Ablauf, bis wann die Dokumente noch da sind. */
  // Tiefenreinigung beendet: Nur-Lesen-Fenster mit Datum und Download-Kontingent. Kein Kaufdialog
  // fuer einen eigenstaendigen Tresor, sondern der Weg zurueck in die Tiefenreinigung.
  const renderBundleEndedBanner = () => {
    if (!quotaInfo.bundleEnded) return null;
    // Kein Datum und keine Tagezahl: die Frist steht in den AGB, hier nur der Hinweis.
    const left = quotaInfo.windowDownloadLeft;
    return (
      <div className="vault-trial-banner is-expired">
        <span className="vault-trial-banner-text">
          <strong>Ihre Tiefenreinigung ist beendet.</strong> Ihre Dateien lassen sich nur noch kurze Zeit wiederherstellen.
          Ansehen und Herunterladen geht weiter, Hochladen nicht mehr.
          {typeof left === 'number' && <> Noch {formatBytes(left)} Download möglich.</>}
        </span>
        <button type="button" className="vault-trial-banner-cta" onClick={() => navigateTop('https://datenputzer.de/direkt-starten?plan=premium_annual', { replace: false })}>
          Tiefenreinigung abschließen
        </button>
      </div>
    );
  };

  const renderTrialBanner = () => {
    if (!quotaInfo.isTrial) return null;
    const expired = !!quotaInfo.readonly;
    const endsAt = formatGermanDate(quotaInfo.trialEndsAt);
    const deleteAt = formatGermanDate(quotaInfo.trialDeleteBy);
    return (
      <div className={`vault-trial-banner ${expired ? 'is-expired' : ''}`}>
        <span className="vault-trial-banner-text">
          {expired ? (
            <>
              <strong>Testphase beendet.</strong> Ansehen und Herunterladen geht weiter, Hochladen nicht mehr.
              {deleteAt && <> Löschung am {deleteAt}.</>}
            </>
          ) : (
            <>
              <strong>Kostenlose Testphase.</strong>
              {endsAt ? <> Läuft bis {endsAt}, danach ist kein Upload mehr möglich.</> : <> 30 Tage, danach ist kein Upload mehr möglich.</>}
            </>
          )}
        </span>
        <button type="button" className="vault-trial-banner-cta" onClick={openTrialModal}>
          Tresor eröffnen
        </button>
      </div>
    );
  };

  /** Buchungsdialog der Testphase.
   *  Eine Vorauswahl (200 GB jaehrlich), ein Knopf. Alles andere liegt hinter
   *  "Mehr Optionen", damit die Entscheidung nicht am Tarifvergleich haengen bleibt. */
  const renderTrialModal = () => {
    if (!showTrialModal) return null;
    const tier = TIER_BY_ID[trialPlan] || TIER_BY_ID.starter;
    const amount = tierChargeAmount(tier.units, trialCycle);
    const fmt = (v) => v.toFixed(2).replace('.', ',');
    const busy = !!upgradeInProgress;
    const expired = !!quotaInfo.readonly;
    const endsAt = formatGermanDate(quotaInfo.trialEndsAt);
    const deleteAt = formatGermanDate(quotaInfo.trialDeleteBy);
    const canConfirm = !busy && trialAgbAccepted && trialPrivacyAccepted;
    const cycleFixed = hasSubscription && !!accountCycle;

    return (
      <div
        className="fixed inset-0 z-[97] flex items-center justify-center p-4"
        style={{ background: 'var(--vault-overlay)' }}
        onClick={() => !busy && setShowTrialModal(false)}
        role="presentation"
      >
        <div
          className="vault-settings-panel max-w-lg w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="trial-modal-title"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 id="trial-modal-title" className="text-lg font-semibold vault-text">
              {expired ? 'Testphase beendet' : 'Tresor eröffnen'}
            </h3>
            <button type="button" disabled={busy} onClick={() => setShowTrialModal(false)} className="vault-btn-ghost px-2 py-1" aria-label="Schließen">
              <X className="w-5 h-5" />
            </button>
          </div>

          <p className="text-sm vault-text-muted mb-4 leading-relaxed">
            {expired ? (
              <>
                Ihre 30 Tage sind vorbei. Sie können Ihre Dokumente weiterhin ansehen und
                herunterladen, aber nichts Neues mehr hochladen.
                {deleteAt && <> Am <strong className="vault-text">{deleteAt}</strong> werden sie gelöscht.</>}
              </>
            ) : (
              <>
                Sie testen den Tresor {endsAt ? <>noch bis zum <strong className="vault-text">{endsAt}</strong></> : '30 Tage lang'} kostenlos,
                ohne Kreditkarte. Danach können Sie nichts mehr hochladen, Ihre Dokumente
                bleiben aber les- und herunterladbar
                {deleteAt ? <> und werden erst am <strong className="vault-text">{deleteAt}</strong> gelöscht.</> : <> und werden erst {quotaInfo.trialGraceDays} Tage später gelöscht.</>}
              </>
            )}
          </p>

          <div className="p-4 rounded-xl vault-glass mb-4">
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <div className="font-semibold vault-text text-lg">{tier.size}</div>
                <div className="text-xs vault-text-muted mt-0.5">{tier.desc}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-bold vault-text-accent text-xl">{fmt(amount)} {currencyLabel}</div>
                <div className="text-xs vault-text-muted">{chargeUnitLabel(trialCycle)}</div>
              </div>
            </div>
            <p className="text-xs vault-text-subtle mt-3">
              Abrechnung <strong className="vault-text">{cycleLabel(trialCycle)}</strong>:{' '}
              {trialCycle === 'annual'
                ? `einmal im Jahr ${fmt(amount)} ${currencyLabel}, das sind ${fmt(tierPriceMonthly(tier.units, 'annual'))} ${currencyLabel} im Monat.`
                : `jeden Monat ${fmt(amount)} ${currencyLabel}.`}
            </p>
          </div>

          <button
            type="button"
            className="text-xs vault-text-muted hover:opacity-80 flex items-center gap-1 mb-3"
            onClick={() => setTrialMoreOptions((v) => !v)}
          >
            <ChevronDown className={`w-3 h-3 transition-transform ${trialMoreOptions ? 'rotate-180' : ''}`} />
            {trialMoreOptions ? 'Weniger Optionen' : 'Mehr Optionen'}
          </button>

          {trialMoreOptions && (
            <div className="mb-4 space-y-3">
              <div>
                <p className="text-xs vault-text-subtle mb-2">Speicher</p>
                <div className="space-y-2">
                  {TRESOR_TIERS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      disabled={busy}
                      onClick={() => setTrialPlan(t.id)}
                      aria-pressed={trialPlan === t.id}
                      className={`w-full text-left px-4 py-3 rounded-xl vault-glass transition-opacity disabled:opacity-50 ${trialPlan === t.id ? '' : 'opacity-60 hover:opacity-90'}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-semibold vault-text">{t.size}</span>
                        <span className="text-sm vault-text-accent">
                          {fmt(tierChargeAmount(t.units, trialCycle))} {currencyLabel} {chargeUnitLabel(trialCycle)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs vault-text-subtle mb-2">Abrechnung</p>
                {cycleFixed ? (
                  <p className="text-xs vault-text-muted leading-relaxed">
                    Ihr Tresor wird zu Ihrem bestehenden Datenputzer-Abo gebucht. Stripe
                    lässt je Abo nur ein Intervall zu, deshalb rechnet der Tresor{' '}
                    <strong className="vault-text">{cycleLabel(accountCycle)}</strong> ab wie Ihr Abo.
                  </p>
                ) : (
                  <>
                    <div className="flex gap-2">
                      {['annual', 'monthly'].map((c) => (
                        <button
                          key={c}
                          type="button"
                          disabled={busy}
                          onClick={() => setTrialCycle(c)}
                          aria-pressed={trialCycle === c}
                          className={`flex-1 px-4 py-2 rounded-xl vault-glass text-sm transition-opacity disabled:opacity-50 ${trialCycle === c ? 'vault-text' : 'opacity-60 hover:opacity-90 vault-text-muted'}`}
                        >
                          {c === 'annual' ? 'Jährlich' : 'Monatlich'}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] vault-text-subtle mt-2">
                      Jährlich ist günstiger: {fmt(tierChargeAmount(tier.units, 'annual'))} {currencyLabel} statt{' '}
                      {fmt(tierChargeAmount(tier.units, 'monthly') * 12)} {currencyLabel} im Jahr.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="space-y-2 mb-4">
            <label className="ed-booking-check">
              <input type="checkbox" checked={trialAgbAccepted} disabled={busy}
                onChange={(e) => setTrialAgbAccepted(e.target.checked)} />
              <span className="text-sm vault-text-muted">
                Ich akzeptiere die <a href="/agb" target="_blank" rel="noopener noreferrer" className="ed-link">AGB</a> für den Cloud-Tresor.
              </span>
            </label>
            <label className="ed-booking-check">
              <input type="checkbox" checked={trialPrivacyAccepted} disabled={busy}
                onChange={(e) => setTrialPrivacyAccepted(e.target.checked)} />
              <span className="text-sm vault-text-muted">
                Ich habe die <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="ed-link">Datenschutzerklärung</a> gelesen.
              </span>
            </label>
            <p className="text-[11px] vault-text-subtle pl-7">14 Tage Widerrufsrecht · kostenpflichtiger Vertrag</p>
          </div>

          <div className="flex flex-col gap-2">
            <button type="button" disabled={!canConfirm} onClick={handleTrialBooking}
              className="ed-cta w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed">
              {busy ? 'Wird gebucht…' : 'Weiter'}
            </button>
            <button type="button" disabled={busy} onClick={() => setShowTrialModal(false)} className="vault-btn-ghost w-full justify-center">
              {expired ? 'Später, erst meine Dateien holen' : 'Erst weiter testen'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderUpgradeModal = () => {
    if (!showUpgradeModal) return null;
    const targets = getUpgradeTargets(quotaInfo.plan);
    // Frueher stand hier fest "/ Monat", auch bei Jahreskunden. Das Intervall kommt jetzt
    // aus dem laufenden Abo; ohne Auskunft bleibt der Jahrestarif der Normalfall.
    const upgradeCycle = accountCycle || 'annual';
    return (
      <div
        className="fixed inset-0 z-[96] flex items-center justify-center p-4"
        style={{ background: 'var(--vault-overlay)' }}
        onClick={() => !upgradeInProgress && setShowUpgradeModal(false)}
      >
        <div className="vault-settings-panel max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold vault-text">Speicher erweitern</h3>
            <button type="button" disabled={!!upgradeInProgress} onClick={() => setShowUpgradeModal(false)} className="vault-btn-ghost px-2 py-1">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm vault-text-muted mb-4">
            Aktuell: <strong className="vault-text">{quotaInfo.planLabel}</strong> ({formatBytes(quotaInfo.used)} belegt).
            Das Maximum beträgt <strong className="vault-text">1 TB</strong>, mehr auf Anfrage.
          </p>
          <p className="text-xs vault-text-subtle mb-4">
            Abgerechnet wird <strong className="vault-text">{cycleLabel(upgradeCycle)}</strong> über Ihre
            hinterlegte Zahlungsmethode, die Differenz anteilig für den Rest der Laufzeit.
          </p>
          <label className="ed-booking-check mb-4">
            <input
              type="checkbox"
              checked={storageAgbAccepted}
              onChange={(e) => setStorageAgbAccepted(e.target.checked)}
              disabled={!!upgradeInProgress}
            />
            <span className="text-xs vault-text-muted">
              Mit der Auswahl ändere ich meinen kostenpflichtigen Vertrag. Es gelten die{' '}
              <a href="/agb" target="_blank" rel="noopener noreferrer" className="ed-link">AGB</a>{' '}
              einschließlich des Widerrufsrechts und die{' '}
              <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="ed-link">Datenschutzerklärung</a>.
            </span>
          </label>
          <div className="space-y-3">
            {targets.map((tier) => (
              <button
                key={tier.id}
                type="button"
                disabled={!!upgradeInProgress || !storageAgbAccepted}
                onClick={() => handleStorageUpgrade(tier.id)}
                className="w-full text-left p-4 rounded-xl vault-glass hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold vault-text">{tier.size} · {tier.name}</div>
                    <div className="text-xs vault-text-muted mt-1">{tier.desc}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold vault-text-accent">
                      {tierChargeAmount(tier.units, upgradeCycle).toFixed(2).replace('.', ',')} {currencyLabel}
                    </div>
                    <div className="text-xs vault-text-subtle">{chargeUnitLabel(upgradeCycle)}</div>
                  </div>
                </div>
                {upgradeInProgress === tier.id && (
                  <p className="text-xs vault-text-accent mt-2">Wird gebucht…</p>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderPricingSection = () => {
    if (!TRESOR_BUCHBAR) return null;
    const isActiveCustomer = quotaInfo.tresorActive;
    return (
    <div id="tarife" className="w-full max-w-4xl mx-auto mt-12 mb-28 relative z-10 scroll-mt-8">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="ed-eyebrow mb-3">Tarife</div>
          <h2 className="ed-display text-3xl md:text-5xl">
            {isActiveCustomer ? 'Speicher erweitern.' : appState === 'landing' ? 'Ein Preis, keine Überraschungen.' : 'Wählen Sie Ihre Größe.'}
          </h2>
          {isActiveCustomer && (
            <p className="ed-muted text-sm mt-3">
              Aktueller Tarif: <strong>{quotaInfo.planLabel}</strong> · Erweiterungen werden anteilig abgerechnet.
            </p>
          )}
        </div>
        {!isActiveCustomer && (
          <div className="flex items-center gap-5">
            <button onClick={() => setBillingCycle('monthly')} className={`ed-toggle-btn ${billingCycle === 'monthly' ? 'is-active' : ''}`}>Monatlich</button>
            <button onClick={() => setBillingCycle('annual')} className={`ed-toggle-btn ${billingCycle === 'annual' ? 'is-active' : ''}`}>Jährlich <span className="ed-accent">−16 %</span></button>
          </div>
        )}
      </div>

      <div>
        {TRESOR_TIERS.map((tier, idx) => {
          const cmp = isActiveCustomer ? comparePlans(tier.id, quotaInfo.plan) : 'new';
          const isCurrent = cmp === 'same';
          const isDowngrade = cmp === 'downgrade';
          const isUpgrade = cmp === 'upgrade';
          const disabled = isCurrent || isDowngrade || !!upgradeInProgress;
          return (
          <button
            key={tier.name}
            type="button"
            disabled={disabled}
            onClick={() => handlePlanSelection(tier.id, billingCycle)}
            className={`ed-tier group text-left w-full ${isCurrent ? 'opacity-60 cursor-default' : ''} ${isDowngrade ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            <div className="flex items-baseline justify-between gap-4">
              <div className="flex items-baseline gap-4 flex-wrap">
                <span className="ed-index text-sm">{String(idx + 1).padStart(2, '0')}</span>
                <h3 className="ed-tier-name text-2xl md:text-3xl">{tier.size}</h3>
                <span className="ed-muted text-base md:text-lg">{tier.name}</span>
                {isCurrent && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded ed-accent" style={{ fontFamily: 'var(--ed-mono)' }}>Ihr Tarif</span>
                )}
                {isUpgrade && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded ed-accent" style={{ fontFamily: 'var(--ed-mono)' }}>Erweitern</span>
                )}
              </div>
              <div className="text-right shrink-0">
                {!isActiveCustomer && (
                  <>
                    <span className="ed-price text-2xl md:text-3xl">{(tier.units * (billingCycle === 'annual' ? 2.50 : 3.00)).toFixed(2).replace('.', ',')} {currencyLabel}</span>
                    <span className="ed-muted text-sm"> / Monat</span>
                  </>
                )}
                {isUpgrade && upgradeInProgress === tier.id && (
                  <span className="ed-muted text-sm">Wird gebucht…</span>
                )}
              </div>
            </div>
            <div className="flex items-baseline justify-between gap-4 mt-2">
              <p className="ed-muted text-sm">{tier.desc}</p>
              {!isActiveCustomer && (
                <span className="ed-muted text-xs whitespace-nowrap" style={{ fontFamily: 'var(--ed-mono)' }}>
                  {billingCycle === 'annual' ? `${(tier.units * 30).toFixed(2).replace('.', ',')} ${currencyLabel} / Jahr` : `${(tier.units * 3).toFixed(2).replace('.', ',')} ${currencyLabel} / Monat`}
                </span>
              )}
            </div>
          </button>
          );
        })}
      </div>

      <div className="mt-10">
        <UspBullets />
      </div>
    </div>
    );
  };

  // ==========================================
  // VIEW ROUTING
  // ==========================================

  if (appState === 'loading') {
    return (
      <div className="ed-page flex h-screen items-center justify-center">
        <div className="ed-accent flex flex-col items-center">
          <HardDrive className="w-12 h-12 animate-pulse mb-4" />
          <p className="font-medium tracking-widest uppercase text-sm ed-muted">Initialisiere Tresor...</p>
        </div>
      </div>
    );
  }

  if (appState === 'landing') {
    return (
      <div className="ed-page min-h-screen overflow-auto relative">
        {renderNotificationToast()}
        {renderBookingModal()}
        <header className="max-w-6xl mx-auto px-6 py-6 flex justify-between items-center gap-4">
          <a href="/" className="flex items-center gap-3 hover:opacity-70 transition-opacity">
            <HardDrive className="w-7 h-7" />
            <span className="leading-tight">
              <span className="block text-xl font-extrabold tracking-tight">Tresor</span>
              <span className="ed-eyebrow ed-eyebrow-brand">Cloudspeicher für Endkunden · Datenputzer</span>
            </span>
          </a>
          <div className="flex items-center gap-5">
            <a href="/docs" className="ed-link text-sm">Technische Doku</a>
            {isLoggedIn() ? (
              <button onClick={doLogout} className="ed-link text-sm">Abmelden</button>
            ) : (
              <a href={getTresorLoginUrl()} rel="nofollow" className="ed-link text-sm">Login</a>
            )}
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-6">
          {/* Asymmetric editorial hero */}
          <section className="pt-16 md:pt-24 pb-20">
            <div className="grid lg:grid-cols-[minmax(0,1fr)_280px] xl:grid-cols-[minmax(0,1fr)_300px] gap-12 lg:gap-12 xl:gap-16 items-start">
              <div className="min-w-0">
                <h1 className="ed-display text-[clamp(2rem,10.2vw,3.75rem)] md:text-[clamp(5rem,11.2vw,7.5rem)] lg:text-[4.6rem] xl:text-[5.6rem] max-w-5xl mb-12">
                  Cloudspeicher, den nicht mal wir öffnen können.
                </h1>
                <hr className="ed-rule-strong mb-12" />
                <div className="grid md:grid-cols-[1.3fr_1fr] lg:grid-cols-1 xl:grid-cols-[1.3fr_1fr] gap-10 md:gap-16 lg:gap-8 items-start">
                  <div className="space-y-6">
                    <p className="text-xl md:text-2xl leading-snug">
                      Die einfache Alternative zu klassischen Cloudspeichern. Ihre Dateien werden direkt
                      auf Ihrem Gerät verschlüsselt, bevor sie hochgeladen werden.
                      <strong> Den Schlüssel haben nur Sie.</strong>
                    </p>
                    <p className="text-lg md:text-xl leading-relaxed ed-muted">
                      Wie das technisch läuft, können Sie jederzeit unter <a href="/docs" className="ed-link">/docs</a> nachprüfen.
                    </p>
                  </div>
                  <div className="md:pt-1 lg:pt-0">
                    <p className="ed-muted text-sm" style={{ fontFamily: 'var(--ed-mono)' }}>
                      Der Tresor ist vorübergehend nicht buchbar.
                    </p>
                  </div>
                </div>
              </div>
              <div className="lg:pt-2 justify-self-center lg:justify-self-end w-full max-w-[300px]">
                <HeroVaultPreview />
              </div>
            </div>
          </section>

          <ComparisonTable />
          {renderPricingSection()}
          <FAQSection />
        </main>

        <SiteFooter />
      </div>
    );
  }

  if (appState === 'device-blocked') {
    return (
      <div className="ed-page min-h-screen flex flex-col items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-6">
          <AlertTriangle className="w-12 h-12 mx-auto vault-text-danger" />
          <h2 className="ed-display text-3xl">Gerätelimit erreicht</h2>
          <p className="ed-muted leading-relaxed">
            {errorMsg || 'Maximal 4 Geräte gleichzeitig. Melden Sie auf einem anderen Gerät ein altes Gerät in den Einstellungen ab, oder melden Sie sich hier ab und versuchen Sie es erneut.'}
          </p>
          <div className="flex flex-col gap-3">
            <button type="button" className="ed-cta" onClick={() => redirectToDatenputzerLogin()}>
              Erneut anmelden
            </button>
            <button type="button" className="vault-btn-ghost w-full" onClick={doLogout}>
              Abmelden
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (appState === 'upsell') {
    return (
      <div className="ed-page min-h-screen flex flex-col relative">
        {renderNotificationToast()}
        {renderBookingModal()}
        <header className="flex items-center justify-between px-6 py-4 gap-4" style={{ borderBottom: '1px solid var(--ed-line)' }}>
          <div className="flex items-center gap-3">
            <HardDrive className="w-6 h-6" />
            <span className="leading-tight">
              <span className="block text-lg font-extrabold tracking-tight">Tresor</span>
              <span className="ed-eyebrow">Zero-Knowledge Cloudspeicher</span>
            </span>
          </div>
          <div className="flex items-center gap-5">
            {!IS_EMBEDDED && <button onClick={() => window.location.href = 'https://datenputzer.de/dashboard'} className="ed-link text-sm">← Datenputzer</button>}
            <button onClick={doLogout} className="ed-link text-sm">Abmelden</button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-6 py-16">
            <div className="mb-12">
              <div className="ed-eyebrow mb-6">{TRESOR_BUCHBAR ? 'Eingeloggt · Tarif wählen' : 'Eingeloggt'}</div>
              <h1 className="ed-display text-5xl md:text-7xl mb-8 max-w-3xl">
                Ihr verschlüsselter Cloudspeicher.
              </h1>
              <hr className="ed-rule-strong mb-8" />
              <p className="text-xl md:text-2xl leading-snug max-w-2xl">
                {TRESOR_BUCHBAR
                  ? <>Wählen Sie unten Ihren Speicher-Tarif und aktivieren Sie Ihren persönlichen <strong>Zero-Knowledge-Datensafe</strong>. Ende-zu-Ende verschlüsselt, gehostet in Deutschland, DSGVO-konform.</>
                  : <>Der Tresor ist vorübergehend nicht buchbar. Ihr Datenputzer-Konto ist davon nicht betroffen.</>}
              </p>
            </div>

            {renderPricingSection()}

            {TRESOR_BUCHBAR && (
              <div className="ed-muted text-sm max-w-2xl mb-24" style={{ fontFamily: 'var(--ed-mono)' }}>
                Der Tresor wird zu Ihrem Datenputzer-Abo hinzugebucht. Gleiche Zahlungsmethode, gleiche Laufzeit. Speicherplatz jederzeit erweiterbar.
              </div>
            )}

            <ComparisonTable />
            <FAQSection />
          </div>
        </main>

        <SiteFooter />
      </div>
    );
  }

  // ==========================================
  // VIEW: LOCK / SETUP SCREEN
  // ==========================================
  if (appState === 'auth') {
    return (
      <div className="vault-app vault-auth-screen" style={{ background: 'var(--vault-bg)' }}>
        {renderNotificationToast()}
        {renderTrialModal()}
        {quotaInfo.isTrial && !IS_EMBEDDED && renderTrialBanner()}
        {renderBundleEndedBanner()}
        <div className="vault-auth-screen-inner">
        <div className="w-full max-w-lg vault-panel vault-auth-panel rounded-3xl p-8">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 border vault-glass" style={{ borderColor: 'var(--vault-accent)' }}>
              <ShieldAlert className="w-10 h-10 vault-text-accent" />
            </div>
            
            <h1 className="text-2xl font-bold vault-text mb-2">
              {isSetupMode ? "Tresor einrichten" : loginMode === 'reset-password' ? "Passwort zurücksetzen" : "Tresor entschlüsseln"}
            </h1>
            {quotaInfo.email && (
              <p className="text-xs vault-text-muted mb-4 truncate" title={quotaInfo.email}>
                Angemeldet als {quotaInfo.email}
              </p>
            )}

            {renderBackgroundUploads()}

            {loginMode === 'reset-password' && !isSetupMode ? (
              <>
                <p className="vault-text-muted text-sm mb-2 text-left">
                  {resetStep === 'mnemonic' && "Schritt 1 von 3: Recovery-Phrase zur Identitätsprüfung."}
                  {resetStep === 'password' && "Schritt 2 von 3: Neues Tresor-Passwort festlegen."}
                  {resetStep === 'password-recheck' && "Schritt 3 von 3: Neues Passwort bestätigen."}
                </p>
                <div className="flex gap-1 mb-6">
                  {['mnemonic', 'password', 'password-recheck'].map((step, i) => (
                    <div key={step} className={`h-1 flex-1 rounded-full ${['mnemonic', 'password', 'password-recheck'].indexOf(resetStep) >= i ? 'bg-dp-green' : 'bg-white/10'}`} />
                  ))}
                </div>
                <form onSubmit={resetStep === 'password-recheck' ? handleResetComplete : handleResetNext} className="space-y-4 text-left">
                  {resetStep === 'mnemonic' && (
                    <div className="relative">
                      <ShieldAlert className="absolute left-3 top-4 w-5 h-5 vault-text-subtle" />
                      <textarea
                        value={recoveryInput}
                        onChange={(e) => setRecoveryInput(e.target.value)}
                        onPaste={blockMnemonicPaste}
                        onDrop={blockMnemonicPaste}
                        onDragOver={(e) => e.preventDefault()}
                        placeholder="12-Wörter Recovery-Phrase (ohne Einfügen)"
                        rows="4"
                        className="vault-input resize-none"
                        autoFocus
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </div>
                  )}
                  {resetStep === 'password' && (
                    <>
                      <div className="relative">
                        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 vault-text-subtle" />
                        <input
                          type="password"
                          value={passwordInput}
                          onChange={(e) => setPasswordInput(e.target.value)}
                          placeholder="Neues Passwort (mind. 12 Zeichen, stark)"
                          className="vault-input"
                          autoFocus
                          autoComplete="new-password"
                          {...passwordInputEvents}
                        />
                      </div>
                      {renderCapsLockWarning('Neues Passwort (mind. 12 Zeichen, stark)')}
                      <div className="relative">
                        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 vault-text-subtle" />
                        <input
                          type="password"
                          value={passwordConfirmInput}
                          onChange={(e) => setPasswordConfirmInput(e.target.value)}
                          placeholder="Neues Passwort wiederholen"
                          className="vault-input"
                          autoComplete="new-password"
                          {...passwordInputEvents}
                        />
                      </div>
                      {renderCapsLockWarning('Neues Passwort wiederholen')}
                      <PasswordStrengthMeter password={passwordInput} />
                    </>
                  )}
                  {resetStep === 'password-recheck' && (
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 vault-text-subtle" />
                      <input
                        type="password"
                        value={passwordRecheckInput}
                        onChange={(e) => setPasswordRecheckInput(e.target.value)}
                        placeholder="Neues Passwort zur Bestätigung"
                        className="vault-input"
                        autoFocus
                        autoComplete="new-password"
                        {...passwordInputEvents}
                      />
                      {renderCapsLockWarning()}
                    </div>
                  )}
                  {errorMsg && <div className="vault-text-danger text-sm">{errorMsg}</div>}
                  <div className="flex gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setErrorMsg('');
                        if (resetStep === 'password') setResetStep('mnemonic');
                        else if (resetStep === 'password-recheck') setResetStep('password');
                        else {
                          setLoginMode('password');
                          setResetStep('mnemonic');
                        }
                      }}
                      className="vault-btn-ghost flex-1 py-3"
                    >
                      Zurück
                    </button>
                    <button type="submit" className="vault-btn-primary flex-1">
                      {resetStep === 'password-recheck' ? 'Passwort speichern' : 'Weiter'}
                    </button>
                  </div>
                </form>
              </>
            ) : isSetupMode ? (
              <>
                <p className="vault-text-muted text-sm mb-2">
                  {setupStep === 'password' && "Schritt 1 von 4: Legen Sie Ihr Tresor-Passwort fest."}
                  {setupStep === 'password-recheck' && "Schritt 2 von 4: Bestätigen Sie Ihr Passwort erneut."}
                  {setupStep === 'mnemonic-display' && "Schritt 3 von 4: Schreiben Sie Ihren Notfall-Schlüssel auf."}
                  {setupStep === 'mnemonic-verify' && "Schritt 4 von 4: Geben Sie Ihre aufgeschriebenen 12 Wörter ein."}
                </p>
                <div className="flex gap-1 mb-6">
                  {['password', 'password-recheck', 'mnemonic-display', 'mnemonic-verify'].map((step, i) => (
                    <div key={step} className={`h-1 flex-1 rounded-full ${['password', 'password-recheck', 'mnemonic-display', 'mnemonic-verify'].indexOf(setupStep) >= i ? 'bg-dp-green' : 'bg-white/10'}`} />
                  ))}
                </div>

                <form onSubmit={setupStep === 'mnemonic-verify' ? handleSetupComplete : handleSetupNext} className="space-y-4 text-left">
                  {setupStep === 'password' && (
                    <>
                      <div className="relative">
                        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 vault-text-subtle" />
                        <input
                          type="password"
                          value={passwordInput}
                          onChange={(e) => setPasswordInput(e.target.value)}
                          placeholder="Tresor-Passwort (mind. 12 Zeichen, stark)"
                          className="vault-input"
                          autoFocus
                          autoComplete="new-password"
                          {...passwordInputEvents}
                        />
                      </div>
                      {renderCapsLockWarning('Tresor-Passwort (mind. 12 Zeichen, stark)')}
                      <div className="relative">
                        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 vault-text-subtle" />
                        <input
                          type="password"
                          value={passwordConfirmInput}
                          onChange={(e) => setPasswordConfirmInput(e.target.value)}
                          placeholder="Passwort wiederholen"
                          className="vault-input"
                          autoComplete="new-password"
                          {...passwordInputEvents}
                        />
                      </div>
                      {renderCapsLockWarning('Passwort wiederholen')}
                      <PasswordStrengthMeter password={passwordInput} />
                    </>
                  )}

                  {setupStep === 'password-recheck' && (
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 vault-text-subtle" />
                      <input
                        type="password"
                        value={passwordRecheckInput}
                        onChange={(e) => setPasswordRecheckInput(e.target.value)}
                        placeholder="Passwort zur Bestätigung eingeben"
                        className="vault-input"
                        autoFocus
                        autoComplete="new-password"
                        {...passwordInputEvents}
                      />
                      {renderCapsLockWarning()}
                    </div>
                  )}

                  {setupStep === 'mnemonic-display' && generatedMnemonic && (
                    <div className="vault-recovery-callout rounded-xl p-4">
                      <div className="flex gap-3 mb-2">
                        <ShieldAlert className="w-5 h-5 vault-text-danger shrink-0" />
                        <span className="font-semibold vault-text-danger">Ihr Notfall-Schlüssel, einmalig sichtbar</span>
                      </div>
                      <p className="text-xs vault-text mb-3 leading-relaxed">
                        <strong>Schreiben Sie diese 12 Wörter jetzt handschriftlich auf Papier</strong>, in genau dieser Reihenfolge.
                        Bewahren Sie den Zettel an einem sicheren, offline Ort auf (z. B. Safe, Bankschließfach).
                        Datenputzer hat <strong>keine Kopie</strong>; ohne diese Wörter ist bei Passwortverlust kein Zugriff mehr möglich.
                      </p>
                      <ul className="text-xs vault-text-muted mb-3 space-y-1.5 list-disc list-inside leading-relaxed">
                        <li><strong>Nicht</strong> per Screenshot, Foto, E-Mail oder Cloud-Notiz speichern</li>
                        <li><strong>Nicht</strong> am gleichen Ort wie Ihr Tresor-Passwort aufbewahren</li>
                        <li><strong>Nicht</strong> auf dem Computer oder Smartphone ablegen</li>
                      </ul>
                      <p className="text-[11px] vault-text-subtle mb-2 text-center">
                        Ihre persönliche Recovery-Phrase (einmalig sichtbar, jetzt auf Papier notieren):
                      </p>
                      <ol
                        className="vault-recovery-words select-none"
                        onCopy={blockMnemonicCopy}
                        onCut={blockMnemonicCopy}
                        onContextMenu={blockMnemonicCopy}
                      >
                        {generatedMnemonic.split(' ').map((word, i) => (
                          <li key={i}><span className="vault-recovery-num">{i + 1}</span>{word}</li>
                        ))}
                      </ol>
                      <p className="mt-4 text-xs vault-text leading-relaxed">
                        <strong>Wofür der Zettel da ist:</strong> Er ist Ihre Sicherung, wenn Sie Ihr
                        Passwort vergessen. Und er ist der einzige Weg, über den Angehörige an Ihre
                        Dokumente kommen, wenn Sie es selbst nicht mehr können. Sagen Sie deshalb jemandem,
                        dass es ihn gibt und wo er liegt.
                      </p>
                      <label className="mt-3 flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={mnemonicAcknowledged}
                          onChange={(e) => setMnemonicAcknowledged(e.target.checked)}
                          className="mt-1"
                        />
                        <span className="text-sm vault-text-muted">
                          Ich habe die 12 Wörter <strong>handschriftlich auf Papier</strong> notiert, sie nicht digital gespeichert
                          und verstanden, dass sie nicht wiederhergestellt werden können.
                        </span>
                      </label>
                    </div>
                  )}

                  {setupStep === 'mnemonic-verify' && (
                    <div className="space-y-4">
                      <p className="text-xs vault-text-muted leading-relaxed">
                        Geben Sie die 12 Wörter ein, die Sie im vorherigen Schritt aufgeschrieben haben.
                        Tippen Sie sie in der gleichen Reihenfolge wie auf Ihrem Zettel. Einfügen ist deaktiviert.
                      </p>
                      <div className="relative">
                        <ShieldAlert className="absolute left-3 top-4 w-5 h-5 vault-text-subtle" />
                        <textarea
                          value={mnemonicVerifyInput}
                          onChange={(e) => setMnemonicVerifyInput(e.target.value)}
                          onPaste={blockMnemonicPaste}
                        onDrop={blockMnemonicPaste}
                          onDragOver={(e) => e.preventDefault()}
                          placeholder="Ihre aufgeschriebenen 12 Wörter in der richtigen Reihenfolge"
                          rows="4"
                          className="vault-input resize-none"
                          autoFocus
                          autoComplete="off"
                          spellCheck={false}
                        />
                      </div>
                    </div>
                  )}

                  {errorMsg && <div className="vault-text-danger text-sm">{errorMsg}</div>}

                  <div className="flex gap-3 mt-4">
                    {setupStep !== 'password' && (
                      <button
                        type="button"
                        onClick={() => {
                          setErrorMsg('');
                          if (setupStep === 'password-recheck') setSetupStep('password');
                          else if (setupStep === 'mnemonic-display') setSetupStep('password-recheck');
                          else if (setupStep === 'mnemonic-verify') setSetupStep('mnemonic-display');
                        }}
                        className="vault-btn-ghost flex-1 py-3"
                      >
                        Zurück
                      </button>
                    )}
                    <button type="submit" className="vault-btn-primary flex-1">
                      <Unlock className="w-5 h-5" />
                      {setupStep === 'mnemonic-verify' ? 'Tresor einrichten' : 'Weiter'}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <p className="vault-text-muted text-sm mb-6">
                  {loginMode === 'password' && "Bitte geben Sie Ihr Tresor-Passwort ein."}
                  {loginMode === 'recovery' && "Bitte geben Sie Ihre 12-Wörter Recovery-Phrase ein."}
                </p>

                <form onSubmit={handleUnlock} className="space-y-4">
                  {loginMode === 'password' && (
                    <>
                      <div className="relative">
                        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 vault-text-subtle" />
                        <input
                          type="password"
                          value={passwordInput}
                          onChange={(e) => setPasswordInput(e.target.value)}
                          placeholder="Tresor-Passwort"
                          className="vault-input"
                          autoFocus
                          autoComplete="current-password"
                          {...passwordInputEvents}
                        />
                      </div>
                      {renderCapsLockWarning()}
                    </>
                  )}

                  {loginMode === 'recovery' && (
                    <div className="relative">
                      <ShieldAlert className="absolute left-3 top-4 w-5 h-5 vault-text-subtle" />
                      <textarea
                        value={recoveryInput}
                        onChange={(e) => setRecoveryInput(e.target.value)}
                        onPaste={blockMnemonicPaste}
                        onDrop={blockMnemonicPaste}
                        onDragOver={(e) => e.preventDefault()}
                        placeholder="Wort1 Wort2 Wort3 … (ohne Einfügen)"
                        rows="3"
                        className="vault-input resize-none"
                        autoFocus
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </div>
                  )}

                  {errorMsg && <div className="vault-text-danger text-sm">{errorMsg}</div>}

                  <button type="submit" className="vault-btn-primary mt-4">
                    <Unlock className="w-5 h-5" />
                    Entsperren
                  </button>
                  {loginMode === 'password' && passkeyOk && quotaInfo.passkeyCount > 0 && (
                    <button type="button" className="vault-btn-secondary mt-2" onClick={handlePasskeyUnlock} disabled={passkeyBusy}>
                      <KeyRound className="w-5 h-5" />
                      {passkeyBusy ? 'Warte auf Passkey…' : 'Mit Passkey entsperren'}
                    </button>
                  )}
                </form>
              </>
            )}

            {/* TOGGLE MODES */}
            {!isSetupMode && loginMode !== 'reset-password' && (
              <div className="mt-6 flex flex-col gap-2">
                {loginMode !== 'password' && (
                  <button type="button" onClick={() => { setLoginMode('password'); setErrorMsg(''); }} className="text-sm vault-text-muted hover:opacity-80 transition-colors">
                    Stattdessen Tresor-Passwort nutzen
                  </button>
                )}
                {loginMode !== 'recovery' && (
                  <button
                    type="button"
                    onClick={() => { setLoginMode('recovery'); setErrorMsg(''); }}
                    className="vault-auth-link-subtle"
                  >
                    Mit 12 Wörtern entsperren
                  </button>
                )}
                {loginMode !== 'reset-password' && (
                  <button
                    type="button"
                    onClick={() => { setLoginMode('reset-password'); setResetStep('mnemonic'); setErrorMsg(''); }}
                    className="vault-auth-link-subtle"
                  >
                    Passwort mit 12-Wörter-Recovery-Phrase zurücksetzen
                  </button>
                )}
              </div>
            )}
            {!isSetupMode && loginMode === 'reset-password' && (
              <div className="mt-6">
                <button type="button" onClick={() => { setLoginMode('password'); setResetStep('mnemonic'); setErrorMsg(''); }} className="text-sm vault-text-muted hover:opacity-80 transition-colors">
                  Zurück zum normalen Login
                </button>
              </div>
            )}

            <div className="vault-embed-hide mt-6 border-t pt-4 flex flex-col gap-3" style={{ borderColor: 'var(--vault-border-subtle)' }}>
              <button
                type="button"
                onClick={doLogout}
                className="vault-btn-ghost w-full text-sm vault-text-danger vault-embed-hide"
                title="Datenputzer-Konto abmelden und zur Tresor-Startseite"
              >
                <LogOut className="w-4 h-4" />
                Abmelden
              </button>
              <p className="text-xs vault-text-subtle text-center vault-embed-hide">
                Zero-Knowledge-Verschlüsselung
              </p>
            </div>
          </div>
        </div>
        <div className="vault-auth-footer-wrap">
          <SiteFooter />
        </div>
        </div>
      </div>
    );
  }

  // ==========================================
  // VIEW: UNLOCKED VAULT (MAIN APP)
  // ==========================================
  return (
    <div
      className="vault-app flex h-screen overflow-hidden relative"
      style={{ background: 'var(--vault-bg)' }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {renderNotificationToast()}
      {renderFolderModal()}
      {renderRenameFolderModal()}
      {renderRenameFileModal()}
      {renderMoveModal()}
      {renderContextMenu()}
      {renderDeleteConfirmModal()}
      {renderEncryptionGuideModal()}
      {renderPasskeyInfoModal()}
      {renderSettingsPanel()}
      <Maschinenraum
        open={showMaschinenraum}
        onClose={() => setShowMaschinenraum(false)}
        getEncryptionKey={getEncryptionKey}
      />
      {renderUpgradeModal()}
      {renderTrialModal()}
      {renderCancelModal()}

      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden transition-opacity duration-300"
          style={{ background: 'var(--vault-overlay)' }}
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside className={`
        vault-sidebar vault-sidebar-drawer fixed inset-y-0 left-0 z-50 flex flex-col border-r ${sidebarCollapsed ? 'is-collapsed' : ''}
        transform transition-transform duration-300 ease-in-out
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        md:relative md:translate-x-0
      `}>
        <div className="vault-sidebar-head h-16 flex items-center justify-between px-6 border-b" style={{ borderColor: 'var(--vault-border-subtle)' }}>
          <a href="/" className="vault-embed-hide flex items-center gap-2 vault-text-accent font-bold text-xl tracking-tight hover:opacity-80 transition-opacity">
            <HardDrive className="w-6 h-6" />
            Tresor
          </a>
          <button onClick={() => setIsSidebarOpen(false)} className="md:hidden p-1 vault-text-muted hover:opacity-80">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 space-y-2">
          <label className="vault-btn-primary cursor-pointer" title="Datei hochladen">
            <UploadCloud className="w-5 h-5" />
            Datei hochladen
            <input type="file" multiple className="hidden" onChange={handleFileInput} />
          </label>
          <label className="vault-btn-secondary cursor-pointer" title="Einen kompletten Ordner mit Unterordnern hochladen">
            <FolderUp className="w-5 h-5" />
            Ordner hochladen
            <input type="file" multiple webkitdirectory="" directory="" className="hidden" onChange={handleFolderInput} />
          </label>
        </div>

        <nav className="flex-1 px-3 py-2 space-y-1">
          <button
            type="button"
            onClick={() => { setShowTrash(false); navigateToFolder('root'); }}
            className={`vault-nav-item w-full text-left ${currentFolderId === 'root' && !showTrash ? 'vault-nav-active' : ''}`}
            title="Mein Tresor"
          >
            <Folder className="w-5 h-5 vault-text-accent" />
            Mein Tresor
          </button>
          <button
            type="button"
            onClick={openNewFolderModal}
            className={`vault-nav-item w-full text-left ${foldersAtLimit ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={foldersAtLimit ? `Maximal ${MAX_VAULT_FOLDERS} Ordner erlaubt` : 'Neuen Ordner anlegen'}
            disabled={foldersAtLimit}
          >
            <Plus className="w-5 h-5" />
            Neuer Ordner
          </button>
          <button
            type="button"
            onClick={() => { setEncryptionGuideStep(0); setShowEncryptionGuide(true); }}
            className="vault-nav-item w-full text-left"
            title="So funktioniert die Verschlüsselung. Schritt für Schritt: Passwort und Recovery-Phrase"
          >
            <ShieldCheck className="w-5 h-5 shrink-0 vault-text-accent" />
            Verschlüsselung erklärt
          </button>
          <button
            type="button"
            onClick={() => { setShowTrash(true); setIsSidebarOpen(false); }}
            className={`vault-nav-item w-full text-left ${showTrash ? 'vault-nav-active' : ''}`}
            title="Gelöschte Dateien und Ordner – 14 Tage wiederherstellbar"
          >
            <Trash2 className="w-5 h-5 shrink-0" />
            <span className="flex-1">Papierkorb</span>
            {trashRoots.length > 0 && (
              <span className="vault-nav-count text-xs font-mono px-1.5 py-0.5 rounded-full vault-upload-card shrink-0">{trashRoots.length}</span>
            )}
          </button>
        </nav>

        <div className="p-6 mt-auto">
          <button
            type="button"
            onClick={handleLock}
            className="vault-btn-ghost vault-btn-lock w-full mb-3"
            title="Versteckt Dateien und stoppt neue Uploads. Laufende Uploads können im Hintergrund weiterlaufen."
          >
            <Lock className="w-4 h-4" />
            Tresor sperren
          </button>
        <div className="vault-collapse-row hidden md:flex">
          <button
            type="button"
            onClick={toggleSidebarCollapsed}
            className="vault-collapse-btn"
            title={sidebarCollapsed ? 'Leiste ausklappen' : 'Leiste einklappen'}
            aria-label={sidebarCollapsed ? 'Leiste ausklappen' : 'Leiste einklappen'}
            aria-expanded={!sidebarCollapsed}
          >
            {sidebarCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        </div>

          <button
            type="button"
            onClick={doLogout}
            className="vault-btn-ghost w-full mb-4 vault-embed-hide"
            title="Beendet die Datenputzer-Sitzung und leitet zu datenputzer.de weiter."
          >
            <LogOut className="w-4 h-4" />
            Abmelden
          </button>

          <div className="vault-storage-block">
          <div className="text-sm vault-text mb-2 font-medium">Speicherplatz</div>
          <div className="h-2 w-full vault-progress-track">
            <div className="vault-progress-fill" style={{ width: `${quotaPercent || 0}%` }} />
          </div>
          <div className="text-xs vault-text-muted mt-2">
            Tarif {quotaInfo.planLabel} · {formatBytes(quotaInfo.used)} von {formatBytes(quotaInfo.limit)}
          </div>
          <div className="text-xs vault-text-subtle mt-1">
            {totalFileCount} Datei{totalFileCount === 1 ? '' : 'en'} · {totalFolderCount}/{MAX_VAULT_FOLDERS} Ordner
            {currentFolderId !== 'root' && files.length !== totalFileCount ? ` · ${files.length} hier` : ''}
          </div>
          {TRESOR_BUCHBAR && quotaInfo.canUpgrade && (
            <button
              type="button"
              onClick={() => { setStorageAgbAccepted(false); setShowUpgradeModal(true); }}
              className="vault-btn-ghost w-full mt-3 text-sm"
              title="Speicher bis maximal 1 TB erweitern"
            >
              <HardDrive className="w-4 h-4 vault-text-accent" />
              Speicher erweitern
            </button>
          )}
          {quotaInfo.plan === 'max' && (
            <p className="text-[11px] vault-text-subtle mt-3 leading-snug" title="Mehr Speicher auf Anfrage">
              1 TB erreicht, mehr auf Anfrage
            </p>
          )}
          </div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0 relative z-10">
        {quotaInfo.isTrial && !IS_EMBEDDED && renderTrialBanner()}
        {renderBundleEndedBanner()}
        {isDragging && (
          <div
            className="absolute inset-0 z-50 flex items-center justify-center border-4 border-dashed m-4 rounded-3xl vault-upload-card backdrop-blur-sm pointer-events-none"
            style={{ borderColor: 'var(--vault-accent)' }}
          >
            <div className="flex flex-col items-center vault-text">
              <UploadCloud className="w-24 h-24 vault-text-accent mb-4 animate-bounce" />
              <h2 className="text-3xl font-bold">Dateien hier ablegen</h2>
              <p className="text-lg vault-text-muted mt-2">Dateien werden sofort lokal verschlüsselt.</p>
            </div>
          </div>
        )}

        <header className="vault-header vault-header-bar flex items-center justify-between border-b z-10">
          <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
            <button
              type="button"
              onClick={() => setIsSidebarOpen(true)}
              className="md:hidden vault-icon-btn vault-text-muted"
              aria-label="Menü öffnen"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex-1 max-w-2xl hidden sm:block min-w-0">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 vault-text-subtle" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Im Tresor suchen…"
                  className="vault-input rounded-full py-2 text-sm pr-9"
                  aria-label="Im Tresor suchen"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 vault-text-subtle hover:opacity-80"
                    aria-label="Suche löschen"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            {isPreparingFolderUpload && (
              <span className="hidden sm:inline-flex text-xs font-medium vault-text-accent animate-pulse px-2.5 py-1 rounded-full border vault-upload-card">
                Ordner wird vorbereitet…
              </span>
            )}
            {isEncrypting && (
              <span className="hidden sm:inline-flex text-xs font-medium vault-text-accent animate-pulse px-2.5 py-1 rounded-full border vault-upload-card">
                Verschlüsselt…
              </span>
            )}
            <label className="md:hidden vault-icon-btn vault-icon-btn-accent cursor-pointer" title="Datei hochladen">
              <UploadCloud className="w-5 h-5" />
              <input type="file" multiple className="hidden" onChange={handleFileInput} />
            </label>
            <label className="md:hidden vault-icon-btn cursor-pointer" title="Ordner hochladen">
              <FolderUp className="w-5 h-5" />
              <input type="file" multiple webkitdirectory="" directory="" className="hidden" onChange={handleFolderInput} />
            </label>
            <div className="relative" ref={profileMenuRef}>
              <button
                type="button"
                onClick={() => setShowProfileMenu((v) => !v)}
                className="flex items-center gap-1 sm:gap-2 rounded-full pl-0.5 pr-1 sm:pr-2 py-0.5 hover:opacity-90 transition-opacity"
                aria-expanded={showProfileMenu}
                aria-haspopup="menu"
                aria-label="Profilmenü"
              >
                <div className="w-9 h-9 rounded-full flex items-center justify-center shadow-lg" style={{ background: 'var(--vault-accent)' }}>
                  <User className="w-5 h-5 text-white" />
                </div>
                <ChevronDown className={`hidden sm:block w-4 h-4 vault-text-muted transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} />
              </button>
              {renderProfileMenu()}
            </div>
          </div>
        </header>

        <div className="sm:hidden vault-mobile-search vault-header border-b">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 vault-text-subtle" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Im Tresor suchen…"
              className="vault-input rounded-full py-2 text-sm pr-9"
              aria-label="Im Tresor suchen"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 vault-text-subtle hover:opacity-80"
                aria-label="Suche löschen"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4 sm:p-6 z-0">
          {isSearchActive ? (
            <div className="mb-6 sm:mb-8">
              <h2 className="text-lg font-semibold vault-text">
                Suchergebnisse für „{searchQuery.trim()}"
              </h2>
              <p className="text-sm vault-text-muted mt-1">
                {visibleFolders.length} Ordner · {visibleFiles.length} Datei{visibleFiles.length === 1 ? '' : 'en'}. Suche läuft lokal in Ihrem Browser.
              </p>
            </div>
          ) : (
          <div className="vault-breadcrumb-bar mb-6 sm:mb-8">
            {breadcrumbScroll.canLeft && (
              <button
                type="button"
                className="vault-breadcrumb-scroll-btn"
                onClick={() => scrollBreadcrumbs(-1)}
                aria-label="Ordnerpfad nach links scrollen"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <nav
              ref={breadcrumbScrollRef}
              className="vault-breadcrumb-track text-base sm:text-lg font-medium"
              aria-label="Ordnerpfad"
            >
              {getBreadcrumbs().map((crumb, i, arr) => (
                <span key={crumb.id} className="vault-breadcrumb-segment">
                  {i > 0 && <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 vault-text-subtle shrink-0" />}
                  {i < arr.length - 1 ? (
                    <button
                      type="button"
                      onClick={() => navigateToFolder(crumb.id)}
                      onDragEnter={(e) => handleFolderDragEnter(e, crumb.id)}
                      onDragLeave={handleFolderDragLeave}
                      onDragOver={handleFolderDragOver}
                      onDrop={(e) => handleFolderDrop(e, crumb.id)}
                      className={`vault-breadcrumb-link transition-all whitespace-nowrap ${
                        isDropTargetActive(crumb.id) ? 'vault-breadcrumb-drop-target' : 'vault-text-muted hover:opacity-80'
                      }`}
                      title={draggingFileId || draggingFolderId ? `„${crumb.name}" als Ziel` : crumb.name}
                    >
                      {isDropTargetActive(crumb.id) ? 'Hier ablegen' : crumb.name}
                    </button>
                  ) : (
                    <span className="vault-text whitespace-nowrap" title={crumb.name}>{crumb.name}</span>
                  )}
                </span>
              ))}
            </nav>
            {breadcrumbScroll.canRight && (
              <button
                type="button"
                className="vault-breadcrumb-scroll-btn"
                onClick={() => scrollBreadcrumbs(1)}
                aria-label="Ordnerpfad nach rechts scrollen"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>
          )}

          {renderUploadProgressList()}

          {showTrash ? renderTrashView() : (
          <>
          {visibleFolders.length > 0 && (
            <>
              <h2 className="text-sm font-semibold vault-text-subtle uppercase tracking-wider mb-4">Ordner</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {displayedFolders.map(folder => (
                  <div
                    key={folder.id}
                    className={`group relative flex items-stretch rounded-xl vault-glass hover:opacity-90 transition-all ${
                      isDropTargetActive(folder.id) ? 'vault-folder-drop-target' : ''
                    } ${openFolderMenuId === folder.id ? 'vault-folder-card--menu-open' : ''}`}
                  >
                    <button
                      type="button"
                      onClick={() => { if (swallowLongPressClick()) return; openFolder(folder); }}
                      onContextMenu={(e) => onItemContextMenu(e, 'folder', folder)}
                      {...longPressHandlers('folder', folder)}
                      draggable={!isSearchActive && !isCoarsePointer()}
                      onDragStart={(e) => handleFolderDragStart(e, folder)}
                      onDragEnd={handleFileDragEnd}
                      onDragEnter={(e) => handleFolderDragEnter(e, folder.id)}
                      onDragLeave={handleFolderDragLeave}
                      onDragOver={handleFolderDragOver}
                      onDrop={(e) => handleFolderDrop(e, folder.id)}
                      className="flex flex-1 items-center gap-3 min-w-0 p-4 text-left cursor-pointer"
                      title={draggingFileId || draggingFolderId ? 'Hier ablegen zum Verschieben' : folder.name}
                    >
                      <Folder
                        className={`w-6 h-6 vault-text-accent shrink-0 ${isDropTargetActive(folder.id) ? 'vault-folder-drop-icon' : ''}`}
                        fill="currentColor"
                        fillOpacity={isDropTargetActive(folder.id) ? 0.35 : 0.2}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium vault-text truncate">{folder.name}</div>
                        <div className="text-xs vault-text-muted truncate">
                          {isDropTargetActive(folder.id)
                            ? 'Hier ablegen'
                            : isSearchActive
                              ? getFolderPathLabel(folder.parentId)
                              : formatDate(folder.createdAt)}
                        </div>
                      </div>
                    </button>
                    <div className="vault-folder-actions relative flex items-center pr-2">
                      <button
                        type="button"
                        onClick={(e) => openCtxFromButton(e, 'folder', folder)}
                        className="vault-touch-target p-2 rounded-lg vault-text-subtle hover:opacity-80 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                        aria-label={`Aktionen für Ordner ${folder.name}`}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {canExpandFolders && (
                <button
                  type="button"
                  onClick={() => setFoldersExpanded((v) => !v)}
                  className="vault-folder-expand-btn mt-3 mb-10"
                  aria-expanded={foldersExpanded}
                >
                  {foldersExpanded ? (
                    <>
                      Weniger anzeigen
                      <ChevronUp className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      {hiddenFolderCount} weitere Ordner anzeigen
                      <ChevronDown className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}
              {!canExpandFolders && <div className="mb-10" />}
            </>
          )}

          <h2 className="text-sm font-semibold vault-text-subtle uppercase tracking-wider mb-4">
            Dateien ({visibleFiles.length}{isSearchActive ? '' : ` · ${files.length} in diesem Ordner`})
          </h2>

          {visibleFiles.length === 0 && visibleFolders.length === 0 ? (
            <div className="text-center py-20 vault-empty-state">
              <UploadCloud className="w-12 h-12 vault-text-subtle mx-auto mb-3" />
              <p className="vault-text font-medium">
                {isSearchActive
                  ? 'Keine Treffer für Ihre Suche.'
                  : currentFolderId === 'root' ? 'Noch keine Dateien im Tresor.' : 'Dieser Ordner ist leer.'}
              </p>
              <p className="vault-text-subtle text-sm mt-1">
                {isSearchActive ? 'Versuchen Sie einen anderen Suchbegriff.' : 'Ziehen Sie Dateien per Drag & Drop hierher.'}
              </p>
            </div>
          ) : (
            <div className="vault-file-list vault-glass rounded-xl overflow-hidden shadow-xl">
              <div className="vault-file-list-header grid grid-cols-12 gap-4 p-4 border-b text-sm font-medium" style={{ borderColor: 'var(--vault-border-subtle)' }}>
                <div className="col-span-6">{renderSortHeader('Name', 'name')}</div>
                <div className="col-span-3">{renderSortHeader('Zuletzt geändert', 'createdAt')}</div>
                <div className="col-span-2">{renderSortHeader('Dateigröße', 'size')}</div>
                <div className="col-span-1" />
              </div>

              <div>
                {visibleFiles.length === 0 ? (
                  <div className="p-8 text-center vault-text-muted text-sm">
                    {isSearchActive ? 'Keine passenden Dateien.' : 'Keine Dateien in diesem Ordner.'}
                  </div>
                ) : visibleFiles.map(file => (
                  <div
                    key={file.id}
                    draggable={!isSearchActive && !isCoarsePointer()}
                    onDragStart={(e) => !isSearchActive && handleFileDragStart(e, file)}
                    onDragEnd={handleFileDragEnd}
                    onClick={() => { if (swallowLongPressClick()) return; downloadFile(file); }}
                    onContextMenu={(e) => onItemContextMenu(e, 'file', file)}
                    {...longPressHandlers('file', file)}
                    className={`vault-file-row group cursor-pointer ${
                      draggingFileId === file.id ? 'is-dragging' : ''
                    }`}
                    title={isSearchActive ? file.name : 'Klicken zum Herunterladen · Rechtsklick für weitere Aktionen'}
                  >
                    <div className="vault-file-col-name flex items-center gap-2 min-w-0">
                      {!isSearchActive && (
                        <GripVertical className="vault-file-drag-handle w-4 h-4 vault-text-subtle opacity-0 group-hover:opacity-70 shrink-0 hidden sm:block" />
                      )}
                      {file.type === 'pdf' ? <FileText className="w-5 h-5 text-red-500 shrink-0" /> :
                       file.type === 'img' ? <FileImage className="w-5 h-5 text-blue-500 shrink-0" /> :
                       <File className="w-5 h-5 vault-text-subtle shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <span className="font-medium vault-text truncate block">{file.name}</span>
                        {isSearchActive ? (
                          <span className="text-xs vault-text-subtle truncate block">{getFolderPathLabel(file.folderId)}</span>
                        ) : (
                          <span className="vault-file-col-meta-mobile text-xs vault-text-muted tabular-nums">
                            {file.size} · {formatDate(file.createdAt)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="vault-file-col-date text-sm vault-text-muted">{formatDate(file.createdAt)}</div>
                    <div className="vault-file-col-size text-sm vault-text-muted tabular-nums">{file.size}</div>
                    <div className="vault-file-col-actions flex justify-end">
                      <button
                        type="button"
                        className="vault-touch-target p-2 -mr-1 rounded-lg vault-text-subtle hover:opacity-80 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                        aria-label={`Aktionen für ${file.name}`}
                        onClick={(e) => openCtxFromButton(e, 'file', file)}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </>
          )}
        </div>
      </main>
    </div>
  );
}

export default App;